
/**
 * Recursively merge `source` into `target` (both plain objects), returning a new
 * object. See en.js for rationale — keeps section keys defined in both the main
 * object and a later `STRINGS.x = {...}` assignment (e.g. salesAnalytics.filters).
 */
const deepMerge = (target, source) => {
  const out = { ...(target || {}) };
  for (const key of Object.keys(source || {})) {
    const s = source[key];
    const t = out[key];
    if (s && typeof s === "object" && !Array.isArray(s) && t && typeof t === "object" && !Array.isArray(t)) {
      out[key] = deepMerge(t, s);
    } else {
      out[key] = s;
    }
  }
  return out;
};

const STRINGS = {
  common: {
    appTitle: "ऑर्डर मैनेजमेंट सिस्टम",
    searchPlaceholder: "खोजें...",
    searchAriaLabel: "खोजें",
    symbols: {
      emDash: "—",
      required: "*",
      currency: "₹",
    },
    units: {
      kg: "kg",
      meters: "मीटर",
      pieces: "पीस",
      m: "m",
      mm: "mm",
      pcs: "Pcs",
      kgOrMtr: "Kg/Mtr",
      // pieces: 'pieces',
      // m2: 'm²',
      // g: 'g',
      // l: 'L',
      // units: 'units',
      // box: 'box',
    },
    alerts: {
      exportFailed: "एक्सपोर्ट विफल",
      successTitle: "सफल",
      errorTitle: "त्रुटि",
      fetchFailed: "डेटा लाने में विफल",
      errorMessage: "कुछ गलत हो गया। कृपया पुनः प्रयास करें।",
    },
    status: {
      success: "सफल",
      error: "त्रुटि",
      failed: "विफल",
      warning: "चेतावनी",
      info: "जानकारी",
    },
    notifications: {
      importCompleted: "इम्पोर्ट सफलतापूर्वक पूरा हुआ",
      exportCompleted: "एक्सपोर्ट सफलतापूर्वक पूरा हुआ",
    },
    buttons: {
      refresh: "रिफ्रेश",
      refreshing: "रिफ्रेश हो रहा है...",
      exportCsv: "CSV एक्सपोर्ट करें",
      bulkAdd: "बल्क जोड़ें",
      addUser: "यूज़र जोड़ें",
      addIpAddress: "IP एड्रेस जोड़ें",
      deleteSelected: "चयनित हटाएं",
      loading: "मार्क किया जा रहा है...",
      cancel: "रद्द करें",
      import: "इम्पोर्ट",
      importing: "इम्पोर्ट हो रहा है...",
      downloadTemplate: "टेम्पलेट डाउनलोड करें",
      close: "बंद करें",
      ok: "ठीक है",
      save: "सहेजें",
      saving: "सहेजा जा रहा है...",
      resetting: "रीसेट हो रहा है...",
      delete: "हटाएं",
      deleting: "हटाया जा रहा है...",
      view: "देखें",
      edit: "संपादित करें",
      done: "हो गया",
      gotIt: "समझ गया",
    },
    values: {
      notAvailable: "उपलब्ध नहीं",
      yesUpper: "हाँ",
      noUpper: "नहीं",
    },
    statusValues: {
      active: "सक्रिय",
      inactive: "निष्क्रिय",
      activeUpper: "सक्रिय",
      inactiveUpper: "निष्क्रिय",
    },
    importHelper: "स्वीकृत फॉर्मेट: .xls, .xlsx. अधिकतम साइज़ 5 MB.",
    importingText: "अपलोड हो रहा है...",
    importReady: "इम्पोर्ट के लिए तैयार",
    dialogs: {
      confirm: {
        title: "कार्रवाई की पुष्टि करें",
        confirm: "पुष्टि करें",
        cancel: "रद्द करें",
      },
      delete: {
        title: "हटाने की पुष्टि करें",
        confirm: "हटाएं",
        cancel: "रद्द करें",
      },
      import: {
        title: "रिकॉर्ड इम्पोर्ट करें",
        subtitle: "डेटा इम्पोर्ट करने के लिए फ़ाइल अपलोड करें",
        description: "बल्क में रिकॉर्ड इम्पोर्ट करने के लिए एक Excel फ़ाइल चुनें।",
        uploadLabel: "फ़ाइल अपलोड करें",
        helper: "स्वीकृत फॉर्मेट: .xls, .xlsx. अधिकतम साइज़ 5 MB.",
        status: {
          success: "इम्पोर्ट सफल",
          error: "इम्पोर्ट विफल",
          uploading: "अपलोड हो रहा है...",
          ready: "इम्पोर्ट के लिए तैयार",
        },
        errors: {
          invalidType: "अमान्य फ़ाइल प्रकार",
          noFile: "कृपया एक फ़ाइल चुनें",
          maxSize: "अधिकतम फ़ाइल साइज़ से अधिक (5 MB)",
          parseError: "Excel फ़ाइल पार्स करने में विफल",
        },
        messages: {
          columnsMismatch: "कॉलम नाम अपेक्षित टेम्पलेट से मेल नहीं खाते।",
          missingRequiredColumns:
            "आपकी फ़ाइल में ये आवश्यक कॉलम नहीं हैं: {missing}. उन्हें जोड़ें (या सैंपल दोबारा डाउनलोड करें) और फिर अपलोड करें।",
          columnsMismatchDetail:
            "गुम कॉलम: {missing}. अतिरिक्त कॉलम: {extra}.",
          errorsTruncated: "...और {count} त्रुटियाँ दिखाई नहीं गईं।",
          errorSummary: "{count} पंक्तियाँ इम्पोर्ट नहीं हो सकीं।",
          downloadErrors: "सभी त्रुटियाँ डाउनलोड करें (CSV)",
          errorsFilename: "import_errors",
        },
        buttons: {
          export: {
            filename: "product_categories",
            downloadPrefix: "product_categories",
          },
          cancel: "रद्द करें",
          import: "इम्पोर्ट",
          importing: "इम्पोर्ट हो रहा है...",
          downloadTemplate: "टेम्पलेट डाउनलोड करें",
        },
        config: {
          allowedExtensions: ["xls", "xlsx"],
          maxFileSizeBytes: 5242880, // 5 MB
          defaultTemplateFilename: "import_template",
          defaultInputId: "import-input",
          acceptAttribute: ".xls,.xlsx",
        },
      },
    },
    table: {
      actions: "कार्रवाइयाँ",
      status: "स्थिति",
      empty: {
        title: "कोई रिकॉर्ड नहीं मिला",
        message: "अभी दिखाने के लिए कोई डेटा नहीं है।",
      },
      pagination: {
        showing: "दिखा रहे हैं",
        of: "में से",
        entries: "प्रविष्टियाँ",
        page: "पृष्ठ",
        previous: "पिछला",
        next: "अगला",
      },
    },
    // Shared action vocabulary — reused across every screen's row-action icons and buttons.
    // Introduced for i18n so these labels have a single source of truth.
    actions: {
      view: "देखें",
      edit: "संपादित करें",
      delete: "हटाएं",
      save: "सहेजें",
      saving: "सहेजा जा रहा है...",
      saved: "सहेजा गया",
      cancel: "रद्द करें",
      confirm: "पुष्टि करें",
      discard: "छोड़ें",
      reset: "रीसेट करें",
      submit: "सबमिट करें",
      close: "बंद करें",
      ok: "ठीक है",
      keepEditing: "संपादन जारी रखें",
      print: "प्रिंट करें",
      loading: "लोड हो रहा है...",
      printChallan: "चालान प्रिंट करें",
      printOrder: "ऑर्डर प्रिंट करें",
      printPurchaseAdvice: "परचेज़ एडवाइस प्रिंट करें",
      printDeliveryOrder: "डिलीवरी ऑर्डर प्रिंट करें",
      generateChallan: "चालान जनरेट करें",
      editChallan: "चालान संपादित करें",
      editSupervisor: "सुपरवाइज़र संपादित करें",
      editUnloading: "अनलोडिंग संपादित करें",
      markComplete: "पूर्ण के रूप में मार्क करें",
      confirmOrder: "ऑर्डर की पुष्टि करें",
      cancelOrder: "ऑर्डर रद्द करें",
      goToDeliveryOrders: "डिलीवरी ऑर्डर पर जाएं",
      sendEmail: "ईमेल से भेजें",
      sendChallanEmail: "चालान ईमेल से भेजें",
      sendWhatsApp: "WhatsApp से भेजें",
      sendChallanWhatsApp: "चालान WhatsApp से भेजें",
      sendPoEmail: "परचेज़ ऑर्डर ईमेल से भेजें",
      sendPoWhatsApp: "परचेज़ ऑर्डर WhatsApp से भेजें",
      email: "ईमेल",
      whatsapp: "WhatsApp",
      auditLogs: "ऑडिट लॉग",
      preview: "पूर्वावलोकन",
      download: "डाउनलोड",
      remove: "हटाएं",
      addItem: "आइटम जोड़ें",
      exportPrintedPurchaseChallans: "प्रिंटेड परचेज़ चालान एक्सपोर्ट करें",
      exportPurchaseChallans: "परचेज़ चालान एक्सपोर्ट करें",
      deleteWarehouse: "वेयरहाउस हटाएं",
      deleteAttachment: "अटैचमेंट हटाएं",
      expandRow: "पंक्ति विस्तृत करें",
      collapseRow: "पंक्ति संक्षिप्त करें",
    },
    // Shared form-field vocabulary — labels, placeholders, and helper text used across
    // multiple forms. Domain-specific labels stay in their domain STRINGS block.
    formFields: {
      labels: {
        vehicleNumber: "वाहन नंबर",
        driverContact: "ड्राइवर संपर्क",
        vehicleRemark: "वाहन टिप्पणी",
        internalRemarks: "आंतरिक टिप्पणियाँ",
        orderRemark: "ऑर्डर टिप्पणी",
        deliveryOrderRemark: "डिलीवरी ऑर्डर टिप्पणी",
        remark: "टिप्पणी",
        itemRemark: "आइटम टिप्पणी",
        expenseRemark: "खर्च टिप्पणी",
        discountRemark: "छूट टिप्पणी",
        expenses: "खर्च",
        discount: "छूट",
        subtotal: "उप-योग",
        totalWithoutGst: "जीएसटी के बिना कुल",
        roundingOff: "राउंडिंग ऑफ",
        grandTotal: "कुल योग",
        loadingCost: "लोडिंग लागत",
        handlingCharges: "हैंडलिंग शुल्क",
        freightGst: "फ्रेट जीएसटी",
        totalLoaded: "कुल लोड किया गया",
        adjustedWeight: "समायोजित वज़न",
        adjustedPerPieceWeight: "समायोजित प्रति पीस वज़न",
        weight: "वज़न",
        pieces: "पीस",
        actualPieces: "वास्तविक पीस",
        actualQty: "वास्तविक मात्रा",
        perPieceWeight: "प्रति पीस वज़न",
        payment: "पेमेंट शर्तें",
        paymentTermsRequired: "पेमेंट शर्तें *",
        supplier: "सप्लायर",
        supplierInvoiceNo: "सप्लायर इनवॉइस नंबर",
        supplierInvoiceDate: "सप्लायर इनवॉइस तारीख",
        customer: "ग्राहक",
        challanDate: "चालान तारीख",
        poNumber: "परचेज़ ऑर्डर नंबर",
        destination: "गंतव्य",
        contact: "संपर्क",
        supervisor: "सुपरवाइज़र",
        deliveryLocation: "डिलीवरी लोकेशन",
        assignedBy: "असाइन किया गया",
        orderCreatedBy: "ऑर्डर बनाने वाला",
        deliveryOrderCreatedBy: "डिलीवरी ऑर्डर बनाने वाला",
        purchaseAdviceCreatedBy: "परचेज़ एडवाइस बनाने वाला",
        purchaseOrderCreatedBy: "परचेज़ ऑर्डर बनाने वाला",
        challanGeneratedBy: "चालान जनरेट करने वाला",
        generatedBy: "जनरेट करने वाला",
        freightType: "फ्रेट प्रकार",
        freightPricingType: "फ्रेट प्राइसिंग प्रकार",
        hsnCodes: "एचएसएन कोड",
        emptyTruckWeight: "खाली ट्रक वज़न",
        finalTruckWeight: "अंतिम ट्रक वज़न",
        loadedTruckWeight: "लोडेड ट्रक वज़न",
        totalWeight: "कुल वज़न",
        totalItems: "कुल आइटम",
        totalQuantity: "कुल मात्रा",
        receivedQuantity: "प्राप्त मात्रा",
        status: "स्थिति",
        dueDate: "देय तारीख",
        expectedDeliveryDate: "अपेक्षित डिलीवरी तारीख",
        referenceParty: "रेफरेंस पार्टी नाम",
        contactPerson: "संपर्क व्यक्ति",
        contactPhone: "संपर्क फ़ोन",
        warehouseName: "वेयरहाउस नाम",
        fullAddress: "पूरा पता",
        phoneNumber: "फ़ोन नंबर",
        default: "डिफ़ॉल्ट",
        unlistedDealer: "अनसूचीबद्ध डीलर",
        loadByPieces: "पीस के अनुसार लोड करें",
        warehouseInformation: "वेयरहाउस जानकारी",
        additionalInformation: "अतिरिक्त जानकारी",
        summary: "सारांश",
        price: "कीमत",
        priceWithCurrency: "कीमत ({currency})",
        emptyTruckWeightKg: "खाली ट्रक वज़न (kg)",
        finalTruckWeightKg: "अंतिम ट्रक वज़न (kg)",
        loadedTruckWeightKg: "लोडेड ट्रक वज़न (kg)",
        qtyWithUnit: "मात्रा ({unit})",
        weightKg: "वज़न (kg)",
        perPieceWtKg: "प्रति पीस वज़न (kg)",
        expensesPlus: "खर्च (+)",
        discountMinus: "छूट (-)",
        attachments: "अटैचमेंट",
        editPurchaseChallanTitle: "परचेज़ चालान संपादित करें",
        itemsAndQuantities: "आइटम और मात्राएँ",
        itemsAndActualQuantities: "आइटम और वास्तविक मात्राएँ",
        editFinancialFields: "वित्तीय फ़ील्ड संपादित करें",
        saveChanges: "परिवर्तन सहेजें",
        actualWeightKg: "वास्तविक वज़न (Kg)",
        actualQtyWithUnit: "वास्तविक मात्रा ({unit})",
        pricePerUnit: "कीमत / {unit}",
        loadingCostPerKgShort: "लोडिंग लागत / Kg",
        loadingCostPerKg: "लोडिंग लागत प्रति KG",
        emptyTruckWeightKgCapital: "खाली ट्रक वज़न (Kg)",
        finalTruckWeightKgCapital: "अंतिम ट्रक वज़न (Kg)",
        product: "प्रोडक्ट",
        qty: "मात्रा",
        loadBy: "लोड आधार",
        remarks: "टिप्पणियाँ",
        orderedPieces: "ऑर्डर किए गए पीस",
        calculatedWeight: "गणना किया गया वज़न",
        netWeight: "नेट वज़न",
        vehicleAndDispatchDetails: "वाहन और डिस्पैच विवरण",
        subtotalItems: "उप-योग (आइटम)",
        gstAtPercent: "जीएसटी @ {pct}%",
        totalIncGst: "कुल (जीएसटी सहित)",
        items: "आइटम",
        calcWt: "गणना वज़न",
        netTruckWeight: "नेट ट्रक वज़न:",
        selectedLabours: "चयनित मज़दूर ({n})",
        itemsAndInitialPieces: "आइटम और प्रारंभिक पीस",
        itemsToUnload: "अनलोड करने के आइटम",
        editUnloadingTitle: "अनलोडिंग संपादित करें",
        country: "देश",
        state: "राज्य",
        city: "शहर",
        address: "पता",
        mapUrl: "मैप URL",
      },
      placeholders: {
        vehicleNumber: "वाहन नंबर",
        driverContact: "ड्राइवर संपर्क",
        driverContactPhone: "फ़ोन नंबर",
        vehicleRemark: "वाहन टिप्पणी",
        remark: "टिप्पणी",
        itemRemark: "लाइन आइटम नोट...",
        itemRemarkGeneric: "आइटम टिप्पणी",
        expenseRemarkGeneric: "खर्च टिप्पणी",
        discountRemarkGeneric: "छूट टिप्पणी",
        expensesNote: "ट्रांसपोर्ट, मज़दूरी आदि।",
        discountNote: "रिबेट, स्कीम आदि।",
        orderRemark: "ऑर्डर टिप्पणी दर्ज करें",
        challanNotes: "समग्र चालान नोट्स...",
        purchaseOrderNotes: "परचेज़ ऑर्डर नोट्स...",
        transportNotes: "ट्रांसपोर्ट / ड्राइवर के बारे में नोट्स...",
        internalRemarksNotVisibleCustomer: "आंतरिक टिप्पणियाँ (ग्राहक को नहीं दिखतीं)",
        internalRemarksNotVisibleSupplier: "आंतरिक टिप्पणियाँ (सप्लायर को नहीं दिखतीं)",
        referenceParty: "रेफरेंस पार्टी नाम दर्ज करें (वैकल्पिक)",
        selectPaymentTerms: "पेमेंट शर्तें चुनें या टाइप करें",
        selectFreightPricingType: "फ्रेट प्राइसिंग प्रकार चुनें",
        selectFreightType: "फ्रेट प्रकार चुनें",
        loadedWeight: "लोडेड वज़न दर्ज करें",
        contactPerson: "संपर्क व्यक्ति का नाम दर्ज करें",
        contactPhone: "संपर्क फ़ोन दर्ज करें",
        warehouseName: "वेयरहाउस नाम दर्ज करें",
        warehouseAddress: "वेयरहाउस पता दर्ज करें",
        deliveryLocationSearch: "डिलीवरी लोकेशन खोजें...",
        india: "भारत",
        selectState: "राज्य चुनें",
        enterCity: "शहर दर्ज करें",
        mapUrl: "मैप URL दर्ज करें",
        attachmentRemark: "संक्षिप्त टिप्पणी / विवरण (वैकल्पिक)",
        allSupervisors: "सभी सुपरवाइज़र",
        addOrderRemark: "ऑर्डर टिप्पणी जोड़ें...",
        addDeliveryOrderRemark: "डिलीवरी ऑर्डर टिप्पणी जोड़ें...",
        internalRemarksOptional: "आंतरिक टिप्पणियाँ (वैकल्पिक)",
        vehicleNumberExample: "उदा. MH12AD9988",
        driverContactExample: "उदा. 9876543210",
        vehicleRemarkExample: "उदा. काली Tata 407",
        supplierInvoiceNoExample: "उदा. RV0123000601",
        remarkExample: "उदा., टायर प्रेशर जांचें",
        selectDefault: "चुनें...",
        selectOption: "विकल्प चुनें",
        noOptionsFound: "कोई विकल्प नहीं मिला",
      },
      options: {
        freightType: {
          exWarehouse: "EX वेयरहाउस",
          freeDelivery: "सप्लायर द्वारा मुफ्त डिलीवरी",
          freightChargeable: "फ्रेट शुल्क योग्य",
          freeDeliveryFreightToPay: "मुफ्त डिलीवरी, फ्रेट देय",
          freeDeliveryPaidByCompany: "मुफ्त डिलीवरी, कंपनी द्वारा भुगतान",
        },
      },
      empty: {
        noItems: "कोई आइटम नहीं",
        noOrderRemark: "कोई ऑर्डर टिप्पणी नहीं",
        noDeliveryOrderRemark: "कोई डिलीवरी ऑर्डर टिप्पणी नहीं",
        noWarehousesYet: "अभी तक कोई वेयरहाउस नहीं जोड़ा गया",
        warehousesOptional: "वेयरहाउस वैकल्पिक हैं",
        noDataFound: "कोई डेटा नहीं मिला",
      },
      errors: {
        vehicleNumberRequired: "वाहन नंबर आवश्यक है।",
        orderRemarkRequired: "ऑर्डर टिप्पणी आवश्यक है।",
        warehouseNameRequired: "वेयरहाउस नाम आवश्यक है",
        stateRequired: "राज्य आवश्यक है",
        cityRequired: "शहर आवश्यक है",
        addressRequired: "पता आवश्यक है",
        selectLocation: "कृपया एक लोकेशन चुनें",
      },
    },
    // Common alert/toast/dialog messages used from many screens.
    messages: {
      unableToOpenPrintWindow: "प्रिंट विंडो खोलने में असमर्थ। कृपया इस साइट के लिए पॉपअप की अनुमति दें।",
      detailsLoadFailed: "नवीनतम विवरण लोड नहीं हो सके — सबमिट करने से पहले इस फ़ॉर्म को बंद करके दोबारा खोलें।",
      unableToPreparePrintContent: "प्रिंट सामग्री तैयार करने में असमर्थ।",
      unableToLoadOrderForPrinting: "प्रिंटिंग के लिए ऑर्डर विवरण लोड करने में असमर्थ।",
      unableToLoadAdviceForPrinting: "प्रिंटिंग के लिए एडवाइस विवरण लोड करने में असमर्थ।",
      failedToLoadOrderForPrinting: "प्रिंटिंग के लिए ऑर्डर डेटा लोड करने में विफल।",
      failedToLoadDeliveryOrderForPrinting: "प्रिंटिंग के लिए डिलीवरी ऑर्डर डेटा लोड करने में विफल।",
      failedToSendNotification: "नोटिफिकेशन भेजने में विफल",
      unexpectedError: "एक अप्रत्याशित त्रुटि हुई। कृपया पुनः प्रयास करें।",
      unexpectedErrorDuringExport: "एक्सपोर्ट के दौरान एक अप्रत्याशित त्रुटि हुई",
      exportError: "एक्सपोर्ट त्रुटि",
      validationError: "सत्यापन त्रुटि",
      subOrderIdNotFound: "सब-ऑर्डर ID नहीं मिला",
      fromDateFuture: "आरंभ तारीख भविष्य में नहीं हो सकती",
      fromDateGreaterThanTo: "आरंभ तारीख अंतिम तारीख से कम या बराबर होनी चाहिए",
      unknownWarehouse: "अज्ञात वेयरहाउस",
      unknownError: "अज्ञात त्रुटि",
      updateFailed: "अपडेट विफल",
      networkError: "नेटवर्क त्रुटि",
      error: "त्रुटि",
      weightOverage: "वज़न अधिकता",
      weightShortage: "वज़न कमी",
      pressResetHint: "मानों को अपडेट करने के लिए रीसेट दबाएं",
      estimatedWeightTooltip: "मात्रा और मानक वज़न/लंबाई के आधार पर अनुमानित वज़न kg में",
    },
    print: {
      invoice: "इनवॉइस",
      deliveryChallan: "डिलीवरी चालान",
      deliveryOrder: "डिलीवरी ऑर्डर",
      purchaseChallan: "परचेज़ चालान",
      purchaseAdvice: "परचेज़ एडवाइस",
      hsnSummary: "एचएसएन सारांश",
      hsnSummaryUpper: "एचएसएन सारांश",
      columns: {
        size: "साइज़",
        actualQty: "वास्तविक मात्रा",
        actQty: "वास्त. मात्रा",
        actPcs: "वास्त. पीस",
        igst: "आईजीएसटी",
        hsn: "एचएसएन",
        hsnCode: "एचएसएन कोड",
        pcs: "पीस",
        pieces: "पीस",
        receivedQty: "प्राप्त मात्रा",
        gst: "जीएसटी",
        amount: "राशि",
        gstAmount: "जीएसटी राशि",
        totalIncGst: "कुल (जीएसटी सहित)",
        product: "प्रोडक्ट",
        category: "कैटेगरी",
        unit: "यूनिट",
        description: "विवरण",
        weight: "वज़न",
        rate: "दर",
        remarks: "टिप्पणियाँ",
        remark: "टिप्पणी",
        quantity: "मात्रा",
        qty: "मात्रा",
        poItemQty: "परचेज़ ऑर्डर आइटम मात्रा",
        stdLength: "मानक लंबाई",
        stdWeight: "मानक वज़न",
        itemDescription: "आइटम विवरण",
        ordQty: "ऑर्डर मात्रा",
        totalQty: "कुल मात्रा",
        itemTotal: "आइटम कुल",
      },
      headers: {
        name: "नाम:",
        customer: "ग्राहक:",
        customerAddress: "ग्राहक पता:",
        supplier: "सप्लायर:",
        date: "तारीख:",
        driver: "ड्राइवर:",
        created: "बनाया गया:",
        printed: "प्रिंट किया गया:",
        challanSerial: "चालान सीरियल:",
        challanNo: "चालान नंबर:",
        orderDate: "ऑर्डर तारीख:",
        paymentTerms: "पेमेंट शर्तें:",
        dueDate: "देय तारीख:",
        deliveryAddress: "डिलीवरी पता:",
        timeCreated: "बनाने का समय:",
        timePrint: "प्रिंट का समय:",
        truckNumber: "ट्रक नंबर:",
        driverContact: "ड्राइवर संपर्क:",
        emptyWeight: "खाली वज़न:",
        finalWeight: "अंतिम वज़न:",
        emptyWt: "खाली वज़न:",
        finalWt: "अंतिम वज़न:",
        generatedBy: "जनरेट करने वाला:",
        orderCreatedBy: "ऑर्डर बनाने वाला:",
        deliveryOrderCreatedBy: "डिलीवरी ऑर्डर बनाने वाला:",
        supervisors: "सुपरवाइज़र:",
        orderRemark: "ऑर्डर टिप्पणी:",
        remarks: "टिप्पणियाँ:",
        adviceNumber: "एडवाइस नंबर:",
        poNumber: "परचेज़ ऑर्डर नंबर:",
        adviceDate: "एडवाइस तारीख:",
        status: "स्थिति:",
        warehouse: "वेयरहाउस:",
      },
      summary: {
        subtotal: "उप-योग",
        loading: "लोडिंग",
        loadingGst: "लोडिंग जीएसटी",
        freightGst: "फ्रेट जीएसटी",
        handlingCharges: "हैंडलिंग शुल्क",
        expenses: "खर्च",
        expense: "खर्च",
        discount: "छूट",
        totalWithoutGst: "जीएसटी के बिना कुल",
        roundingOff: "राउंडिंग ऑफ",
        grandTotal: "कुल योग",
        itemTotal: "आइटम कुल",
        summary: "सारांश",
        additionalInformation: "अतिरिक्त जानकारी",
        totalItems: "कुल आइटम",
        totalQuantity: "कुल मात्रा",
        receivedQuantity: "प्राप्त मात्रा",
        status: "स्थिति",
      },
      empty: {
        noItems: "कोई आइटम नहीं",
      },
      buttons: {
        printChallan: "चालान प्रिंट करें",
        printOrder: "ऑर्डर प्रिंट करें",
        printDeliveryOrder: "डिलीवरी ऑर्डर प्रिंट करें",
        printPurchaseAdvice: "परचेज़ एडवाइस प्रिंट करें",
        printChallanUpper: "चालान प्रिंट करें",
      },
    },
    // Widget/section titles used across dashboard and analytics screens.
    analyticsWidgets: {
      topProducts: {
        title: "टॉप प्रोडक्ट",
        subtitle: "इस अवधि में ऑर्डर वॉल्यूम के अनुसार",
        items: "आइटम",
      },
      lowStock: {
        title: "स्टॉक कमी अलर्ट",
        subtitle: "उपलब्ध स्टॉक से अधिक पेंडिंग ऑर्डर वाले प्रोडक्ट",
      },
      challanList: {
        title: "चालान सूची",
        subtitle: "चयनित अवधि के सभी डिलीवरी चालान",
      },
      salesLeaderboard: {
        title: "सेल्स लीडरबोर्ड",
        subtitle: "वज़न प्रदर्शन के अनुसार रैंक किए गए स्टाफ",
      },
      supervisorLeaderboard: {
        title: "सुपरवाइज़र लीडरबोर्ड",
      },
      supervisorWorkload: {
        title: "सुपरवाइज़र वर्कलोड",
      },
      pendingBacklog: {
        title: "पेंडिंग बैकलॉग",
        subtitle: "चालान की प्रतीक्षा में असाइन किए गए सब-ऑर्डर — सबसे पुराने पहले",
      },
      unprintedChallans: {
        title: "अनप्रिंटेड चालान",
        subtitle: "चालान जनरेट हुए लेकिन अभी तक प्रिंट नहीं हुए। प्रिंटेड मार्क होने तक टैली डेबुक से गायब।",
        empty: "कोई अनप्रिंटेड चालान नहीं",
        tiles: {
          totalUnprinted: "कुल अनप्रिंटेड",
          over1Day: "1 दिन से अधिक",
          over7Days: "7 दिन से अधिक",
          oldestPending: "सबसे पुराना पेंडिंग",
        },
        searchPlaceholder: "चालान नं., ऑर्डर नं., ग्राहक, वाहन…",
        apply: "लागू करें",
        loading: "लोड हो रहा है…",
        order: "ऑर्डर",
        age: "आयु",
      },
    },
    // Shared column-header vocabulary for analytics tables (dashboard, challan-analytics,
    // supervisor-analytics, sales-analytics, movement-analytics, assigned-to-challan).
    analyticsColumns: {
      rank: "रैंक",
      product: "प्रोडक्ट",
      category: "कैटेगरी",
      orders: "ऑर्डर",
      cancelled: "रद्द",
      delivered: "डिलीवर हो गया",
      quantity: "मात्रा",
      weight: "वज़न",
      warehouse: "वेयरहाउस",
      inStock: "स्टॉक में",
      pendingOrders: "पेंडिंग ऑर्डर",
      shortage: "कमी",
      supervisor: "सुपरवाइज़र",
      assigned: "असाइन किया गया",
      loading: "लोडिंग",
      loaded: "लोड हो गया",
      total: "कुल",
      pendingWt: "पेंडिंग वज़न",
      loadedWt: "लोडेड वज़न",
      salesStaff: "सेल्स स्टाफ",
      customer: "ग्राहक",
      tallyName: "टैली नाम",
      challan: "चालान",
      challanNo: "चालान नं.",
      vehicle: "वाहन",
      status: "स्थिति",
      date: "तारीख",
      printed: "प्रिंट किया गया",
      remark: "टिप्पणी",
      adviceNo: "एडवाइस नं.",
      poNumber: "परचेज़ ऑर्डर नंबर",
      supplier: "सप्लायर",
      orderNo: "ऑर्डर नं.",
      generatedBy: "जनरेट करने वाला",
      pending: "पेंडिंग",
      aging: "एजिंग",
      action: "कार्रवाई",
      challanDate: "चालान तारीख",
      subOrder: "सब-ऑर्डर",
      weightT: "वज़न (T)",
      weightKg: "वज़न (KG)",
      weightTons: "वज़न (टन)",
      search: "खोजें",
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
      groupBy: "समूह आधार",
      unitType: "यूनिट प्रकार",
      challanPrinted: "चालान प्रिंट किया गया",
      productCatalogue: "प्रोडक्ट कैटलॉग",
      all: "सभी",
      allSupervisors: "सभी सुपरवाइज़र",
      notPrinted: "प्रिंट नहीं हुआ",
      kg: "KG",
      ton: "टन",
      allStatuses: "सभी स्थितियाँ",
      inTransit: "ट्रांज़िट में",
      productSizeSku: "प्रोडक्ट साइज़ (SKU)",
      applyFilters: "फ़िल्टर लागू करें",
      searching: "खोज रहे हैं...",
      challans: "चालान",
      productName: "प्रोडक्ट नाम",
      skuSize: "SKU / साइज़",
      qtyUnits: "मात्रा / यूनिट",
      weightT2: "वज़न (T)",
      count: "संख्या",
      average: "औसत",
      min: "न्यूनतम",
      max: "अधिकतम",
      completed: "पूर्ण",
      unloading: "अनलोडिंग",
      totalWeight: "कुल वज़न",
      confirmed: "पुष्टि की गई",
      invoiced: "इनवॉइस किया गया",
      deliveryAssigned: "डिलीवरी असाइन की गई",
      supervisorAssigned: "सुपरवाइज़र असाइन किया गया",
      unloaded: "अनलोड हो गया",
      deliveries: "डिलीवरी",
      deliveryRate: "डिलीवरी दर",
      qty: "मात्रा",
    },
    filters: {
      from: "से",
      to: "तक",
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
      status: "स्थिति",
      allStatuses: "सभी स्थितियाँ",
      sortBy: "क्रमबद्ध करें",
      apply: "लागू करें",
      reset: "रीसेट करें",
    },
    statuses: {
      pending: "पेंडिंग",
      loading: "लोडिंग",
      loaded: "लोड हो गया",
      inTransit: "ट्रांज़िट में",
      delivered: "डिलीवर हो गया",
      unloaded: "अनलोड हो गया",
      assigned: "असाइन किया गया",
      confirmed: "पुष्टि की गई",
      invoiced: "इनवॉइस किया गया",
      cancelled: "रद्द",
      supervisorAssigned: "सुपरवाइज़र असाइन किया गया",
    },
    sections: {
      overview: "अवलोकन",
      dailyTrend: "दैनिक रुझान",
    },
    emptyStates: {
      noData: "कोई डेटा नहीं",
      noDataAvailable: "कोई डेटा उपलब्ध नहीं",
      loadingDailyData: "दैनिक डेटा लोड हो रहा है...",
      noDailyData: "चयनित फ़िल्टर के लिए कोई दैनिक डेटा नहीं मिला।",
    },
    labels: {
      unassigned: "असाइन नहीं किया गया",
      notAssigned: "असाइन नहीं किया गया",
      daysCountSuffix: "दिन",
      itemsCountSuffix: "आइटम",
      kgSuffix: "kg",
    },
    form: {
      requiredIndicator: "*",
      editHint: {
        keepCurrent: "(मौजूदा बनाए रखने के लिए खाली छोड़ें)",
      },
      assignPersonnel: {
        title: "डिलीवरी कर्मी असाइन करें",
        subtitle: "इस ऑर्डर के लिए डिलीवरी सुपरवाइज़र और डिलीवरी बॉय चुनें।",
        labels: {
          supervisor: "डिलीवरी सुपरवाइज़र",
          deliveryBoy: "डिलीवरी बॉय",
          vehicleNumber: "वाहन नंबर",
          driverContact: "ड्राइवर संपर्क",
        },
        buttons: {
          cancel: "रद्द करें",
          assign: "असाइन करें",
          assigning: "असाइन हो रहा है...",
        },
        messages: {
          loadingUsers: "यूज़र लोड हो रहे हैं...",
          selectAtLeastOne: "कम से कम एक व्यक्ति चुनें",
          invalidOrder: "अमान्य ऑर्डर",
          vehicleRequired: "वाहन नंबर आवश्यक है",
          driverContactRequired: "ड्राइवर संपर्क आवश्यक है",
          assignFailed: "कर्मी असाइन करने में विफल",
        },
        placeholders: {
          vehicleNumber: "उदा., KA-53-1234",
          driverContact: "उदा., 9876543210",
        },
      },
      startTruck: {
        title: "ट्रक लोडिंग शुरू करें",
        subtitle: "ट्रक विवरण दर्ज करें और लोडिंग के लिए मज़दूर असाइन करें।",
        labels: {
          vehicleNumber: "वाहन नंबर",
          driverContact: "ड्राइवर संपर्क",
          emptyTruckWeight: "खाली ट्रक वज़न (kg)",
          labours: "मज़दूर",
          remark: "टिप्पणी",
        },
        placeholders: {
          vehicleNumber: "उदा., KA-53-1234",
          driverContact: "उदा., 9876543210",
          emptyTruckWeight: "उदा., 5000",
          remark: "उदा., टायर प्रेशर जांचें",
        },
        buttons: {
          cancel: "रद्द करें",
          start: "लोडिंग शुरू करें",
          starting: "शुरू हो रहा है...",
        },
        messages: {
          loadingUsers: "मज़दूर लोड हो रहे हैं...",
          selectAtLeastOneLabour: "कम से कम एक मज़दूर चुनें",
          vehicleRequired: "वाहन नंबर आवश्यक है",
          invalidVehicleNumber:
            "कृपया एक मान्य वाहन नंबर दर्ज करें (उदा., MH-12-AB-1234)",
          invalidDriverContact: "कृपया एक मान्य ड्राइवर संपर्क नंबर दर्ज करें",
          invalidEmptyTruckWeight: "कृपया एक मान्य खाली ट्रक वज़न दर्ज करें",
          formErrors: "कृपया हाइलाइट किए गए फ़ील्ड ठीक करें",
          invalidOrder: "अमान्य ऑर्डर",
          startFailed: "ट्रक लोडिंग शुरू करने में विफल",
        },
      },
    },

    /* Backwards-compatible alias: some components use `orders.forms` */
    forms: {
      requiredIndicator: "*",
      editHint: {
        keepCurrent: "(मौजूदा बनाए रखने के लिए खाली छोड़ें)",
      },
      assignPersonnel: {
        title: "डिलीवरी कर्मी असाइन करें",
        subtitle: "इस ऑर्डर के लिए डिलीवरी सुपरवाइज़र और डिलीवरी बॉय चुनें।",
        labels: {
          supervisor: "डिलीवरी सुपरवाइज़र",
          deliveryBoy: "डिलीवरी बॉय",
          vehicleNumber: "वाहन नंबर",
          driverContact: "ड्राइवर संपर्क",
        },
        buttons: {
          cancel: "रद्द करें",
          assign: "असाइन करें",
          assigning: "असाइन हो रहा है...",
        },
        messages: {
          loadingUsers: "यूज़र लोड हो रहे हैं...",
          selectAtLeastOne: "कम से कम एक व्यक्ति चुनें",
          invalidOrder: "अमान्य ऑर्डर",
          vehicleRequired: "वाहन नंबर आवश्यक है",
          driverContactRequired: "ड्राइवर संपर्क आवश्यक है",
          assignFailed: "कर्मी असाइन करने में विफल",
        },
        placeholders: {
          vehicleNumber: "उदा., MH-12-AB-1234",
          driverContact: "उदा., 9876543210",
        },
      },
      startTruck: {
        title: "ट्रक लोडिंग शुरू करें",
        subtitle: "ट्रक विवरण दर्ज करें और लोडिंग के लिए मज़दूर असाइन करें।",
        labels: {
          vehicleNumber: "वाहन नंबर",
          driverContact: "ड्राइवर संपर्क",
          emptyTruckWeight: "खाली ट्रक वज़न (kg)",
          labours: "मज़दूर",
          remark: "टिप्पणी",
        },
        placeholders: {
          vehicleNumber: "उदा., MH-12-AB-1234",
          driverContact: "उदा., 9876543210",
          emptyTruckWeight: "उदा., 5000",
          remark: "उदा., टायर प्रेशर जांचें",
          selectLabour: "जोड़ने के लिए मज़दूर चुनें...",
        },
        buttons: {
          cancel: "रद्द करें",
          start: "लोडिंग शुरू करें",
          starting: "शुरू हो रहा है...",
        },
        messages: {
          loadingUsers: "मज़दूर लोड हो रहे हैं...",
          selectAtLeastOneLabour: "कम से कम एक मज़दूर चुनें",
          vehicleRequired: "वाहन नंबर आवश्यक है",
          invalidVehicleNumber:
            "कृपया एक मान्य वाहन नंबर दर्ज करें (उदा., MH-12-AB-1234)",
          invalidDriverContact: "कृपया एक मान्य ड्राइवर संपर्क नंबर दर्ज करें",
          invalidEmptyTruckWeight: "कृपया एक मान्य खाली ट्रक वज़न दर्ज करें",
          formErrors: "कृपया हाइलाइट किए गए फ़ील्ड ठीक करें",
          invalidOrder: "अमान्य ऑर्डर",
          startFailed: "ट्रक लोडिंग शुरू करने में विफल",
          selectLabourHelper:
            "उन्हें सूची में जोड़ने के लिए ड्रॉपडाउन से एक मज़दूर चुनें।",
          noLaboursSelected: "कोई मज़दूर नहीं चुना गया",
        },
      },
      completeTruckLoading: {
        title: "ट्रक लोडिंग पूर्ण करें",
        subtitle:
          "आइटम की समीक्षा करें, वास्तविक लोड की गई मात्राएँ दर्ज करें, और ट्रक वज़न अंतिम रूप दें।",
        labels: {
          orderId: "ऑर्डर ID",
          truckWeight: "अंतिम ट्रक वज़न (kg)",
          items: "आइटम",
          productName: "प्रोडक्ट नाम",
          quantity: "मात्रा",
          actualQuantity: "वास्तविक मात्रा",
          unknownProduct: "अज्ञात प्रोडक्ट",
          approxWeight: "अनुमानित वज़न:",
          loadingCost: "लोडिंग लागत:",
          totalLoadingCost: "कुल लोडिंग लागत",
        },
        messages: {
          loadingOrder: "ऑर्डर विवरण लोड हो रहा है...",
          invalidOrder: "अमान्य ऑर्डर या ऑर्डर नहीं मिला",
          noItems: "इस ऑर्डर में कोई आइटम नहीं",
          invalidWeight: "कृपया एक मान्य अंतिम ट्रक वज़न दर्ज करें",
          invalidQuantity: "कृपया वास्तविक मात्राओं के लिए मान्य संख्याएँ दर्ज करें",
          completeFailed: "ट्रक लोडिंग पूर्ण करने में विफल",
          weightsAdjusted:
            "* नेट ट्रक वज़न मेल न खाने के कारण वज़न समायोजित (फैक्टर: {factor})।",
        },
        placeholders: {
          truckWeight: "उदा., 15000",
        },
        buttons: {
          cancel: "रद्द करें",
          complete: "लोडिंग पूर्ण करें",
          completing: "पूर्ण हो रहा है...",
        },
      },
      completeUnloadingForm: {
        title: "अनलोडिंग पूर्ण करें",
        subtitle:
          "आइटम की समीक्षा करें, वास्तविक अनलोड की गई मात्राएँ दर्ज करें, और ट्रक वज़न अंतिम रूप दें।",
        labels: {
          orderId: "ऑर्डर ID",
          truckWeight: "अंतिम ट्रक वज़न (kg)",
          items: "आइटम",
          productName: "प्रोडक्ट नाम",
          quantity: "मात्रा",
          actualQuantity: "वास्तविक मात्रा",
          unknownProduct: "अज्ञात प्रोडक्ट",
          approxWeight: "अनुमानित वज़न:",
          loadingCost: "अनलोडिंग लागत:",
          totalLoadingCost: "कुल अनलोडिंग लागत",
        },
        messages: {
          loadingOrder: "विवरण लोड हो रहा है...",
          invalidOrder: "अमान्य ऑर्डर या ऑर्डर नहीं मिला",
          noItems: "इस एडवाइस में कोई आइटम नहीं",
          invalidWeight: "कृपया एक मान्य अंतिम ट्रक वज़न दर्ज करें",
          invalidQuantity: "कृपया वास्तविक मात्राओं के लिए मान्य संख्याएँ दर्ज करें",
          completeFailed: "अनलोडिंग पूर्ण करने में विफल",
          weightsAdjusted:
            "* नेट ट्रक वज़न मेल न खाने के कारण वज़न समायोजित (फैक्टर: {factor})।",
        },
        placeholders: {
          truckWeight: "उदा., 5000",
        },
        buttons: {
          cancel: "रद्द करें",
          complete: "अनलोडिंग पूर्ण करें",
          completing: "पूर्ण हो रहा है...",
        },
      },
    },
    messageAlert: {
      closeAria: "संदेश बंद करें",
      providerError:
        "useMessageAlert must be used within a MessageAlertProvider",
    },
    developerCredit: {
      prefix: "द्वारा विकसित और प्रबंधित",
      linkText: "Flip Retail",
    },
  },
  dashboard: {
    title: "डैशबोर्ड",
    subtitle: "आपके व्यवसाय प्रदर्शन का अवलोकन",
    generatedAt: "जनरेट किया गया",
    exportExcel: "एक्सेल निर्यात",
    exportPdf: "पीडीएफ निर्यात",
    kpis: {
      todayRevenue: "आज का राजस्व",
      todayOrders: "आज के ऑर्डर",
      monthRevenue: "महीने का राजस्व",
      monthOrders: "महीने के ऑर्डर",
      revenueGrowth: "राजस्व वृद्धि",
      pendingOrders: "लंबित ऑर्डर",
      inTransitOrders: "ट्रांज़िट में",
      deliveredToday: "आज डिलीवर किए गए",
      lowStockAlerts: "कम स्टॉक अलर्ट",
      outOfStockItems: "स्टॉक समाप्त",
      pendingPurchaseOrders: "लंबित परचेज़ ऑर्डर",
      expectedDeliveries: "अपेक्षित डिलीवरी",
    },
    charts: {
      salesTrend: {
        title: "सेल्स ट्रेंड",
        revenue: "राजस्व",
        orders: "ऑर्डर",
      },
      orderStatus: {
        title: "ऑर्डर स्थिति वितरण",
        titleShort: "ऑर्डर स्थिति",
        titleBreakdown: "ऑर्डर स्थिति विवरण",
        totalOrders: "कुल: {count} ऑर्डर",
        noData: "कोई डेटा उपलब्ध नहीं",
        tonSuffix: "टन",
        statuses: {
          pending: "लंबित",
          in_transit: "ट्रांज़िट में",
          loading: "लोडिंग",
          loaded: "लोड किया गया",
          invoiced: "इनवॉइस किया गया",
        },
      },
    },
    tables: {
      topProducts: {
        title: "सर्वाधिक बिकने वाले प्रोडक्ट",
        columns: {
          product: "प्रोडक्ट",
          category: "कैटेगरी",
          quantity: "कुल मात्रा",
          revenue: "कुल राजस्व",
          orders: "ऑर्डर संख्या",
          avgPrice: "औसत कीमत",
        },
      },
      lowStock: {
        title: "कम स्टॉक अलर्ट",
        columns: {
          product: "प्रोडक्ट",
          warehouse: "वेयरहाउस",
          currentStock: "वर्तमान स्टॉक",
          minStock: "न्यूनतम स्टॉक",
          shortage: "कमी",
          pendingOrders: "लंबित ऑर्डर",
        },
      },
    },
  },
  salesAnalytics: {
    page: {
      title: "सेल्स स्टाफ",
      subtitle:
        "प्रोडक्ट, कैटेगरी और वेयरहाउस के अनुसार अपने सेल्स प्रदर्शन का विश्लेषण करें",
    },
    filters: {
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
      submit: "सबमिट करें",
      period: "अवधि",
      periods: {
        today: "आज",
        yesterday: "कल",
        last_7_days: "पिछले 7 दिन",
        last_30_days: "पिछले 30 दिन",
        last_90_days: "पिछले 90 दिन",
        this_month: "इस महीने",
        last_month: "पिछले महीने",
        this_year: "इस साल",
        custom: "कस्टम",
      },
    },
    overview: {
      title: "सेल्स अवलोकन",
      sectionTitle: "अवलोकन",
      totalOrders: "कुल ऑर्डर",
      totalRevenue: "कुल राजस्व",
      totalQuantity: "कुल मात्रा",
      totalWeight: "कुल वज़न",
      avgOrderValue: "औसत ऑर्डर मूल्य",
      totalDiscount: "कुल छूट",
      totalFreight: "कुल भाड़ा",
      totalGst: "कुल जीएसटी",
      subs: {
        inSelectedPeriod: "चयनित अवधि में",
        dispatchedWeight: "डिस्पैच किया गया वज़न",
        unitsOrdered: "ऑर्डर की गई यूनिट",
      },
    },
    summary: {
      sections: {
        overview: "अवलोकन",
        byStatus: "स्थिति के अनुसार",
        fulfillment: "पूर्ति",
      },
      cards: {
        totalOrders: "कुल ऑर्डर",
        totalWeight: "कुल वज़न",
        confirmed: "पुष्ट",
        pending: "लंबित",
        cancelled: "रद्द",
        delivered: "डिलीवर किए गए",
        invoiced: "इनवॉइस किए गए",
        challanGenerated: "चालान जनरेट किया गया",
      },
      subs: {
        allOrdersInPeriod: "अवधि के सभी ऑर्डर",
        tonsKgDispatched: "टन / केजी डिस्पैच किया गया",
        confirmedOrders: "पुष्ट ऑर्डर",
        awaitingConfirmation: "पुष्टि की प्रतीक्षा में",
        cancelledOrders: "रद्द ऑर्डर",
        ordersDelivered: "डिलीवर किए गए ऑर्डर",
        ordersInvoiced: "इनवॉइस किए गए ऑर्डर",
        challansCreated: "बनाए गए चालान",
      },
    },
    charts: {
      trend: {
        title: "सेल्स ट्रेंड",
        subtitle: "दैनिक वज़न और ऑर्डर गतिविधि",
        revenue: "राजस्व",
        orders: "ऑर्डर",
        quantity: "मात्रा",
        weightTon: "वज़न (टन)",
      },
      category: {
        title: "कैटेगरी के अनुसार सेल्स",
        heading: "कैटेगरी के अनुसार",
        subtitle: "मात्रा वितरण",
        orderCountSuffix: "ऑर्डर",
      },
      warehouse: {
        title: "वेयरहाउस के अनुसार सेल्स",
        heading: "वेयरहाउस के अनुसार",
        subtitle: "लोकेशन के अनुसार ऑर्डर और वज़न",
        warehousesCount: "{count} वेयरहाउस",
        warehousePrefix: "वेयरहाउस #{id}",
        orders: "ऑर्डर",
        weight: "वज़न",
        qtyLabel: "मात्रा:",
        unitsSuffix: "यूनिट",
        tonSuffix: "टन",
      },
      orderStatus: {
        title: "ऑर्डर स्थिति वितरण",
        subtitle: "कुल: सभी स्थितियों में {count} ऑर्डर",
        tonSuffix: "टन",
        statuses: {
          pending: "लंबित",
          delivery_assigned: "डिलीवरी असाइन की गई",
          loading: "लोडिंग",
          loaded: "लोड किया गया",
          in_transit: "ट्रांज़िट में",
          delivered: "डिलीवर किया गया",
          invoiced: "इनवॉइस किया गया",
          confirmed: "पुष्ट",
          cancelled: "रद्द",
        },
      },
    },
    topProductsWidget: {
      title: "टॉप प्रोडक्ट",
      subtitle: "इस अवधि में ऑर्डर की गई मात्रा के अनुसार",
      productsCount: "{count} प्रोडक्ट",
      headers: {
        product: "प्रोडक्ट",
        category: "कैटेगरी",
        orders: "ऑर्डर",
        quantity: "मात्रा",
      },
    },
    dailyTable: {
      title: "दैनिक ट्रेंड",
      subtitle: "दिन-प्रतिदिन सेल्स गतिविधि",
      daysCount: "{count} दिन",
      loading: "दैनिक डेटा लोड हो रहा है...",
      noData: "चयनित फ़िल्टर के लिए कोई दैनिक डेटा नहीं मिला।",
      headers: {
        index: "#",
        date: "तारीख",
        orders: "ऑर्डर",
      },
    },
    ordersTable: {
      title: "ऑर्डर सूची",
      subtitle: "चयनित अवधि के सभी सेल्स ऑर्डर",
      ordersCount: "{count} ऑर्डर",
      loading: "ऑर्डर लोड हो रहे हैं...",
      noData: "चयनित फ़िल्टर के लिए कोई ऑर्डर नहीं मिला।",
      headers: {
        index: "#",
        orderNo: "ऑर्डर नंबर",
        customer: "ग्राहक",
        salesStaff: "सेल्स स्टाफ",
        status: "स्थिति",
        date: "तारीख",
      },
    },
    filtersExtra: {
      allSalesStaff: "सभी सेल्स स्टाफ",
      allStatuses: "सभी स्थितियाँ",
      apply: "लागू करें",
      loading: "लोड हो रहा है...",
      status: "स्थिति",
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
      statusOptions: {
        pending: "लंबित",
        confirmed: "पुष्ट",
        cancelled: "रद्द",
        assigned: "असाइन किया गया",
        loading: "लोडिंग",
        loaded: "लोड किया गया",
        inTransit: "ट्रांज़िट में",
      },
    },
    mySubOrders: {
      loading: "डिलीवरी लोड हो रही हैं…",
      empty: "कोई सक्रिय डिलीवरी सब-ऑर्डर नहीं",
      unassigned: "असाइन नहीं किया गया",
      kg: "केजी",
      statuses: {
        pending: "लंबित",
        assigned: "असाइन किया गया",
        loading: "लोडिंग",
        loaded: "लोड किया गया",
        inTransit: "ट्रांज़िट में",
        delivered: "डिलीवर किया गया",
        cancelled: "रद्द",
      },
      detail: {
        orderDetail: "ऑर्डर विवरण",
        supervisor: "सुपरवाइज़र",
        mobile: "मोबाइल",
        vehicle: "वाहन",
        totalWeight: "कुल वज़न",
        challan: "चालान",
        items: "आइटम",
        loadingItems: "आइटम लोड हो रहे हैं…",
        noItems: "कोई आइटम विवरण उपलब्ध नहीं",
        headers: {
          product: "प्रोडक्ट",
          ordered: "ऑर्डर किया गया",
          actual: "वास्तविक",
          pieces: "पीस",
        },
        loadByPieces: "पीस के अनुसार लोड करें",
      },
    },
    tables: {
      topProducts: {
        title: "सर्वाधिक बिकने वाले प्रोडक्ट",
        headers: {
          product: "प्रोडक्ट नाम",
          category: "कैटेगरी",
          quantity: "कुल मात्रा",
          revenue: "कुल राजस्व",
          orders: "ऑर्डर संख्या",
          avgPrice: "औसत कीमत",
        },
      },
      warehouse: {
        title: "वेयरहाउस के अनुसार सेल्स",
        headers: {
          warehouse: "वेयरहाउस नाम",
          orders: "ऑर्डर",
          revenue: "कुल राजस्व",
          quantity: "कुल मात्रा",
        },
      },
      orderStatus: {
        title: "ऑर्डर स्थिति वितरण",
        headers: {
          status: "स्थिति",
          count: "संख्या",
          percentage: "प्रतिशत",
          amount: "कुल राशि",
        },
        statuses: {
          pending: "लंबित",
          delivery_assigned: "डिलीवरी असाइन की गई",
          loading: "लोडिंग",
          loaded: "लोड किया गया",
          in_transit: "ट्रांज़िट में",
          delivered: "डिलीवर किया गया",
          invoiced: "इनवॉइस किया गया",
        },
      },
    },
  },
  deliveryOrders: {
    page: {
      header: {
        title: "डिलीवरी ऑर्डर",
        subtitle: "डिलीवरी सब-ऑर्डर प्रबंधित करें और स्थिति ट्रैक करें",
      },
    },
    table: {
      columns: {
        subOrderNumber: "सब-ऑर्डर नंबर",
        orderNumber: "ऑर्डर नंबर",
      },
    },
  },
  deliveryAnalytics: {
    page: {
      title: "डिलीवरी एनालिटिक्स",
      subtitle:
        "अपने डिलीवरी प्रदर्शन, चालान और कर्मचारी दक्षता का विश्लेषण करें",
    },
    filters: {
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
      submit: "सबमिट करें",
      period: "अवधि",
      periods: {
        today: "आज",
        yesterday: "कल",
        last_7_days: "पिछले 7 दिन",
        last_30_days: "पिछले 30 दिन",
        last_90_days: "पिछले 90 दिन",
        this_month: "इस महीने",
        last_month: "पिछले महीने",
        this_year: "इस साल",
        custom: "कस्टम",
      },
    },
    overview: {
      title: "डिलीवरी अवलोकन",
      challanOverview: "चालान अवलोकन",
      totalChallans: "कुल चालान",
      totalWeight: "कुल वज़न",
      totalAmount: "कुल राशि",
      totalQuantity: "कुल मात्रा",
      challanCount: "चालान संख्या",
      tonSuffix: "टन",
      subs: {
        inSelectedPeriod: "चयनित अवधि में",
        dispatchedWeight: "डिस्पैच किया गया वज़न",
      },
    },
    charts: {
      trend: {
        title: "डिलीवरी ट्रेंड",
        challanTitle: "चालान ट्रेंड",
        subtitle: "दैनिक चालान संख्या और डिस्पैच किया गया वज़न",
        amount: "राशि",
        challans: "चालान",
        quantity: "मात्रा",
        weightTon: "वज़न (टन)",
        empty: "इस अवधि के लिए कोई चालान डेटा नहीं",
      },
      performance: {
        title: "डिलीवरी प्रदर्शन",
        subtitle: "ऑर्डर पूर्ति विवरण",
        totalOrders: "कुल ऑर्डर",
        totalOrdersSuffix: "कुल ऑर्डर",
        delivered: "डिलीवर किए गए",
        pending: "लंबित",
        inTransit: "ट्रांज़िट में",
        avgDeliveryDays: "औसत डिलीवरी दिन",
        avgDeliveryTime: "औसत डिलीवरी समय",
        daysSuffix: "दिन",
        deliveryRate: "डिलीवरी दर",
      },
      personnel: {
        title: "कर्मचारी प्रदर्शन",
        subtitle: "इस अवधि में डिलीवरी सुपरवाइज़र गतिविधि",
        supervisorsCount: "{count} सुपरवाइज़र",
        avgPerDelivery: "औसत/डिलीवरी",
        deliveries: "डिलीवरी",
        weight: "वज़न",
        tonSuffix: "टन",
        headers: {
          name: "नाम",
          role: "भूमिका",
          deliveries: "कुल डिलीवरी",
          amount: "कुल राशि",
          avgPerDelivery: "प्रति डिलीवरी औसत",
        },
      },
    },
  },
  purchaseAnalytics: {
    page: {
      title: "परचेज़ एनालिटिक्स",
      subtitle: "अपने परचेज़ ऑर्डर, सप्लायर और ट्रेंड का विश्लेषण करें",
    },
    filters: {
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
      submit: "सबमिट करें",
      period: "अवधि",
      periods: {
        today: "आज",
        yesterday: "कल",
        last_7_days: "पिछले 7 दिन",
        last_30_days: "पिछले 30 दिन",
        last_90_days: "पिछले 90 दिन",
        this_month: "इस महीने",
        last_month: "पिछले महीने",
        this_year: "इस साल",
        custom: "कस्टम",
      },
    },
    overview: {
      title: "परचेज़ अवलोकन",
      totalOrders: "कुल ऑर्डर",
      totalWeightTons: "कुल वज़न (टन)",
      totalQuantity: "कुल मात्रा",
      pendingOrders: "लंबित ऑर्डर",
      receivedOrders: "प्राप्त ऑर्डर",
    },
    charts: {
      trend: {
        title: "परचेज़ ट्रेंड",
        weightTons: "वज़न (टन)",
        orders: "ऑर्डर",
        quantity: "मात्रा",
      },
    },
    tables: {
      topSuppliers: {
        title: "टॉप सप्लायर",
        headers: {
          supplier: "सप्लायर",
          orders: "ऑर्डर",
          weightTons: "वज़न (टन)",
          quantity: "मात्रा",
        },
      },
    },
  },
  inventoryAnalytics: {
    page: {
      title: "इन्वेंटरी एनालिटिक्स",
      subtitle: "वेयरहाउस स्टॉक और कम इन्वेंटरी अलर्ट पर नज़र रखें",
    },
    charts: {
      warehouseStock: {
        title: "वेयरहाउस स्टॉक वितरण",
        subtitle: "प्रति वेयरहाउस स्टॉक मात्रा बनाम लंबित ऑर्डर",
        summary: "वेयरहाउस सारांश",
        detailsTitle: "वेयरहाउस विवरण",
        detailsSubtitle: "वेयरहाउस के अनुसार स्टॉक, लंबित ऑर्डर और अलर्ट",
        warehousesCount: "{count} वेयरहाउस",
        stock: "स्टॉक",
        pending: "लंबित",
        available: "उपलब्ध",
        lowStockLabel: "कम स्टॉक",
        outOfStockLabel: "स्टॉक समाप्त",
        headers: {
          warehouse: "वेयरहाउस",
          products: "प्रोडक्ट",
          stock: "कुल स्टॉक",
          pending: "लंबित ऑर्डर",
          available: "उपलब्ध",
          lowStock: "कम स्टॉक",
          outOfStock: "स्टॉक समाप्त",
        },
      },
      lowStock: {
        title: "कम स्टॉक अलर्ट",
        subtitle: "न्यूनतम स्टॉक स्तर से नीचे के प्रोडक्ट",
        itemsCount: "{count} आइटम",
        headers: {
          product: "प्रोडक्ट",
          category: "कैटेगरी",
          warehouse: "वेयरहाउस",
          stock: "वर्तमान स्टॉक",
          minimum: "न्यूनतम स्टॉक",
          shortage: "कमी",
          status: "स्थिति",
        },
      },
    },
  },
  customerAnalytics: {
    page: {
      title: "ग्राहक एनालिटिक्स",
      subtitle: "ग्राहक वृद्धि और शीर्ष प्रदर्शनकर्ताओं को ट्रैक करें",
    },
    filters: {
      period: "अवधि",
      periods: {
        today: "आज",
        yesterday: "कल",
        last_7_days: "पिछले 7 दिन",
        last_30_days: "पिछले 30 दिन",
        last_90_days: "पिछले 90 दिन",
        this_month: "इस महीने",
        last_month: "पिछले महीने",
        this_year: "इस साल",
        custom: "कस्टम रेंज",
      },
      apply: "फ़िल्टर लागू करें",
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
    },
    overview: {
      newCustomers: "नए ग्राहक",
      returningCustomers: "लौटने वाले ग्राहक",
      activeCustomers: "कुल सक्रिय",
      newWeightTons: "नए ग्राहक वज़न (टन)",
      returningWeightTons: "लौटने वाला वज़न (टन)",
    },
    tables: {
      topCustomers: {
        title: "टॉप ग्राहक",
        headers: {
          customer: "ग्राहक",
          orders: "ऑर्डर",
          weightTons: "वज़न (टन)",
          lastOrder: "अंतिम ऑर्डर",
        },
      },
    },
  },
  inquiryAnalytics: {
    page: {
      title: "पूछताछ एनालिटिक्स",
      subtitle: "पूछताछ और रूपांतरण ट्रैक करें",
    },
    filters: {
      period: "अवधि",
      periods: {
        today: "आज",
        yesterday: "कल",
        last_7_days: "पिछले 7 दिन",
        last_30_days: "पिछले 30 दिन",
        last_90_days: "पिछले 90 दिन",
        this_month: "इस महीने",
        last_month: "पिछले महीने",
        this_year: "इस साल",
        custom: "कस्टम रेंज",
      },
      apply: "फ़िल्टर लागू करें",
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
    },
    overview: {
      totalInquiries: "कुल पूछताछ",
      pendingInquiries: "लंबित",
      convertedToOrders: "ऑर्डर में परिवर्तित",
      totalQuotedQuantity: "कुल कोट की गई मात्रा",
      conversionRate: "रूपांतरण दर",
    },
    charts: {
      trend: {
        title: "पूछताछ ट्रेंड",
        seriesInquiries: "पूछताछ",
        seriesQuantity: "कोट की गई मात्रा",
      },
    },
  },
  financialAnalytics: {
    page: {
      title: "वित्तीय एनालिटिक्स",
      subtitle: "टैक्स और राजस्व अवलोकन",
    },
    filters: {
      period: "अवधि",
      periods: {
        today: "आज",
        yesterday: "कल",
        last_7_days: "पिछले 7 दिन",
        last_30_days: "पिछले 30 दिन",
        last_90_days: "पिछले 90 दिन",
        this_month: "इस महीने",
        last_month: "पिछले महीने",
        this_year: "इस साल",
        custom: "कस्टम रेंज",
      },
      apply: "फ़िल्टर लागू करें",
      fromDate: "आरंभ तारीख",
      toDate: "अंतिम तारीख",
    },
    gst: {
      title: "जीएसटी सारांश",
      totalTaxable: "कर योग्य मूल्य",
      totalGst: "कुल जीएसटी",
      cgst: "सीजीएसटी",
      sgst: "एसजीएसटी",
      igst: "आईजीएसटी",
    },
    tcs: {
      title: "टीसीएस सारांश",
      totalOrdersWithTcs: "टीसीएस वाले ऑर्डर",
      totalTcsCollected: "टीसीएस संग्रहित",
    },
    revenue: {
      title: "राजस्व तुलना",
      weightChange: "वज़न परिवर्तन",
      ordersChange: "ऑर्डर परिवर्तन",
      currentPeriod: "वर्तमान अवधि",
      previousPeriod: "पिछली अवधि",
    },
  },
  access: {
    authenticationRequired: {
      title: "प्रमाणीकरण आवश्यक",
      message: "इस पेज तक पहुंचने के लिए कृपया लॉग इन करें।",
    },
    accessDenied: {
      title: "एक्सेस अस्वीकृत",
      message: "आपके पास इस पेज तक पहुंचने की अनुमति नहीं है।",
    },
  },
  taxes: {
    actions: {
      searchPlaceholder: "टैक्स खोजें...",
      alerts: {
        download: "डाउनलोड",
        noDataToExport: "एक्सपोर्ट करने के लिए कोई टैक्स उपलब्ध नहीं है",
      },
      downloadPrefix: "taxes_export_",
      buttons: {
        add: "टैक्स जोड़ें",
      },
    },
    page: {
      header: {
        title: "टैक्स स्लैब",
        subtitle: "जीएसटी, सीजीएसटी, एसजीएसटी और आईजीएसटी दरें प्रबंधित करें",
      },
      stats: {
        total: "कुल टैक्स दरें",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "त्रुटि",
      },
      accessDenied: {
        title: "एक्सेस अस्वीकृत",
        message: "इस पेज को देखने के लिए आपको सुपर एडमिन होना आवश्यक है।",
      },
    },
    table: {
      columns: {
        select: "चुनें",
        gst: "जीएसटी (%)",
        cgst: "सीजीएसटी (%)",
        sgst: "एसजीएसटी (%)",
        igst: "आईजीएसटी (%)",
        created: "निर्मित",
        actions: "कार्रवाइयाँ",
      },
      emptyState: {
        title: "कोई टैक्स दर नहीं मिली",
        message: "अपनी पहली टैक्स दर कॉन्फ़िगरेशन जोड़कर शुरू करें।",
      },
      aria: {
        selectAll: "सभी टैक्स दरें चुनें",
        selectTax: "टैक्स दर चुनें",
        viewTax: "टैक्स दर देखें",
        editTax: "टैक्स दर संपादित करें",
        deleteTax: "टैक्स दर हटाएं",
      },
    },
    forms: {
      title: {
        add: "नई टैक्स दर जोड़ें",
        edit: "टैक्स दर संपादित करें",
      },
      fields: {
        gst: {
          label: "जीएसटी दर (%)",
          placeholder: "जीएसटी दर दर्ज करें",
          errors: {
            required: "जीएसटी दर आवश्यक है",
            invalid: "कृपया एक मान्य जीएसटी दर दर्ज करें (0-100)",
          },
        },
        cgst: {
          label: "सीजीएसटी दर (%)",
          placeholder: "सीजीएसटी दर दर्ज करें",
          errors: {
            required: "सीजीएसटी दर आवश्यक है",
            invalid: "कृपया एक मान्य सीजीएसटी दर दर्ज करें (0-100)",
          },
        },
        sgst: {
          label: "एसजीएसटी दर (%)",
          placeholder: "एसजीएसटी दर दर्ज करें",
          errors: {
            required: "एसजीएसटी दर आवश्यक है",
            invalid: "कृपया एक मान्य एसजीएसटी दर दर्ज करें (0-100)",
          },
        },
        igst: {
          label: "आईजीएसटी दर (%)",
          placeholder: "आईजीएसटी दर दर्ज करें",
          errors: {
            required: "आईजीएसटी दर आवश्यक है",
            invalid: "कृपया एक मान्य आईजीएसटी दर दर्ज करें (0-100)",
          },
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: {
          add: "टैक्स दर जोड़ें",
          edit: "टैक्स दर अपडेट करें",
          loading: {
            add: "बनाया जा रहा है...",
            edit: "अपडेट हो रहा है...",
          },
        },
      },
    },
    confirm: {
      deleteSingle: {
        title: "टैक्स दर हटाएं",
        message:
          "क्या आप वाकई इस टैक्स दर को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      deleteBulk: {
        title: "चयनित टैक्स दरें हटाएं",
        messageSingle:
          "क्या आप वाकई चयनित टैक्स दर को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        messageMultiple:
          "क्या आप वाकई {count} चयनित टैक्स दरें हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      buttons: {
        cancel: "रद्द करें",
      },
    },
  },

  productSizes: {
    notifications: {
      created: 'प्रोडक्ट "{name}" सफलतापूर्वक बनाया गया',
      updated: 'प्रोडक्ट "{name}" सफलतापूर्वक अपडेट किया गया',
      deleted: 'प्रोडक्ट "{name}" सफलतापूर्वक हटाया गया',
      stockUpdated: '"{name}" के लिए स्टॉक अपडेट किया गया',
      imported: "{count} प्रोडक्ट सफलतापूर्वक इम्पोर्ट किए गए",
      bulkDeletedSingle: "1 प्रोडक्ट सफलतापूर्वक हटाया गया",
      bulkDeletedMultiple: "{count} प्रोडक्ट सफलतापूर्वक हटाए गए",
      bulkDeletedList: "{names} सफलतापूर्वक हटाए गए",
      bulkDeletedListSingle: "{name} सफलतापूर्वक हटाया गया",
      bulkDeletedListMultiple: "{names} सफलतापूर्वक हटाए गए",
    },
    page: {
      header: {
        title: "प्रोडक्ट",
        subtitle: "प्रोडक्ट, इन्वेंटरी और विशिष्टताएं प्रबंधित करें",
      },
      stats: {
        total: "कुल प्रोडक्ट",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "प्रोडक्ट त्रुटि",
      },
    },
    actions: {
      searchPlaceholder: "प्रोडक्ट खोजें...",
      alerts: {
        noDataToExport: "एक्सपोर्ट करने के लिए कोई प्रोडक्ट उपलब्ध नहीं है",
      },
      downloadPrefix: "product_sizes_export_",
      buttons: {
        add: "प्रोडक्ट जोड़ें",
        bulkDelete: "चयनित हटाएं",
        refresh: "रिफ्रेश",
        import: "इम्पोर्ट",
      },
    },
    confirm: {
      deleteSingle: {
        title: "प्रोडक्ट हटाएं",
        message:
          "क्या आप वाकई {name} को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      deleteBulk: {
        title: "प्रोडक्ट हटाएं",
        messageSingle:
          "क्या आप वाकई चयनित प्रोडक्ट को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        messageMultiple:
          "क्या आप वाकई {count} चयनित प्रोडक्ट को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      buttons: {
        cancel: "रद्द करें",
      },
      errorTitle: "त्रुटि",
    },
    errors: {
      noSelection: "हटाने के लिए कोई प्रोडक्ट चयनित नहीं है",
    },
    table: {
      columns: {
        select: "चुनें",
        aliasName: "उपनाम",
        productSize: "प्रोडक्ट",
        weight: "वज़न",
        standardLength: "मानक लंबाई",
        thickness: "मोटाई",
        category: "कैटेगरी",
        productName: "प्रोडक्ट नाम",
        hsnCode: "एचएसएन कोड",
        basePrice: "मूल कीमत",
        currentStock: "वर्तमान स्टॉक",
        minimumStock: "वैकल्पिक री-ऑर्डर स्तर, पूरे किलो में, कभी ऋणात्मक नहीं। केवल यह भरी पंक्ति और कुछ नहीं बदलती — मौजूदा स्टॉक और कॉइल्स वैसे ही रहते हैं।",
        stockStatus: "स्टॉक स्थिति",
        actions: "कार्रवाइयाँ",
      },
      copy: {
        unnamedProductSize: "अनाम प्रोडक्ट",
        noProductSize: "लागू नहीं",
        noThickness: "लागू नहीं",
        noWeight: "लागू नहीं",
        noCategory: "लागू नहीं",
        noHsnCode: "लागू नहीं",
        noPrice: "₹0.00",
        noProductName: "लागू नहीं",
        noStock: "0",
        noMinStock: "0",
        // Stock status labels
        statuses: {
          in_stock: "स्टॉक में",
          low_stock: "कम स्टॉक",
          out_of_stock: "स्टॉक समाप्त",
        },
      },
      emptyState: {
        title: "कोई प्रोडक्ट नहीं मिला",
        message: "दिखाने के लिए कोई प्रोडक्ट नहीं है।",
      },
      aria: {
        selectAll: "सभी प्रोडक्ट चुनें",
        selectProductSize: "प्रोडक्ट चुनें",
        viewProductSize: "प्रोडक्ट देखें",
        editProductSize: "प्रोडक्ट संपादित करें",
        deleteProductSize: "प्रोडक्ट हटाएं",
      },
    },
    forms: {
      title: {
        add: "प्रोडक्ट जोड़ें",
        edit: "प्रोडक्ट संपादित करें",
      },
      actions: {
        searchPlaceholder: "प्रोडक्ट खोजें...",
        buttons: {
          add: "प्रोडक्ट जोड़ें",
          bulkDelete: "चयनित हटाएं",
          refresh: "रिफ्रेश",
        },
      },
      fields: {
        alias_name: {
          label: "उपनाम",
          placeholder: "प्रोडक्ट उपनाम दर्ज करें",
          errors: {
            required: "उपनाम आवश्यक है",
            minLength: "उपनाम कम से कम 2 अक्षरों का होना चाहिए",
          },
        },
        product_size: {
          label: "प्रोडक्ट",
          placeholder: "प्रोडक्ट दर्ज करें (उदा., 4x8 फीट)",
          errors: {
            required: "प्रोडक्ट आवश्यक है",
          },
        },
        product_thickness: {
          label: "प्रोडक्ट मोटाई",
          placeholder: "मोटाई दर्ज करें (उदा., 2)",
          errors: {
            maxLength: "मोटाई 50 अक्षरों से अधिक नहीं हो सकती",
          },
        },
        thickness_unit: {
          label: "मोटाई इकाई",
          placeholder: "उदा., mm, cm, inch",
        },
        price: {
          label: "कीमत (₹)",
          placeholder: "वैकल्पिक — कीमत न हो तो खाली छोड़ें",
        },
        product_weight: {
          label: "वज़न (kg)",
          placeholder: "वज़न kg में दर्ज करें",
          errors: {
            invalid: "कृपया एक मान्य वज़न दर्ज करें",
          },
        },
        standard_length: {
          label: "मानक लंबाई (m)",
          placeholder: "लंबाई मीटर में दर्ज करें",
          errors: {
            invalid: "कृपया एक मान्य लंबाई दर्ज करें",
          },
        },
        category_id: {
          label: "कैटेगरी",
          placeholder: "एक कैटेगरी चुनें",
          errors: {
            required: "कैटेगरी आवश्यक है",
          },
        },
        product_catalogue_id: {
          label: "सब प्रोडक्ट",
          placeholder: "एक सब प्रोडक्ट चुनें",
          errors: {
            required: "सब प्रोडक्ट आवश्यक है",
          },
        },
        hsn_code_id: {
          label: "एचएसएन कोड",
          placeholder: "एक एचएसएन कोड चुनें",
          errors: {
            required: "एचएसएन कोड आवश्यक है",
          },
        },
        base_price: {
          label: "मूल कीमत",
          placeholder: "उदा., 1500.00",
          errors: {
            required: "मूल कीमत आवश्यक है",
            invalid: "कृपया एक मान्य कीमत दर्ज करें",
          },
        },

        current_stock: {
          label: "वर्तमान स्टॉक",
          placeholder: "0",
          errors: {
            invalid: "कृपया एक मान्य स्टॉक मात्रा दर्ज करें",
          },
        },
        minimum_stock: {
          label: "न्यूनतम स्टॉक",
          placeholder: "0",
          errors: {
            invalid: "कृपया एक मान्य न्यूनतम स्टॉक दर्ज करें",
          },
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: {
          add: "प्रोडक्ट बनाएं",
          edit: "प्रोडक्ट अपडेट करें",
          loading: {
            add: "बनाया जा रहा है...",
            edit: "अपडेट हो रहा है...",
          },
        },
      },
    },
    view: {
      header: {
        sectionTitle: "प्रोडक्ट विवरण",
        fallbackTitle: "प्रोडक्ट",
      },
      loading: "प्रोडक्ट विवरण लोड हो रहा है...",
      empty: {
        title: "प्रोडक्ट डेटा अनुपलब्ध",
        message: "हम चयनित प्रोडक्ट लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
      },
      fields: {
        aliasName: "उपनाम",
        productSize: "प्रोडक्ट",
        productName: "प्रोडक्ट नाम",
        thickness: "मोटाई",
        productWeight: "वज़न",
        standardLength: "मानक लंबाई",
        basePrice: "मूल कीमत",
        category: "कैटेगरी",
        hsnCode: "एचएसएन कोड",
        currentStock: "वर्तमान स्टॉक",
        minimumStock: "न्यूनतम स्टॉक",
        stockStatus: "स्टॉक स्थिति",
      },
      buttons: {
        close: "बंद करें",
      },
    },
    import: {
      config: {
        templateHeaders: [
          "alias_name",
          "product_size",
          "product_weight",
          "standard_length",
          "product_thickness",
          "current_stock",
          "minimum_stock",
          "product_category_name",
          "product_catalogue_name",
        ],
        templateFilename: "product_sizes_import_template",
        inputId: "product-sizes-import-input",
      },
    },
    distribution: {
      header: {
        sectionTitle: "प्रोडक्ट वितरण",
      },
      loading: "वितरण डेटा लोड हो रहा है...",
      error: {
        title: "वितरण डेटा लोड करने में विफल",
      },
      empty: {
        title: "कोई वितरण डेटा उपलब्ध नहीं है",
        message: "यह प्रोडक्ट वर्तमान में किसी भी वेयरहाउस में स्टॉक में नहीं है।",
      },
      fields: {
        productName: "प्रोडक्ट नाम",
        productSize: "प्रोडक्ट",
        warehouseDistribution: "वेयरहाउस वितरण",
        warehouseId: "वेयरहाउस आईडी",
        stockQuantity: "स्टॉक मात्रा",
        totalStock: "कुल स्टॉक",
        acrossWarehouses: "{count} वेयरहाउस में",
        totalUnits: "कुल इकाइयाँ",
      },
      buttons: {
        close: "बंद करें",
      },
      actions: {
        view: "वितरण देखें",
        tooltip: "इस प्रोडक्ट के लिए वेयरहाउस वितरण देखें",
      },
    },
  },
  ipAddresses: {
    actions: {
      searchPlaceholder: "IP पते खोजें...",
      statusOptions: {
        all: "सभी स्थिति",
        active: "केवल सक्रिय",
        inactive: "केवल निष्क्रिय",
      },
      alerts: {
        noDataToExport: "एक्सपोर्ट करने के लिए कोई IP पता उपलब्ध नहीं है",
      },
      downloadPrefix: "ip_addresses_export_",
      buttons: {
        bulkAdd: "बल्क जोड़ें",
        add: "IP पता जोड़ें",
      },
    },
    page: {
      header: {
        title: "IP पता प्रबंधन",
        subtitle: "अपने नेटवर्क में IP पतों को प्रबंधित और मॉनिटर करें",
      },
      stats: {
        total: "कुल IP",
        active: "सक्रिय",
        inactive: "निष्क्रिय",
      },
      alerts: {
        errorTitle: "त्रुटि",
        dismissAria: "त्रुटि बंद करें",
      },
    },
    confirm: {
      delete: {
        title: "IP पता हटाएं",
        message:
          "क्या आप वाकई {ip} को स्थायी रूप से हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
      },
      deactivate: {
        title: "IP पता निष्क्रिय करें",
        message:
          "क्या आप वाकई {ip} को निष्क्रिय करना चाहते हैं? पुनः सक्रिय होने तक यूज़र इस IP का उपयोग नहीं कर सकेंगे।",
        confirmLabel: "निष्क्रिय करें",
      },
      buttons: {
        cancel: "रद्द करें",
      },
    },
    export: {
      headers: {
        ipAddress: "ip_address",
        description: "description",
        isActive: "is_active",
        createdAt: "created_at",
      },
      values: {
        active: "सक्रिय",
        inactive: "निष्क्रिय",
        notAvailable: "लागू नहीं",
      },
    },
    table: {
      columns: {
        ipAddress: "IP पता",
        description: "विवरण",
        status: "स्थिति",
        actions: "कार्रवाइयाँ",
      },
      copy: {
        noDescription: "कोई विवरण नहीं",
      },
      statuses: {
        active: "सक्रिय",
        inactive: "निष्क्रिय",
      },
      emptyState: {
        defaultTitle: "कोई IP पता नहीं मिला",
        defaultMessage: "सिस्टम में अपना पहला IP पता जोड़कर शुरू करें।",
        filteredTitle: "कोई IP पता आपके फ़िल्टर से मेल नहीं खाता",
        filteredMessage: "अपने खोज मापदंड या फ़िल्टर समायोजित करने का प्रयास करें।",
        clearFilters: "फ़िल्टर साफ़ करें",
      },
      tooltips: {
        view: "IP विवरण देखें",
        edit: "IP पता संपादित करें",
        activate: "IP सक्रिय करें",
        deactivate: "IP निष्क्रिय करें",
        delete: "IP पता हटाएं",
      },
    },
    forms: {
      single: {
        title: {
          add: "नया IP पता जोड़ें",
          edit: "IP पता संपादित करें",
        },
        subtitle: {
          add: "नए IP पते के लिए विवरण दर्ज करें।",
          edit: "नीचे IP पते की जानकारी अपडेट करें।",
        },
        fields: {
          ipAddress: {
            label: "IP पता",
            placeholder: "उदा., 192.168.1.105",
            errors: {
              required: "IP पता आवश्यक है",
              invalid: "कृपया एक मान्य IP पता दर्ज करें (उदा., 192.168.1.1)",
            },
          },
          description: {
            label: "विवरण",
            placeholder: "उदा., ऑफिस कंप्यूटर 3",
            errors: {
              required: "विवरण आवश्यक है",
              minLength: "विवरण कम से कम 5 अक्षरों का होना चाहिए",
            },
          },
          isActive: {
            label: "सक्रिय",
          },
        },
        buttons: {
          cancel: "रद्द करें",
          submit: {
            add: "IP पता जोड़ें",
            edit: "IP पता अपडेट करें",
            loading: {
              add: "बनाया जा रहा है...",
              edit: "अपडेट हो रहा है...",
            },
          },
        },
      },
      bulk: {
        title: "एकाधिक IP पते जोड़ें",
        subtitle: "प्रति पंक्ति एक IP दर्ज करें, या कॉमा से अलग करें।",
        fields: {
          ipAddresses: {
            label: "IP पते",
            placeholder:
              "192.168.1.106\n192.168.1.107\n10.0.0.55\n\nया कॉमा से अलग करें: 192.168.1.1, 192.168.1.2",
            actions: {
              pasteExample: "उदाहरण पेस्ट करें",
            },
            errors: {
              required: "कम से कम एक IP पता आवश्यक है",
              noneValid: "कृपया मान्य IP पते दर्ज करें",
              invalidList: "अमान्य IP पते: {ips}",
              limitExceeded: "एक बार में अधिकतम {max} IP पते अनुमत हैं",
            },
          },
          description: {
            label: "विवरण",
            placeholder: "उदा., बल्क में जोड़े गए ऑफिस कंप्यूटर",
            helper:
              "यह विवरण इस बैच के सभी IP पतों पर लागू किया जाएगा।",
            errors: {
              required: "बल्क इम्पोर्ट के लिए विवरण आवश्यक है",
              minLength: "विवरण कम से कम 5 अक्षरों का होना चाहिए",
            },
          },
        },
        preview: {
          title: "पूर्वावलोकन ({count} IP पते)",
          truncated: "पहले {count} दिखाए जा रहे हैं",
          more: "+{count} और",
        },
        buttons: {
          cancel: "रद्द करें",
          submit: "{count} IP पते जोड़ें",
          submitFallback: "IP पते जोड़ें",
          loading: "जोड़ा जा रहा है...",
        },
        examples: {
          ipList: "192.168.1.106\n192.168.1.107\n10.0.0.55",
        },
      },
    },
    view: {
      header: {
        sectionTitle: "IP पता विवरण",
        fallbackTitle: "IP रिकॉर्ड",
      },
      loading: "IP विवरण लोड हो रहा है...",
      empty: {
        title: "IP डेटा अनुपलब्ध",
        message: "हम चयनित IP पता लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
      },
      fields: {
        ipAddress: "IP पता",
        description: "विवरण",
        status: "स्थिति",
        recordId: "रिकॉर्ड आईडी",
        createdAt: "निर्मित तिथि",
        updatedAt: "अंतिम अपडेट",
      },
      buttons: {
        close: "बंद करें",
      },
    },
  },
  users: {
    actions: {
      searchPlaceholder: "यूज़र खोजें...",
      alerts: {
        noDataToExport: "एक्सपोर्ट करने के लिए कोई यूज़र उपलब्ध नहीं",
      },
      downloadPrefix: "users_export_",
      buttons: {
        add: "यूज़र जोड़ें",
      },
    },
    page: {
      header: {
        title: "यूज़र",
        subtitle: "सिस्टम यूज़र और उनकी अनुमतियाँ प्रबंधित करें",
      },
      stats: {
        total: "कुल यूज़र",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "त्रुटि",
      },
      accessDenied: {
        title: "पहुँच अस्वीकृत",
        message: "इस पृष्ठ को देखने के लिए आपका सुपर एडमिन होना आवश्यक है।",
      },
    },
    confirm: {
      deleteSingle: {
        title: "यूज़र हटाएं",
        message:
          "क्या आप वाकई {name} को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      deleteBulk: {
        title: "चयनित यूज़र हटाएं",
        messageSingle:
          "क्या आप वाकई चयनित यूज़र को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
        messageMultiple:
          "क्या आप वाकई {count} चयनित यूज़र हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      buttons: {
        cancel: "रद्द करें",
      },
    },
    table: {
      columns: {
        select: "चुनें",
        name: "नाम",
        email: "ईमेल",
        phone: "फ़ोन",
        mobile: "मोबाइल नंबर",
        role: "भूमिका",
        status: "स्थिति",
        created: "बनाया गया",
        actions: "क्रियाएं",
        warehouses: "वेयरहाउस",
      },
      copy: {
        unnamedUser: "अनाम यूज़र",
        noEmail: "कोई ईमेल नहीं",
        noPhone: "उपलब्ध नहीं",
        noMobile: "उपलब्ध नहीं",
        initialFallback: "U",
      },
      statuses: {
        active: "सक्रिय",
        inactive: "निष्क्रिय",
      },
      emptyState: {
        title: "कोई यूज़र नहीं मिला",
        message: "दिखाने के लिए कोई यूज़र नहीं है।",
      },
      aria: {
        selectAll: "सभी यूज़र चुनें",
        selectUser: "यूज़र चुनें",
        viewUser: "यूज़र देखें",
        editUser: "यूज़र संपादित करें",
        deleteUser: "यूज़र हटाएं",
        activateUser: "यूज़र सक्रिय करें",
      },
    },
    forms: {
      title: {
        add: "नया यूज़र जोड़ें",
        edit: "यूज़र संपादित करें",
      },
      userTypes: [
        { value: "SUPER_ADMIN", label: "सुपर एडमिन" },
        { value: "ADMIN", label: "एडमिन" },
        { value: "OPERATIONS_MANAGER", label: "ऑपरेशंस मैनेजर" },
        { value: "SALES_STAFF", label: "सेल्स एक्ज़िक्यूटिव" },
        { value: "DELIVERY_SUPERVISOR", label: "डिलीवरी सुपरवाइज़र" },
        { value: "ACCOUNT_USER", label: "अकाउंट एक्ज़िक्यूटिव" },
        { value: "LABOUR", label: "श्रमिक" },
      ],
      fields: {
        firstName: {
          label: "पहला नाम",
          placeholder: "पहला नाम दर्ज करें",
          errors: {
            required: "पहला नाम आवश्यक है",
          },
        },
        lastName: {
          label: "अंतिम नाम",
          placeholder: "अंतिम नाम दर्ज करें",
          errors: {
            required: "अंतिम नाम आवश्यक है",
          },
        },
        email: {
          label: "ईमेल पता",
          placeholder: "ईमेल पता दर्ज करें",
          errors: {
            required: "ईमेल आवश्यक है",
            invalid: "कृपया एक मान्य ईमेल पता दर्ज करें",
          },
        },
        phoneNumber: {
          label: "फ़ोन नंबर",
          placeholder: "फ़ोन नंबर दर्ज करें",
          errors: {
            invalid: "कृपया एक मान्य फ़ोन नंबर दर्ज करें (10 अंक)",
          },
        },
        mobileNumber: {
          label: "मोबाइल नंबर",
          placeholder: "मोबाइल नंबर दर्ज करें",
          errors: {
            invalid: "कृपया एक मान्य मोबाइल नंबर दर्ज करें",
          },
        },
        warehouses: {
          label: "वेयरहाउस",
          placeholder: "जोड़ने के लिए वेयरहाउस चुनें...",
          empty: "कोई वेयरहाउस असाइन नहीं",
          helper: "सूची में जोड़ने के लिए ड्रॉपडाउन से एक वेयरहाउस चुनें।",
        },
        defaultWarehouse: {
          label: "डिफ़ॉल्ट वेयरहाउस",
          placeholder: "डिफ़ॉल्ट वेयरहाउस चुनें...",
          helper: "ऑर्डर को डिफ़ॉल्ट रूप से असाइन किया गया वेयरहाउस।",
          errors: {
            required: "डिफ़ॉल्ट वेयरहाउस आवश्यक है",
          },
        },
        userType: {
          label: "यूज़र प्रकार",
          errors: {
            required: "यूज़र प्रकार आवश्यक है",
          },
        },
        language: {
          label: "भाषा",
          english: "English",
          hindi: "हिंदी",
        },
        password: {
          label: "पासवर्ड",
          hints: {
            keepCurrent: "(वर्तमान रखने के लिए खाली छोड़ें)",
          },
          placeholders: {
            add: "पासवर्ड दर्ज करें",
            edit: "वर्तमान पासवर्ड रखने के लिए खाली छोड़ें",
          },
          aria: {
            show: "पासवर्ड दिखाएं",
            hide: "पासवर्ड छिपाएं",
          },
          errors: {
            required: "पासवर्ड आवश्यक है",
            policy:
              "पासवर्ड में कम से कम 8 अक्षर होने चाहिए और इसमें अपरकेस, लोअरकेस, एक अंक और एक चिह्न शामिल होना चाहिए",
          },
        },
        confirmPassword: {
          label: "पासवर्ड की पुष्टि करें",
          placeholders: {
            add: "पासवर्ड की पुष्टि करें",
            edit: "नए पासवर्ड की पुष्टि करें",
          },
          aria: {
            show: "पुष्टि पासवर्ड दिखाएं",
            hide: "पुष्टि पासवर्ड छिपाएं",
          },
          errors: {
            required: "कृपया पासवर्ड की पुष्टि करें",
            mismatch: "पासवर्ड मेल नहीं खाते",
          },
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: {
          add: "यूज़र बनाएं",
          edit: "यूज़र अपडेट करें",
          loading: {
            add: "बनाया जा रहा है...",
            edit: "अपडेट हो रहा है...",
          },
        },
      },
    },
    view: {
      header: {
        sectionTitle: "यूज़र विवरण",
        fallbackTitle: "यूज़र प्रोफ़ाइल",
      },
      loading: "यूज़र विवरण लोड हो रहा है...",
      empty: {
        title: "यूज़र डेटा अनुपलब्ध",
        message: "हम चयनित यूज़र को लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
      },
      fields: {
        firstName: "पहला नाम",
        lastName: "अंतिम नाम",
        email: "ईमेल",
        role: "भूमिका",
        phoneNumber: "फ़ोन नंबर",
        mobileNumber: "मोबाइल नंबर",
        status: "स्थिति",
        userId: "यूज़र आईडी",
        createdAt: "बनाया गया",
      },
      buttons: {
        close: "बंद करें",
      },
    },
    notifications: {
      created: "{name} सफलतापूर्वक बनाया गया है",
      updated: "{name} सफलतापूर्वक अपडेट किया गया है",
      deleted: "{name} सफलतापूर्वक हटाया गया है",
      activated: "{name} सक्रिय कर दिया गया है",
      deactivated: "{name} निष्क्रिय कर दिया गया है",
      bulkDeletedSingle: "1 यूज़र सफलतापूर्वक हटाया गया है",
      bulkDeletedMultiple: "{count} यूज़र सफलतापूर्वक हटाए गए हैं",
    },
    alerts: {
      fetchFailed: "यूज़र लाने में विफल",
      unexpected: "एक अप्रत्याशित त्रुटि हुई",
      createFailed: "यूज़र बनाने में विफल",
      updateFailed: "यूज़र अपडेट करने में विफल",
      deleteFailed: "यूज़र हटाने में विफल",
      exportFailed: "यूज़र एक्सपोर्ट करने में विफल",
      noSelection: "जारी रखने के लिए कम से कम एक यूज़र चुनें",
    },
    warehousesModal: {
      title: "असाइन किए गए वेयरहाउस",
      empty: "इस यूज़र को कोई वेयरहाउस असाइन नहीं है।",
    },
  },
  customers: {
    header: {
      title: "ग्राहक",
      subtitle: "ग्राहक रिकॉर्ड, अकाउंट स्थिति और इम्पोर्ट प्रबंधित करें",
      totalCard: {
        label: "कुल ग्राहक",
      },
      selectedCard: {
        label: "चयनित",
      },
    },
    actions: {
      searchPlaceholder: "ग्राहक खोजें...",
      searchAriaLabel: "ग्राहक खोजें",
      filters: {
        onboarding: {
          label: "ऑनबोर्डिंग",
          options: {
            approved: "केवल स्वीकृत",
            all: "सभी स्थितियाँ",
            invited: "आमंत्रित",
            profileSubmitted: "प्रोफ़ाइल सबमिट",
            rejected: "अस्वीकृत",
          },
        },
        customerType: {
          label: "ग्राहक प्रकार",
          placeholder: "सभी प्रकार",
          options: {
            all: "सभी",
            customer: "ग्राहक",
            supplier: "सप्लायर",
          },
        },
        state: {
          label: "राज्य",
          placeholder: "सभी राज्य",
        },
        city: {
          label: "शहर",
          placeholder: "सभी शहर",
        },
        status: {
          label: "स्थिति",
          options: {
            all: "सभी",
            active: "सक्रिय",
            inactive: "निष्क्रिय",
          },
        },
        reset: "फ़िल्टर साफ़ करें",
      },
      buttons: {
        add: "ग्राहक जोड़ें",
        import: "एक्सेल इम्पोर्ट करें",
        export: "CSV एक्सपोर्ट करें",
        deleteSelected: "चयनित हटाएं",
        refresh: "रिफ्रेश",
      },
      alerts: {
        noSelection: "जारी रखने के लिए कम से कम एक ग्राहक चुनें।",
        noDataToExport: "एक्सपोर्ट करने के लिए कोई ग्राहक उपलब्ध नहीं",
      },
    },
    statuses: {
      active: "सक्रिय",
      inactive: "निष्क्रिय",
    },
    pagination: {
      pageOfTotal: "पृष्ठ {current} / {total} — कुल {count} ग्राहक",
      previous: "पिछला",
      next: "अगला",
    },
    fallbacks: {
      customerName: "ग्राहक",
      errorTitle: "त्रुटि",
    },
    table: {
      columns: {
        select: "चुनें",
        company: "टैली नाम",
        owner: "मालिक",
        contact: "संपर्क व्यक्ति",
        type: "ग्राहक प्रकार",
        location: "लोकेशन",
        status: "स्थिति",
        createdAt: "बनाया गया",
        actions: "क्रियाएं",
      },
      emptyState: {
        defaultTitle: "कोई ग्राहक नहीं मिला",
        defaultMessage: "शुरू करने के लिए अपना पहला ग्राहक जोड़ें।",
        filteredTitle: "आपके फ़िल्टर से मेल खाने वाला कोई ग्राहक नहीं",
        filteredMessage: "अपने फ़िल्टर या खोज कीवर्ड समायोजित करने का प्रयास करें।",
      },
      tooltips: {
        view: "ग्राहक विवरण देखें",
        edit: "ग्राहक संपादित करें",
        activate: "ग्राहक सक्रिय करें",
        deactivate: "ग्राहक निष्क्रिय करें",
        delete: "ग्राहक हटाएं",
      },
    },
    forms: {
      title: {
        add: "नया ग्राहक जोड़ें",
        edit: "ग्राहक संपादित करें",
      },
      subtitle: {
        add: "नया ग्राहक रिकॉर्ड जोड़ने के लिए फ़ॉर्म भरें।",
        edit: "आवश्यकतानुसार ग्राहक जानकारी अपडेट करें।",
      },
      fields: {
        ownerName: {
          label: "मालिक का नाम",
          placeholder: "मालिक का नाम दर्ज करें",
          errors: {
            required: "मालिक का नाम आवश्यक है",
          },
        },
        companyName: {
          label: "कंपनी का नाम",
          placeholder: "कंपनी का नाम दर्ज करें",
          errors: {
            required: "कंपनी का नाम आवश्यक है",
          },
        },
        gstNumber: {
          label: "जीएसटी नंबर",
          placeholder: "जीएसटी नंबर दर्ज करें",
          errors: {
            required: "जीएसटी नंबर आवश्यक है",
          },
        },
        contactPerson: {
          label: "संपर्क व्यक्ति",
          placeholder: "संपर्क व्यक्ति का नाम दर्ज करें",
          errors: {
            required: "संपर्क व्यक्ति का नाम आवश्यक है",
          },
        },
        customerType: {
          label: "ग्राहक प्रकार",
          placeholder: "ग्राहक चुनें",
          errors: {
            required: "ग्राहक प्रकार आवश्यक है",
          },
          options: {
            select: "ग्राहक चुनें",
            customer: "ग्राहक",
            supplier: "सप्लायर",
          },
        },
        address1: {
          label: "पता पंक्ति 1",
          placeholder: "प्राथमिक पता दर्ज करें",
          errors: {
            required: "पता पंक्ति 1 आवश्यक है",
          },
        },
        address2: {
          label: "पता पंक्ति 2",
          placeholder: "अतिरिक्त पता जानकारी दर्ज करें",
        },
        state: {
          label: "राज्य",
          placeholder: "राज्य दर्ज करें",
          errors: {
            required: "राज्य आवश्यक है",
          },
        },
        city: {
          label: "शहर",
          placeholder: "शहर दर्ज करें",
          errors: {
            required: "शहर आवश्यक है",
          },
        },
        country: {
          label: "देश",
          placeholder: "देश दर्ज करें",
          errors: {
            required: "देश आवश्यक है",
          },
        },
        zipCode: {
          label: "ज़िप कोड",
          placeholder: "ज़िप या पोस्टल कोड दर्ज करें",
          errors: {
            required: "ज़िप कोड आवश्यक है",
            invalid: "एक मान्य ज़िप या पोस्टल कोड दर्ज करें",
          },
        },
        deliveryLocation: {
          label: "डिलीवरी लोकेशन",
          placeholder: "डिलीवरी लोकेशन टिप्पणी दर्ज करें",
        },
        email: {
          label: "ईमेल",
          placeholder: "ईमेल पता दर्ज करें",
          errors: {
            invalid: "एक मान्य ईमेल पता दर्ज करें",
          },
        },
        mobileNumber: {
          label: "मोबाइल नंबर",
          placeholder: "मोबाइल नंबर दर्ज करें",
          errors: {
            invalid: "एक मान्य मोबाइल नंबर दर्ज करें",
          },
        },
        phoneNumber1: {
          label: "प्राथमिक फ़ोन नंबर",
          placeholder: "प्राथमिक फ़ोन नंबर दर्ज करें",
          errors: {
            invalid: "एक मान्य 10-अंकीय फ़ोन नंबर दर्ज करें",
          },
        },
        phoneNumber2: {
          label: "द्वितीयक फ़ोन नंबर",
          placeholder: "द्वितीयक फ़ोन नंबर दर्ज करें",
          errors: {
            invalid: "एक मान्य 10-अंकीय फ़ोन नंबर दर्ज करें",
          },
        },
        tallyName: {
          label: "टैली नाम",
          placeholder: "टैली अकाउंट नाम दर्ज करें",
          errors: {
            required: "टैली नाम आवश्यक है",
          },
        },
        sisterCompany: {
          label: "सिस्टर कंपनी",
          placeholder: "क्या यह एक सिस्टर कंपनी है?",
        },
        creditPeriod: {
          label: "क्रेडिट अवधि (दिन)",
          placeholder: "दिनों में क्रेडिट अवधि दर्ज करें",
          errors: {
            required: "क्रेडिट अवधि आवश्यक है",
          },
        },
        isActive: {
          label: "सक्रिय",
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: {
          add: "ग्राहक बनाएं",
          edit: "ग्राहक अपडेट करें",
          loading: {
            add: "बनाया जा रहा है...",
            edit: "अपडेट हो रहा है...",
          },
        },
      },
    },
    details: {
      header: {
        sectionTitle: "ग्राहक विवरण",
        fallbackTitle: "ग्राहक रिकॉर्ड",
      },
      loading: "ग्राहक विवरण लोड हो रहा है...",
      empty: {
        title: "ग्राहक डेटा अनुपलब्ध",
        message: "हम चयनित ग्राहक को लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
      },
      fields: {
        ownerName: "मालिक का नाम",
        companyName: "कंपनी का नाम",
        gstNumber: "जीएसटी नंबर",
        contactPerson: "संपर्क व्यक्ति",
        customerType: "ग्राहक प्रकार",
        address1: "पता पंक्ति 1",
        address2: "पता पंक्ति 2",
        state: "राज्य",
        city: "शहर",
        country: "देश",
        zipCode: "ज़िप कोड",
        deliveryLocation: "डिलीवरी लोकेशन",
        email: "ईमेल",
        phoneNumber1: "प्राथमिक फ़ोन",
        phoneNumber2: "द्वितीयक फ़ोन",
        tallyName: "टैली नाम",
        creditPeriod: "क्रेडिट अवधि",
        status: "स्थिति",
        createdAt: "बनाया गया",
        updatedAt: "अंतिम बार अपडेट किया गया",
      },
      buttons: {
        close: "बंद करें",
      },
    },
    import: {
      title: "ग्राहक इम्पोर्ट करें",
      subtitle:
        "ग्राहकों को बल्क इम्पोर्ट करने के लिए एक एक्सेल फ़ाइल (.xls या .xlsx) अपलोड करें।",
      description:
        "सुनिश्चित करें कि फ़ाइल कॉलम हेडर के साथ आवश्यक टेम्पलेट से मेल खाती है।",
      uploadLabel: "एक्सेल फ़ाइल अपलोड करें",
      helper: "स्वीकृत प्रारूप: .xls, .xlsx. अधिकतम आकार 5 MB।",
      status: {
        ready: "इम्पोर्ट के लिए तैयार",
        uploading: "फ़ाइल अपलोड हो रही है...",
        success: "इम्पोर्ट सफलतापूर्वक पूर्ण हुआ",
        error: "इम्पोर्ट विफल, कृपया त्रुटियों की समीक्षा करें और पुनः प्रयास करें।",
      },
      errors: {
        noFile: "कृपया इम्पोर्ट करने के लिए एक फ़ाइल चुनें।",
        invalidType: "केवल .xls या .xlsx फ़ाइलें समर्थित हैं।",
        uploadFailed: "ग्राहक इम्पोर्ट करने में विफल। कृपया बाद में पुनः प्रयास करें।",
      },

      buttons: {
        cancel: "रद्द करें",
        import: "ग्राहक इम्पोर्ट करें",
        importing: "इम्पोर्ट हो रहा है...",
        downloadTemplate: "नमूना एक्सेल शीट डाउनलोड करें",
      },
    },
    confirm: {
      deleteSingle: {
        title: "ग्राहक हटाएं",
        message:
          "क्या आप वाकई {name} को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
      },
      deleteMultiple: {
        title: "ग्राहक हटाएं",
        message:
          "क्या आप वाकई {count} चयनित ग्राहक हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
      },
      deactivate: {
        title: "ग्राहक निष्क्रिय करें",
        message: "क्या आप वाकई {name} को निष्क्रिय करना चाहते हैं?",
      },
      activate: {
        title: "ग्राहक सक्रिय करें",
        message: "क्या आप वाकई {name} को सक्रिय करना चाहते हैं?",
      },
      buttons: {
        confirm: "पुष्टि करें",
        delete: "हटाएं",
        activate: "सक्रिय करें",
        deactivate: "निष्क्रिय करें",
        cancel: "रद्द करें",
      },
    },
    notifications: {
      created: "ग्राहक सफलतापूर्वक बनाया गया",
      updated: "ग्राहक सफलतापूर्वक अपडेट किया गया",
      deleted: "ग्राहक सफलतापूर्वक हटाया गया",
      statusUpdated: "ग्राहक स्थिति अपडेट की गई",
      importSuccess: "ग्राहक सफलतापूर्वक इम्पोर्ट किए गए",
      importFailed: "ग्राहक इम्पोर्ट विफल",
    },
    export: {
      filename: "customers_export",
      columns: {
        owner_name: "मालिक का नाम",
        company_name: "कंपनी का नाम",
        gst_number: "जीएसटी नंबर",
        contact_person: "संपर्क व्यक्ति",
        customer_type: "ग्राहक प्रकार",
        state: "राज्य",
        city: "शहर",
        email: "ईमेल",
        phone_number_1: "प्राथमिक फ़ोन",
        phone_number_2: "द्वितीयक फ़ोन",
        tally_name: "टैली नाम",
        credit_period: "क्रेडिट अवधि",
        is_active: "स्थिति",
        created_at: "बनाया गया",
      },
    },
  },
  hsnCodes: {
    page: {
      header: {
        title: "एचएसएन कोड प्रबंधन",
        subtitle:
          "हार्मोनाइज़्ड सिस्टम ऑफ़ नोमेनक्लेचर कोड और उनके टैक्स संबंध प्रबंधित करें",
      },
      stats: {
        total: "कुल एचएसएन कोड",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "त्रुटि",
      },
      accessDenied: {
        title: "पहुँच अस्वीकृत",
        message: "आपके पास एचएसएन कोड प्रबंधन तक पहुँचने की अनुमति नहीं है।",
      },
    },
    actions: {
      searchPlaceholder: "एचएसएन कोड खोजें...",
      buttons: {
        add: "एचएसएन कोड जोड़ें",
        export: "एक्सपोर्ट",
        bulkDelete: "चयनित हटाएं",
        refresh: "रिफ्रेश",
      },
      filters: {
        hsnCode: "एचएसएन कोड",
        chapter: "अध्याय",
        gstRate: "जीएसटी दर",
        minRate: "न्यूनतम दर",
        maxRate: "अधिकतम दर",
      },
    },
    table: {
      columns: {
        hsnCode: "एचएसएन कोड",
        description: "विवरण",
        tax: "टैक्स",
        actions: "क्रियाएं",
      },
      emptyState: {
        title: "कोई एचएसएन कोड नहीं मिला",
        message: "आपके वर्तमान खोज मानदंड से मेल खाने वाला कोई एचएसएन कोड नहीं।",
      },
      aria: {
        viewHsnCode: "एचएसएन कोड विवरण देखें",
        editHsnCode: "एचएसएन कोड संपादित करें",
        deleteHsnCode: "एचएसएन कोड हटाएं",
        selectHsnCode: "एचएसएन कोड चुनें",
        selectAll: "सभी एचएसएन कोड चुनें",
      },
    },
    forms: {
      title: {
        add: "एचएसएन कोड जोड़ें",
        edit: "एचएसएन कोड संपादित करें",
      },
      fields: {
        hsnCode: {
          label: "एचएसएन कोड",
          placeholder: "एचएसएन कोड दर्ज करें (उदा., 8703)",
          errors: {
            required: "एचएसएन कोड आवश्यक है",
            invalid: "एचएसएन कोड बिल्कुल 4, 6, या 8 अंकों का होना चाहिए",
          },
        },
        description: {
          label: "विवरण",
          placeholder: "एचएसएन विवरण दर्ज करें",
          errors: {
            required: "विवरण आवश्यक है",
            minLength: "विवरण कम से कम 5 अक्षरों का होना चाहिए",
            duplicate: "समान विवरण वाला एक अन्य एचएसएन पहले से मौजूद है",
          },
        },
        tax: {
          label: "टैक्स",
          placeholder: "टैक्स कॉन्फ़िगरेशन चुनें",
          errors: {
            required: "टैक्स आईडी आवश्यक है",
            invalid: "कृपया एक मान्य टैक्स कॉन्फ़िगरेशन चुनें",
          },
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: {
          add: "एचएसएन कोड जोड़ें",
          edit: "एचएसएन कोड अपडेट करें",
          loading: {
            add: "जोड़ा जा रहा है...",
            edit: "अपडेट हो रहा है...",
          },
        },
      },
    },
    confirm: {
      deleteSingle: {
        title: "एचएसएन कोड हटाएं",
        message:
          "क्या आप वाकई इस एचएसएन कोड को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      deleteBulk: {
        title: "एचएसएन कोड हटाएं",
        messageSingle: "क्या आप वाकई चयनित एचएसएन कोड को हटाना चाहते हैं?",
        messageMultiple:
          "क्या आप वाकई {count} चयनित एचएसएन कोड हटाना चाहते हैं?",
        confirmLabel: "चयनित हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      buttons: {
        cancel: "रद्द करें",
      },
    },
  },
  navigation: {
    appName: "ऑर्डर मैनेजमेंट सिस्टम",
    pages: {
      dashboard: "डैशबोर्ड",
      paymentsTracking: "भुगतान ट्रैकिंग",
      overview: "अवलोकन",
      sales: "सेल्स",
      delivery: "डिलीवरी",
      purchase: "परचेज़",
      financial: "वित्तीय",
      users: "यूज़र",
      ipAddresses: "आईपी एड्रेस",
      customers: "ग्राहक",
      warehouses: "वेयरहाउस",
      inventory: "इन्वेंटरी",
      products: "प्रोडक्ट",
      purchaseOrders: "परचेज़ ऑर्डर",
      category: "कैटेगरी",
      subCategory: "सब कैटेगरी",
      product: "प्रोडक्ट",
      taxes: "टैक्स मैनेजमेंट",
      hsnCodes: "एचएसएन कोड",
      locations: "लोकेशन",
      inquiries: "पूछताछ",
      purchaseAdvice: "परचेज़ एडवाइस",
      purchaseChallan: "परचेज़ चालान",
      purchaseDaybook: "परचेज़ डेबुक",
      // Order related labels
      order: "ऑर्डर",
      ordersLabel: "ऑर्डर",
      deliveryOrderLabel: "डिलीवरी ऑर्डर",
      deliveryOrder: "डिलीवरी ऑर्डर",
      deliveryChallan: "डिलीवरी चालान",
      invoiceSales: "प्रोफॉर्मा इनवॉइस",
      invoicePurchase: "परचेज़ एस्टिमेट",
      salesDaybook: "सेल्स डेबुक",
      ordersDaily: "दैनिक ऑर्डर",
      reports: "रिपोर्ट",
      tallyIntegration: "टैली इंटीग्रेशन",
      // AI Advisor
      ai: "एआई",
      aiChat: "एआई चैट",
      aiPurchasePlan: "परचेज़ प्लान",
      // Settings
      settings: "सेटिंग्स",
      channelSettings: "सेटिंग्स",
      tallySettings: "टैली सेटिंग्स",
      scheduledReports: "अनुसूचित रिपोर्ट",
      intelligence: "एनालिटिक्स इंटेलिजेंस",
    },
    pageSubtitles: {
      purchaseChallan: "परचेज़ चालान प्रबंधित करें",
      purchaseChallanPrinted: "प्रिंट किए गए परचेज़ चालान",
      purchaseOrders: "परचेज़ ऑर्डर और सप्लायर प्रबंधित करें",
    },
    pageMetrics: {
      totalChallans: "कुल चालान",
      totalPrinted: "कुल प्रिंट किए गए",
      totalOrders: "कुल ऑर्डर",
    },
    pageTitles: {
      purchaseChallanPrinted: "परचेज़ चालान प्रिंट किया गया",
    },
    sidebar: {
      mobile: {
        openMenu: "मेनू खोलें",
        closeMenu: "मेनू बंद करें",
      },
      collapse: {
        expand: "साइडबार विस्तृत करें",
        collapse: "साइडबार समेटें",
      },
      logout: "लॉगआउट",
      profile: "प्रोफाइल",
      userFallback: "यूज़र",
      loading: "लोड हो रहा है...",
    },
    badges: {
      role: "भूमिका",
    },
  },
  orders: {},
  purchaseOrders: {},
  deliveryChallans: {
    page: {
      header: {
        title: "डिलीवरी चालान",
        subtitle: "डिलीवरी चालान प्रबंधित करें और शिपमेंट ट्रैक करें",
      },
      stats: {
        total: "कुल चालान",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "त्रुटि",
      },
    },
    table: {
      columns: {
        select: "चुनें",
        orderNumber: "ऑर्डर नंबर",
        serialNumber: "सीरियल नंबर",
        challanNumber: "चालान नंबर",
        date: "तारीख",
        challanDate: "चालान तारीख",
        customerName: "ग्राहक का नाम",
        totalAmount: "कुल राशि",
        status: "स्थिति",
        actions: "क्रियाएं",
      },
      emptyState: {
        title: "कोई डिलीवरी चालान नहीं मिला",
        message: "अभी दिखाने के लिए कोई डिलीवरी चालान नहीं है।",
      },
      empty: {
        title: "कोई डिलीवरी चालान नहीं मिला",
        message: "अभी दिखाने के लिए कोई डिलीवरी चालान नहीं है।",
      },
      aria: {
        selectAll: "सभी डिलीवरी चालान चुनें",
        selectChallan: "डिलीवरी चालान चुनें",
        viewChallan: "डिलीवरी चालान देखें",
        updateChallan: "डिलीवरी चालान अपडेट करें",
        printChallan: "डिलीवरी चालान प्रिंट करें",
      },
      tooltips: {
        view: "डिलीवरी चालान देखें",
        updateDeliveryChallan: "डिलीवरी चालान अपडेट करें",
        printDeliveryChallan: "डिलीवरी चालान प्रिंट करें",
        markDelivered: "डिलीवर के रूप में चिह्नित करें",
      },
    },
    statuses: {
      pending: "लंबित",
      in_transit: "ट्रांजिट में",
      delivered: "डिलीवर किया गया",
      invoiced: "इनवॉइस किया गया",
    },
    messages: {
      updateSuccess: "डिलीवरी चालान सफलतापूर्वक अपडेट किया गया",
      updateFailed: "डिलीवरी चालान अपडेट करने में विफल",
      markDeliveredSuccess: "डिलीवरी चालान डिलीवर के रूप में चिह्नित किया गया",
      markDeliveredFailed: "डिलीवरी चालान को डिलीवर के रूप में चिह्नित करने में विफल",
      confirmMarkDelivered: "इस डिलीवरी चालान को डिलीवर के रूप में चिह्नित करें?",
    },
    actions: {
      searchPlaceholder: "डिलीवरी चालान खोजें...",
      buttons: {
        export: "चालान एक्सपोर्ट करें",
        bulkDelete: "चयनित हटाएं",
      },
    },
  },
  invoiceSales: {
    page: {
      header: {
        title: "प्रोफॉर्मा इनवॉइस",
        subtitle: "सेल्स डेबुक और इनवॉइस रिकॉर्ड देखें",
      },
      stats: {
        total: "कुल रिकॉर्ड",
      },
      alerts: {
        errorTitle: "त्रुटि",
      },
    },
    table: {
      columns: {
        date: "तारीख",
        orderNumber: "ऑर्डर नंबर",
        tallyName: "टैली नाम",
        vehicleNumber: "वाहन नंबर",
        supervisorName: "सुपरवाइज़र",
        labourCount: "मज़दूर",
        totalAmount: "कुल राशि",
        remark: "टिप्पणियाँ",
        invoiceNumber: "इनवॉइस नंबर",
        status: "स्थिति",
      },
      emptyState: {
        title: "कोई सेल्स रिकॉर्ड नहीं मिला",
        message:
          "चयनित तारीख सीमा के लिए दिखाने हेतु कोई सेल्स रिकॉर्ड नहीं है।",
      },
      aria: {
        selectAll: "सभी सेल्स रिकॉर्ड चुनें",
        selectRecord: "सेल्स रिकॉर्ड चुनें",
      },
    },
    statuses: {
      pending: "लंबित",
      in_transit: "ट्रांजिट में",
      delivered: "डिलीवर किया गया",
      invoiced: "इनवॉइस किया गया",
    },
    actions: {
      searchPlaceholder: "सेल्स रिकॉर्ड खोजें...",
      fromPlaceholder: "से",
      toPlaceholder: "तक",
      buttons: {
        refresh: "रिफ्रेश",
      },
    },
  },
  salesDaybook: {
    page: {
      header: {
        title: "सेल्स डेबुक",
        subtitle: "सेल्स डेबुक रिकॉर्ड देखें और प्रबंधित करें",
      },
      stats: {
        total: "कुल रिकॉर्ड",
      },
      alerts: {
        errorTitle: "त्रुटि",
      },
    },
    table: {
      columns: {
        date: "तारीख",
        orderNumber: "ऑर्डर नंबर",
        tallyName: "टैली नाम",
        vehicleNumber: "वाहन नंबर",
        supervisorName: "सुपरवाइज़र",
        labourCount: "मज़दूर",
        totalAmount: "कुल राशि",
        remark: "टिप्पणियाँ",
        invoiceNumber: "इनवॉइस नंबर",
        status: "स्थिति",
      },
      emptyState: {
        title: "कोई सेल्स डेबुक रिकॉर्ड नहीं मिला",
        message:
          "चयनित तारीख सीमा के लिए दिखाने हेतु कोई सेल्स डेबुक रिकॉर्ड नहीं है।",
      },
      aria: {
        selectAll: "सभी डेबुक रिकॉर्ड चुनें",
        selectRecord: "डेबुक रिकॉर्ड चुनें",
      },
    },
    statuses: {
      pending: "लंबित",
      in_transit: "ट्रांजिट में",
      delivered: "डिलीवर किया गया",
      invoiced: "इनवॉइस किया गया",
    },
    actions: {
      searchPlaceholder: "डेबुक रिकॉर्ड खोजें...",
      fromPlaceholder: "से",
      toPlaceholder: "तक",
      buttons: {
        refresh: "रिफ्रेश",
      },
    },
  },
  profile: {
    title: "मेरी प्रोफाइल",
    subtitle: "अपनी व्यक्तिगत जानकारी और अकाउंट सेटिंग्स प्रबंधित करें",
    loading: "प्रोफाइल लोड हो रही है...",
    fields: {
      freight: { label: "फ्रेट", placeholder: "फ्रेट राशि दर्ज करें" },
      discountType: {
        label: "छूट का प्रकार",
        placeholder: "जैसे: percent | flat",
      },
      discountValue: {
        label: "छूट का मूल्य",
        placeholder: "छूट का मूल्य दर्ज करें",
      },
      loadingCost: { label: "लोडिंग लागत", placeholder: "लोडिंग लागत दर्ज करें" },
      gstOption: {
        label: "जीएसटी विकल्प",
        placeholder: "gst inclusive | exclusive",
      },
      gstnNumber: { label: "जीएसटीएन नंबर", placeholder: "जीएसटीएन नंबर दर्ज करें" },

      smsNotification: { label: "एसएमएस नोटिफिकेशन", placeholder: "" },
      firstName: "पहला नाम",
      lastName: "अंतिम नाम",
      email: "ईमेल एड्रेस",
      phoneNumber: "फ़ोन नंबर",
      mobileNumber: "मोबाइल नंबर",
      userType: "यूज़र प्रकार",
      language: "भाषा",
    },
    language: {
      label: "भाषा",
      english: "English",
      hindi: "हिंदी",
      updated: "भाषा अपडेट की गई",
    },
    buttons: {
      edit: "प्रोफाइल संपादित करें",
      cancel: "रद्द करें",
      save: "परिवर्तन सहेजें",
      saving: "सहेजा जा रहा है...",
    },
    validation: {
      firstNameMin: "पहला नाम कम से कम 2 अक्षरों का होना चाहिए",
      lastNameMin: "अंतिम नाम कम से कम 2 अक्षरों का होना चाहिए",
      emailInvalid: "कृपया एक मान्य ईमेल एड्रेस दर्ज करें",
      phoneInvalid: "फ़ोन नंबर 10 अंकों का होना चाहिए",
      mobileInvalid: "मोबाइल नंबर 10 अंकों का होना चाहिए",
    },
    note: "आपका यूज़र प्रकार एडमिन द्वारा प्रबंधित किया जाता है और इस पेज से बदला नहीं जा सकता। यदि आपको भूमिका में बदलाव की आवश्यकता है तो अपने सिस्टम एडमिनिस्ट्रेटर से संपर्क करें।",
    notifications: {
      updated: "प्रोफाइल सफलतापूर्वक अपडेट की गई",
    },
    errors: {
      fetchFailed: "प्रोफाइल लाने में विफल",
      updateFailed: "प्रोफाइल अपडेट करने में विफल",
    },
    resetPassword: {
      title: "पासवर्ड रीसेट करें",
      fields: {
        currentPassword: "वर्तमान पासवर्ड",
        newPassword: "नया पासवर्ड",
        confirmPassword: "नए पासवर्ड की पुष्टि करें",
      },
      buttons: {
        reset: "पासवर्ड रीसेट करें",
        resetting: "रीसेट किया जा रहा है...",
        cancel: "रद्द करें",
      },
      notifications: {
        success: "पासवर्ड सफलतापूर्वक रीसेट किया गया",
      },
      errors: {
        currentPasswordRequired: "वर्तमान पासवर्ड आवश्यक है",
        newPasswordRequired: "नया पासवर्ड आवश्यक है",
        passwordTooShort: "पासवर्ड कम से कम 8 अक्षरों का होना चाहिए",
        passwordMissingLowercase:
          "पासवर्ड में कम से कम एक लोअरकेस अक्षर होना चाहिए",
        passwordMissingUppercase:
          "पासवर्ड में कम से कम एक अपरकेस अक्षर होना चाहिए",
        passwordMissingNumber: "पासवर्ड में कम से कम एक अंक होना चाहिए",
        passwordMissingSymbol:
          "पासवर्ड में कम से कम एक विशेष वर्ण होना चाहिए",
        confirmPasswordRequired: "कृपया अपने नए पासवर्ड की पुष्टि करें",
        confirmPasswordMismatch: "पासवर्ड मेल नहीं खाते",
      },
    },
    // Inline "Change Password" section within the user create/edit form.
    passwordReset: {
      heading: "पासवर्ड बदलें",
      helper: "ऊपर दोनों पासवर्ड फ़ील्ड भरें, फिर रीसेट करने के लिए क्लिक करें।",
      resetting: "रीसेट किया जा रहा है...",
      submit: "अभी पासवर्ड अपडेट करें",
      success: "पासवर्ड सफलतापूर्वक रीसेट किया गया",
      error: "पासवर्ड रीसेट करने में विफल",
      unexpectedError: "एक अप्रत्याशित त्रुटि हुई",
    },
    submitFallbackError: "यूज़र सहेजने में विफल। कृपया विवरण जांचें और पुनः प्रयास करें।",
    removeWarehouse: "हटाएं",
    warehouseFallback: "वेयरहाउस {id}",
  },
  login: {
    brand: {
      name: "ऑर्डर मैनेजमेंट सिस्टम",
      alt: "ऑर्डर पूर्ति",
    },
    header: {
      welcome: "वापसी पर स्वागत है",
      subtitle: "कृपया अपने अकाउंट में साइन इन करें",
    },
    form: {
      labels: {
        email: "ईमेल या मोबाइल नंबर",
        password: "पासवर्ड",
      },
      placeholders: {
        email: "ईमेल या मोबाइल नंबर दर्ज करें",
        password: "अपना पासवर्ड दर्ज करें",
      },
      rememberMe: "मुझे याद रखें",
      submit: {
        default: "साइन इन करें",
        loading: "साइन इन हो रहा है...",
      },
      aria: {
        showPassword: "पासवर्ड दिखाएं",
        hidePassword: "पासवर्ड छिपाएं",
      },
      validation: {
        emailRequired: "ईमेल या मोबाइल नंबर आवश्यक है",
        emailInvalid:
          "कृपया एक मान्य ईमेल एड्रेस या 10-अंकीय मोबाइल नंबर दर्ज करें",
        passwordRequired: "पासवर्ड आवश्यक है",
        passwordLength: "पासवर्ड कम से कम 8 अक्षरों का होना चाहिए",
        passwordLowercase:
          "पासवर्ड में कम से कम एक लोअरकेस अक्षर होना चाहिए",
        passwordUppercase:
          "पासवर्ड में कम से कम एक अपरकेस अक्षर होना चाहिए",
        passwordNumber: "पासवर्ड में कम से कम एक अंक होना चाहिए",
        passwordSymbol:
          "पासवर्ड में कम से कम एक विशेष वर्ण होना चाहिए (!@#$%^&*()_+-=[]{}|;:,.<>?)",
      },
      errors: {
        unexpected: "एक अप्रत्याशित त्रुटि हुई। कृपया पुनः प्रयास करें।",
      },
    },
    messages: {
      loginSuccess: "लॉगिन सफल:",
      loginError: "लॉगिन त्रुटि:",
    },
  },
  locations: {
    defaults: {
      country: "भारत",
    },
    page: {
      header: {
        title: "लोकेशन मैनेजमेंट",
        subtitle:
          "वेयरहाउस लोकेशन, ऑफिस, स्टोर और वितरण केंद्र प्रबंधित करें",
      },
      accessDenied: {
        title: "एक्सेस अस्वीकृत",
        message: "आपके पास लोकेशन मैनेजमेंट तक पहुंचने की अनुमति नहीं है।",
      },
      stats: {
        total: "कुल लोकेशन",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "लोकेशन त्रुटि",
      },
    },
    actions: {
      searchPlaceholder: "लोकेशन खोजें...",
      buttons: {
        add: "लोकेशन जोड़ें",
        edit: "लोकेशन संपादित करें",
        delete: "लोकेशन हटाएं",
        bulkDelete: "चयनित हटाएं",
        export: "लोकेशन एक्सपोर्ट करें",
        view: "विवरण देखें",
        activate: "सक्रिय करें",
        deactivate: "निष्क्रिय करें",
        refresh: "रिफ्रेश",
      },
      toolbar: {
        title: "लोकेशन क्रियाएं",
        description:
          "खोज, फ़िल्टर और बल्क ऑपरेशन के साथ लोकेशन प्रबंधित करें",
      },
    },
    table: {
      columns: {
        id: "आईडी",
        name: "लोकेशन नाम",
        type: "प्रकार",
        address: "पता",
        addressLine1: "पता पंक्ति 1",
        addressLine2: "पता पंक्ति 2",
        location: "लोकेशन",
        city: "शहर",
        state: "राज्य",
        country: "देश",
        postalCode: "पिन कोड",
        phone: "फ़ोन",
        email: "ईमेल",
        status: "स्थिति",
        createdAt: "बनाया गया",
        actions: "क्रियाएं",
        map_url: "मैप",
      },
      copy: {
        unnamedLocation: "अनाम लोकेशन",
        noAddress: "कोई पता नहीं दिया गया",
        noPhone: "कोई फ़ोन नहीं",
        viewMap: "मैप देखें",
      },
      status: {
        active: "सक्रिय",
        inactive: "निष्क्रिय",
      },
      aria: {
        selectAll: "सभी लोकेशन चुनें",
        selectRow: "चुनें",
      },
    },
    forms: {
      title: {
        add: "लोकेशन जोड़ें",
        edit: "लोकेशन संपादित करें",
      },
      buttons: {
        cancel: "रद्द करें",
        save: "सहेजें",
        previewMap: "मैप पूर्वावलोकन",
        submit: {
          add: "सहेजें",
          loading: "सहेजा जा रहा है...",
        },
      },
      map: {
        title: "लोकेशन चुनें",
        buttons: {
          currentLocation: "वर्तमान लोकेशन",
          useLocation: "लोकेशन उपयोग करें",
          close: "बंद करें",
        },
        tooltips: {
          currentLocation: "वर्तमान लोकेशन उपयोग करें",
        },
        errors: {
          geolocationNotSupported:
            "यह ब्राउज़र जियोलोकेशन का समर्थन नहीं करता।",
          permissionDenied: "यूज़र द्वारा लोकेशन एक्सेस अस्वीकृत।",
          positionUnavailable: "लोकेशन जानकारी उपलब्ध नहीं है।",
          timeout: "लोकेशन अनुरोध का समय समाप्त हो गया।",
          unknown: "लोकेशन प्राप्त करते समय एक अज्ञात त्रुटि हुई।",
          mapLoad: "मैप लोड करने में विफल। कृपया अपना इंटरनेट कनेक्शन जांचें।",
          selectedLocation: "चयनित लोकेशन प्राप्त करने में त्रुटि।",
        },
        instructions:
          'लोकेशन चुनने के लिए मैप पर क्लिक करें या मार्कर खींचें। अपनी स्थिति पर केंद्रित करने के लिए "वर्तमान लोकेशन" का उपयोग करें, फिर पुष्टि के लिए "लोकेशन उपयोग करें" पर क्लिक करें।',
        markerTitle: "लोकेशन चुनने के लिए मुझे खींचें",
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
      create: {
        title: "नई लोकेशन जोड़ें",
        submit: "लोकेशन बनाएं",
        submitting: "बनाया जा रहा है...",
      },
      edit: {
        title: "लोकेशन संपादित करें",
        submit: "लोकेशन अपडेट करें",
        submitting: "अपडेट किया जा रहा है...",
      },
      fields: {
        name: {
          label: "लोकेशन नाम",
          placeholder: "लोकेशन नाम दर्ज करें",
          errors: {
            required: "लोकेशन नाम आवश्यक है",
            minLength: "लोकेशन नाम कम से कम 2 अक्षरों का होना चाहिए",
          },
        },
        locationType: {
          label: "लोकेशन प्रकार",
          placeholder: "लोकेशन प्रकार चुनें",
          errors: {
            required: "लोकेशन प्रकार आवश्यक है",
          },
        },
        addressLine1: {
          label: "पता पंक्ति 1",
          placeholder: "पता पंक्ति 1 दर्ज करें",
          errors: {
            required: "पता पंक्ति 1 आवश्यक है",
          },
        },
        addressLine2: {
          label: "पता पंक्ति 2",
          placeholder: "पता पंक्ति 2 दर्ज करें (वैकल्पिक)",
        },
        city: {
          label: "शहर",
          placeholder: "शहर दर्ज करें",
          errors: {
            required: "शहर आवश्यक है",
          },
        },
        state: {
          label: "राज्य",
          placeholder: "राज्य चुनें",
          errors: {
            required: "राज्य आवश्यक है",
          },
        },
        country: {
          label: "देश",
          placeholder: "देश दर्ज करें",
          errors: {
            required: "देश आवश्यक है",
          },
        },
        postalCode: {
          label: "पिन कोड",
          placeholder: "पिन कोड दर्ज करें",
          errors: {
            invalid: "पिन कोड 5-6 अंकों का होना चाहिए",
          },
        },
        phone: {
          label: "फ़ोन नंबर",
          placeholder: "फ़ोन नंबर दर्ज करें",
          errors: {
            invalid: "फ़ोन नंबर का प्रारूप अमान्य है",
          },
        },
        email: {
          label: "ईमेल",
          placeholder: "ईमेल एड्रेस दर्ज करें",
          errors: {
            invalid: "ईमेल का प्रारूप अमान्य है",
          },
        },
        description: {
          label: "विवरण",
          placeholder: "लोकेशन विवरण दर्ज करें (वैकल्पिक)",
        },
        deliveryLocation: {
          label: "डिलीवरी लोकेशन",
          placeholder: "विशिष्ट डिलीवरी लोकेशन या निर्देशांक दर्ज करें",
          errors: {
            required: "डिलीवरी लोकेशन आवश्यक है",
          },
        },
        mapUrl: {
          label: "मैप यूआरएल",
          placeholder: "https://maps.google.com/?q=...",
          errors: {
            invalid:
              "कृपया एक मान्य यूआरएल दर्ज करें (http:// या https:// से शुरू)",
          },
        },
        isActive: {
          label: "सक्रिय स्थिति",
        },
      },
      validation: {
        failed: "कृपया सबमिट करने से पहले फ़ॉर्म की त्रुटियाँ ठीक करें",
      },
    },
    confirm: {
      deleteSingle: {
        title: "लोकेशन हटाएं",
        message:
          "क्या आप वाकई इस लोकेशन को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
        confirmLabel: "लोकेशन हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      deleteBulk: {
        title: "लोकेशन हटाएं",
        messageSingle: "क्या आप वाकई चयनित लोकेशन को हटाना चाहते हैं?",
        messageMultiple:
          "क्या आप वाकई {count} चयनित लोकेशन हटाना चाहते हैं?",
        confirmLabel: "लोकेशन हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      toggleStatus: {
        activate: {
          title: "लोकेशन सक्रिय करें",
          message: "क्या आप वाकई इस लोकेशन को सक्रिय करना चाहते हैं?",
          confirmLabel: "सक्रिय करें",
          loadingLabel: "सक्रिय किया जा रहा है...",
        },
        deactivate: {
          title: "लोकेशन निष्क्रिय करें",
          message: "क्या आप वाकई इस लोकेशन को निष्क्रिय करना चाहते हैं?",
          confirmLabel: "निष्क्रिय करें",
          loadingLabel: "निष्क्रिय किया जा रहा है...",
        },
      },
      buttons: {
        cancel: "रद्द करें",
      },
    },
    details: {
      title: "लोकेशन विवरण",
      sections: {
        basic: "मूल जानकारी",
        address: "पता जानकारी",
        contact: "संपर्क जानकारी",
        status: "स्थिति जानकारी",
      },
      fields: {
        id: "लोकेशन आईडी",
        name: "लोकेशन नाम",
        type: "लोकेशन प्रकार",
        addressLine1: "पता पंक्ति 1",
        addressLine2: "पता पंक्ति 2",
        city: "शहर",
        state: "राज्य",
        country: "देश",
        postalCode: "पिन कोड",
        phone: "फ़ोन नंबर",
        email: "ईमेल एड्रेस",
        description: "विवरण",
        isActive: "स्थिति",
        createdAt: "बनाने की तारीख",
        updatedAt: "अंतिम अपडेट",
      },
    },
    messages: {
      loading: "लोकेशन लोड हो रहे हैं...",
      noData: "कोई लोकेशन नहीं मिला",
      noDataLong:
        'अभी तक कोई लोकेशन नहीं जोड़ा गया है। शुरू करने के लिए "लोकेशन जोड़ें" पर क्लिक करें।',
      fetchError: "लोकेशन लोड करने में विफल",
      refreshSuccess: "लोकेशन सफलतापूर्वक रिफ्रेश किए गए",
      refreshError: "लोकेशन रिफ्रेश करने में विफल",
      createSuccess: "लोकेशन सफलतापूर्वक बनाया गया",
      createError: "लोकेशन बनाने में विफल",
      updateSuccess: "लोकेशन सफलतापूर्वक अपडेट किया गया",
      updateError: "लोकेशन अपडेट करने में विफल",
      deleteSuccess: "लोकेशन सफलतापूर्वक हटाया गया",
      deleteError: "लोकेशन हटाने में विफल",
      bulkDeleteSuccess: "लोकेशन सफलतापूर्वक हटाए गए",
      bulkDeleteError: "लोकेशन हटाने में विफल",
      statusUpdateSuccess: "लोकेशन स्थिति सफलतापूर्वक अपडेट की गई",
      statusUpdateError: "लोकेशन स्थिति अपडेट करने में विफल",
      exportSuccess: "लोकेशन सफलतापूर्वक एक्सपोर्ट किए गए",
      exportError: "लोकेशन एक्सपोर्ट करने में विफल",
    },
    filters: {
      all: "सभी लोकेशन",
      active: "केवल सक्रिय",
      inactive: "केवल निष्क्रिय",
      state: "राज्य के अनुसार फ़िल्टर करें",
      status: "स्थिति",
      city: "शहर के अनुसार फ़िल्टर करें",
      type: "प्रकार के अनुसार फ़िल्टर करें",
    },
    types: {
      warehouse: "वेयरहाउस",
      office: "ऑफिस",
      store: "स्टोर",
      distribution_center: "वितरण केंद्र",
      pickup_point: "पिकअप पॉइंट",
      delivery_hub: "डिलीवरी हब",
      service_center: "सर्विस सेंटर",
      other: "अन्य",
    },
    // List of Indian states and union territories for filter dropdowns
    states: [
      { value: "ANDHRA_PRADESH", label: "आंध्र प्रदेश" },
      { value: "ARUNACHAL_PRADESH", label: "अरुणाचल प्रदेश" },
      { value: "ASSAM", label: "असम" },
      { value: "BIHAR", label: "बिहार" },
      { value: "CHHATTISGARH", label: "छत्तीसगढ़" },
      { value: "GOA", label: "गोवा" },
      { value: "GUJARAT", label: "गुजरात" },
      { value: "HARYANA", label: "हरियाणा" },
      { value: "HIMACHAL_PRADESH", label: "हिमाचल प्रदेश" },
      { value: "JHARKHAND", label: "झारखंड" },
      { value: "KARNATAKA", label: "कर्नाटक" },
      { value: "KERALA", label: "केरल" },
      { value: "MADHYA_PRADESH", label: "मध्य प्रदेश" },
      { value: "MAHARASHTRA", label: "महाराष्ट्र" },
      { value: "MANIPUR", label: "मणिपुर" },
      { value: "MEGHALAYA", label: "मेघालय" },
      { value: "MIZORAM", label: "मिज़ोरम" },
      { value: "NAGALAND", label: "नागालैंड" },
      { value: "ODISHA", label: "ओडिशा" },
      { value: "PUNJAB", label: "पंजाब" },
      { value: "RAJASTHAN", label: "राजस्थान" },
      { value: "SIKKIM", label: "सिक्किम" },
      { value: "TAMIL_NADU", label: "तमिलनाडु" },
      { value: "TELANGANA", label: "तेलंगाना" },
      { value: "TRIPURA", label: "त्रिपुरा" },
      { value: "UTTAR_PRADESH", label: "उत्तर प्रदेश" },
      { value: "UTTARAKHAND", label: "उत्तराखंड" },
      { value: "WEST_BENGAL", label: "पश्चिम बंगाल" },
      // Union Territories
      { value: "ANDAMAN_NICOBAR", label: "अंडमान और निकोबार द्वीप समूह" },
      { value: "CHANDIGARH", label: "चंडीगढ़" },
      {
        value: "DADRA_NAGAR_HAVELI_DAMAN_DIU",
        label: "दादरा और नगर हवेली और दमन और दीव",
      },
      { value: "DELHI", label: "दिल्ली" },
      { value: "JAMMU_KASHMIR", label: "जम्मू और कश्मीर" },
      { value: "LADAKH", label: "लद्दाख" },
      { value: "LAKSHADWEEP", label: "लक्षद्वीप" },
      { value: "PUDUCHERRY", label: "पुडुचेरी" },
    ],
  },
  warehouses: {
    page: {
      header: {
        title: "वेयरहाउस",
        subtitle: "वेयरहाउस और इन्वेंटरी लोकेशन प्रबंधित करें",
      },
      stats: {
        total: "कुल वेयरहाउस",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "वेयरहाउस त्रुटि",
      },
    },
    actions: {
      searchPlaceholder: "वेयरहाउस खोजें...",
      buttons: {
        add: "वेयरहाउस जोड़ें",
        refresh: "रिफ्रेश",
        bulkDelete: "चयनित हटाएं",
      },
      alerts: {
        noSelection: "जारी रखने के लिए कम से कम एक वेयरहाउस चुनें।",
      },
    },
    table: {
      columns: {
        select: "चुनें",
        name: "वेयरहाउस नाम",
        total_capacity: "कुल क्षमता (m²)",
        address: "पता",
        google_maps_url: "मैप",
        type: "प्रकार",
        location: "लोकेशन",
        manager: "मैनेजर",
        phone: "फ़ोन",
        email: "ईमेल",
        status: "स्थिति",
        createdAt: "बनाया गया",
        actions: "कार्रवाई",
      },
      emptyState: {
        title: "कोई वेयरहाउस नहीं मिला",
        message: "दिखाने के लिए कोई वेयरहाउस नहीं है।",
      },
      aria: {
        selectAll: "सभी वेयरहाउस चुनें",
        viewWarehouse: "वेयरहाउस देखें",
        editWarehouse: "वेयरहाउस संपादित करें",
        deleteWarehouse: "वेयरहाउस हटाएं",
        openMap: "मैप खोलें",
      },
    },
    forms: {
      title: {
        add: "नया वेयरहाउस जोड़ें",
        edit: "वेयरहाउस संपादित करें",
      },
      fields: {
        name: {
          label: "वेयरहाउस नाम",
          placeholder: "वेयरहाउस नाम दर्ज करें",
          errors: {
            required: "वेयरहाउस नाम आवश्यक है",
          },
        },
        code: { label: "कोड", placeholder: "जैसे, WH-001" },
        address: { label: "पता", placeholder: "पता दर्ज करें" },
        city: { label: "शहर", placeholder: "शहर दर्ज करें" },
        state: { label: "राज्य", placeholder: "राज्य चुनें" },
        pincode: { label: "पिन कोड", placeholder: "पिन कोड दर्ज करें" },
        country: { label: "देश", placeholder: "देश दर्ज करें" },
        postalCode: { label: "पोस्टल कोड", placeholder: "पोस्टल कोड दर्ज करें" },
        contact_person: {
          label: "संपर्क व्यक्ति",
          placeholder: "संपर्क व्यक्ति का नाम दर्ज करें",
        },
        contact_phone: {
          label: "संपर्क फ़ोन",
          placeholder: "संपर्क फ़ोन दर्ज करें",
        },
        contact_email: {
          label: "संपर्क ईमेल",
          placeholder: "संपर्क ईमेल दर्ज करें",
        },
        manager: { label: "मैनेजर", placeholder: "मैनेजर का नाम दर्ज करें" },
        phone: { label: "फ़ोन", placeholder: "फ़ोन नंबर दर्ज करें" },
        email: { label: "ईमेल", placeholder: "ईमेल पता दर्ज करें" },
        total_capacity: {
          label: "कुल क्षमता (m²)",
          placeholder: "जैसे, 500 m²",
        },
        google_maps_url: {
          label: "गूगल मैप्स URL",
          placeholder: "मैप पर लोकेशन चुनें",
        },
        isActive: { label: "सक्रिय" },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: {
          add: "वेयरहाउस बनाएं",
          edit: "वेयरहाउस अपडेट करें",
          loading: {
            add: "बनाया जा रहा है...",
            edit: "अपडेट हो रहा है...",
          },
        },
      },
    },
    view: {
      header: {
        sectionTitle: "वेयरहाउस विवरण",
        fallbackTitle: "वेयरहाउस जानकारी",
      },
      loading: "वेयरहाउस विवरण लोड हो रहा है...",
      empty: {
        title: "कोई वेयरहाउस नहीं मिला",
        message: "वेयरहाउस विवरण लोड नहीं किया जा सका।",
      },
      fields: {
        warehouseName: "वेयरहाउस नाम",
        totalCapacity: "कुल क्षमता (m²)",
        status: "स्थिति",
        fullAddress: "पूरा पता",
        googleMaps: "गूगल मैप्स",
        viewOnGoogleMaps: "गूगल मैप्स पर देखें",
      },
      buttons: {
        close: "बंद करें",
      },
      sections: {
        basicInformation: "बुनियादी जानकारी",
        addressInformation: "पता जानकारी",
        location: "लोकेशन",
      },
    },
    confirm: {
      deleteSingle: {
        title: "वेयरहाउस हटाएं",
        message:
          "क्या आप वाकई इस वेयरहाउस को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      deleteBulk: {
        title: "चयनित वेयरहाउस हटाएं",
        messageMultiple:
          "क्या आप वाकई {count} चयनित वेयरहाउस हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      buttons: {
        cancel: "रद्द करें",
      },
    },
    notifications: {
      created: "वेयरहाउस {id} सफलतापूर्वक बनाया गया",
      updated: "वेयरहाउस {id} सफलतापूर्वक अपडेट किया गया",
      deleted: "वेयरहाउस {id} सफलतापूर्वक हटाया गया",
      bulkDeleted: "{count} वेयरहाउस सफलतापूर्वक हटाए गए",
    },
    alerts: {
      fetchFailed: "वेयरहाउस लाने में विफल",
      createFailed: "वेयरहाउस बनाने में विफल",
      updateFailed: "वेयरहाउस अपडेट करने में विफल",
      deleteFailed: "वेयरहाउस हटाने में विफल",
      noSelection: "जारी रखने के लिए कम से कम एक वेयरहाउस चुनें",
    },
    messages: {
      loading: "वेयरहाउस लोड हो रहे हैं...",
      noData: "कोई वेयरहाउस नहीं मिला",
    },
  },
  warehouseInventory: {
    import: {
      config: {
        templateFilename: "inventory_import_template",
        inputId: "inventory-import-input",
      },
      success: "इम्पोर्ट पूरा — {balances} बैलेंस सेट, {coils} कॉइल जोड़ी गईं, {minimums} न्यूनतम स्टॉक स्तर सेट, {mappings} प्रोडक्ट वेयरहाउस में जोड़े गए।",
      noChanges: "कुछ नहीं बदला — हर पंक्ति पहले से दर्ज जानकारी से मेल खाती है।",
    },
    notifications: {
      created: "इन्वेंटरी {id} सफलतापूर्वक बनाई गई",
      updated: "इन्वेंटरी {id} सफलतापूर्वक अपडेट की गई",
      deleted: "इन्वेंटरी {id} सफलतापूर्वक हटाई गई",
      transferred: "स्टॉक सफलतापूर्वक ट्रांसफर किया गया",
      stockUpdated: "{id} के लिए स्टॉक सफलतापूर्वक अपडेट किया गया",
      lowStock: "कम स्टॉक",
    },
    messages: {
      loading: "इन्वेंटरी लोड हो रही है...",
      noData: "कोई इन्वेंटरी नहीं मिली",
      fetchError: "इन्वेंटरी लोड करने में विफल",
    },
    table: {
      columns: {
        warehouse_name: "वेयरहाउस",
        warehouse: "वेयरहाउस",
        address: "पता",
        product_name: "प्रोडक्ट",
        product_id: "प्रोडक्ट ID",
        total_stock_all_warehouses: "कुल स्टॉक",
        total_pending_all_warehouses: "कुल पेंडिंग",
        total_pending_purchase_all_warehouses: "कुल पेंडिंग परचेज़",
        total_available_all_warehouses: "कुल उपलब्ध",
        warehouse_count: "वेयरहाउस",
        available_inventory: "उपलब्ध स्टॉक",
        stock_quantity: "स्टॉक मात्रा",
        minimum_stock: "न्यूनतम स्टॉक",
        pending_order: "पेंडिंग ऑर्डर",
        actions: "कार्रवाई",
      },
      emptyState: {
        title: "कोई इन्वेंटरी नहीं मिली",
        message: "दिखाने के लिए कोई इन्वेंटरी आइटम नहीं है।",
      },
      aria: {
        viewInventory: "इन्वेंटरी देखें",
        editInventory: "इन्वेंटरी संपादित करें",
        adjustStock: "स्टॉक समायोजित करें",
        deleteInventory: "इन्वेंटरी हटाएं",
      },
    },
    actions: {
      searchPlaceholder: "इन्वेंटरी खोजें...",
      filters: {
        warehouseName: "वेयरहाउस",
        all: "सभी",
        subCategory: "सब कैटेगरी",
        subCategoryAll: "सभी सब कैटेगरी",
        productName: "प्रोडक्ट",
        lowStock: "कम स्टॉक",
        lowStockOptions: {
          all: "सभी",
          yes: "केवल कम स्टॉक",
          no: "कम स्टॉक को छोड़ें",
        },
      },
      buttons: {
        import: "इम्पोर्ट",
        refresh: "रिफ्रेश",
        add: "इन्वेंटरी जोड़ें",
        transferStock: "स्टॉक ट्रांसफर करें",
        exportBySubcategory: "सब कैटेगरी द्वारा एक्सपोर्ट",
      },
    },
    exportBySubcategory: {
      title: "सब कैटेगरी द्वारा इन्वेंटरी एक्सपोर्ट करें",
      subtitle: "प्रति प्रोडक्ट एक पंक्ति और प्रति वेयरहाउस एक कॉलम वाली CSV डाउनलोड करें।",
      subCategoryLabel: "सब कैटेगरी",
      subCategoryPlaceholder: "सब कैटेगरी खोजें...",
      subCategoryHint: "हर सब कैटेगरी एक्सपोर्ट करने के लिए खाली छोड़ें।",
      selectedSuffix: "चयनित",
      noSubCategories: "कोई मेल खाती सब कैटेगरी नहीं",
      warehouseLabel: "वेयरहाउस",
      warehousePlaceholder: "आपके स्कोप के सभी वेयरहाउस",
      warehouseHint: "आपके पहुंच योग्य हर वेयरहाउस को शामिल करने के लिए खाली छोड़ें।",
      valueLabel: "मात्रा",
      valueStock: "स्टॉक (मौजूदा)",
      valueAvailable: "उपलब्ध (स्टॉक − पेंडिंग)",
      cancel: "रद्द करें",
      export: "CSV एक्सपोर्ट करें",
      exporting: "एक्सपोर्ट हो रहा है...",
      loadingSubCategories: "सब कैटेगरी लोड हो रही हैं...",
      success: "इन्वेंटरी एक्सपोर्ट सफलतापूर्वक डाउनलोड हुई।",
      error: "इन्वेंटरी एक्सपोर्ट करने में विफल।",
      subCategoryFallback: "सब कैटेगरी {id}",
      warehouseFallback: "वेयरहाउस {id}",
      removeChip: "{label} हटाएं",
    },
    bulkEdit: {
      enterMode: "बल्क एडिट",
      exitMode: "बल्क एडिट से बाहर निकलें",
      saveAll: "सभी सहेजें",
      discard: "छोड़ें",
      discardConfirm: "छोड़ें",
      discardTitle: "बदलाव छोड़ें?",
      discardMessage: "आपके पास असहेजे बदलाव हैं। क्या आप वाकई बल्क एडिट मोड से बाहर निकलना चाहते हैं?",
      dirtyCount: "{products} प्रोडक्ट में {count} बदलाव",
      savingProgress: "{total} में से {current} सहेजा जा रहा है...",
      footerHint: "प्रति वेयरहाउस स्टॉक, न्यूनतम-स्टॉक और पेंडिंग मान संपादित करें, फिर सभी सहेजें पर क्लिक करें।",
      hiddenHint: "संपादन वाले {n} प्रोडक्ट वर्तमान फ़िल्टर द्वारा छिपे हैं — समीक्षा के लिए फ़िल्टर हटाएं।",
      allSuccess: "{products} प्रोडक्ट में {count} बदलाव सहेजे गए।",
      allFailed: "कोई भी बदलाव सहेजने में विफल।",
      partialFailureTitle: "आंशिक सहेजना",
      partialFailureBody: "{total} में से {succeeded} प्रोडक्ट सहेजे गए। विफल:\n\n",
      lowStockLabel: "कम स्टॉक",
      noWarehouses: "इस प्रोडक्ट के लिए कोई वेयरहाउस डेटा उपलब्ध नहीं है।",
    },
    view: {
      header: {
        sectionTitle: "वेयरहाउस इन्वेंटरी",
        fallbackTitle: "इन्वेंटरी सारांश",
      },
      loading: "इन्वेंटरी विवरण लोड हो रहा है...",
      empty: {
        title: "इन्वेंटरी डेटा अनुपलब्ध",
        message: "हम इन्वेंटरी विवरण लोड नहीं कर सके। कृपया फिर से प्रयास करें।",
      },
      fields: {
        warehouseName: "वेयरहाउस",
        warehouseAddress: "पता",
        warehouseCapacity: "कुल क्षमता (m²)",
        totalProducts: "कुल प्रोडक्ट",
        totalStockUnits: "कुल स्टॉक यूनिट",
        lowStockProducts: "कम स्टॉक प्रोडक्ट",
      },
      productTable: {
        columns: {
          warehouse: "वेयरहाउस नाम",
          productName: "प्रोडक्ट",
          productSize: "साइज़",
          stockQuantity: "स्टॉक मात्रा",
          pendingPurchaseOrder: "पेंडिंग परचेज़ ऑर्डर",
          minimumStock: "न्यूनतम स्टॉक",
          lowStock: "कम स्टॉक",
          availableStock: "उपलब्ध स्टॉक",
        },
      },
      buttons: {
        close: "बंद करें",
      },
    },
    forms: {
      addWarehouse: {
        placeholder: "दूसरा वेयरहाउस जोड़ें…",
        button: "वेयरहाउस जोड़ें",
        remove: "वेयरहाउस हटाओ",
      },
      title: {
        add: "वेयरहाउस इन्वेंटरी जोड़ें",
        edit: "वेयरहाउस इन्वेंटरी संपादित करें",
      },
      fields: {
        warehouse_id: {
          label: "वेयरहाउस",
          placeholder: "वेयरहाउस चुनें",
          errors: {
            required: "वेयरहाउस आवश्यक है",
          },
        },
        product_size_id: {
          label: "प्रोडक्ट",
          placeholder: "प्रोडक्ट चुनें",
          errors: {
            required: "प्रोडक्ट आवश्यक है",
          },
        },
        stock_quantity: {
          label: "स्टॉक मात्रा",
          placeholder: "स्टॉक मात्रा दर्ज करें",
          errors: {
            required: "स्टॉक मात्रा आवश्यक है",
            invalid: "स्टॉक मात्रा एक मान्य धनात्मक संख्या होनी चाहिए",
          },
        },
        minimum_stock: {
          label: "न्यूनतम स्टॉक",
          placeholder: "न्यूनतम स्टॉक स्तर दर्ज करें",
          errors: {
            required: "न्यूनतम स्टॉक आवश्यक है",
            invalid: "न्यूनतम स्टॉक एक मान्य धनात्मक संख्या होनी चाहिए",
          },
        },
        pending_order: {
          label: "पेंडिंग ऑर्डर",
          placeholder: "पेंडिंग ऑर्डर मात्रा दर्ज करें",
          errors: {
            invalid: "पेंडिंग ऑर्डर एक मान्य संख्या होनी चाहिए",
          },
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: {
          add: "इन्वेंटरी बनाएं",
          edit: "इन्वेंटरी अपडेट करें",
          loading: {
            add: "बनाया जा रहा है...",
            edit: "अपडेट हो रहा है...",
          },
        },
      },
      loading: {
        warehouses: "वेयरहाउस लोड हो रहे हैं...",
        products: "प्रोडक्ट लोड हो रहे हैं...",
        inventory: "इन्वेंटरी विवरण लोड हो रहा है...",
      },
    },
    adjustForm: {
      title: "स्टॉक समायोजित करें",
      fields: {
        quantity: {
          label: "मात्रा",
          placeholder:
            "जोड़ने/हटाने के लिए मात्रा दर्ज करें (हटाने के लिए ऋणात्मक का उपयोग करें)",
          errors: {
            required: "मात्रा आवश्यक है",
            invalid: "मात्रा एक मान्य संख्या होनी चाहिए (शून्य नहीं)",
          },
        },
        reason: {
          label: "कारण",
          placeholder: "स्टॉक समायोजन का कारण दर्ज करें",
          errors: {
            required: "कारण आवश्यक है",
          },
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: "स्टॉक समायोजित करें",
        loading: "समायोजित हो रहा है...",
      },
      labels: {
        adjustingFor: "इसके लिए स्टॉक समायोजित किया जा रहा है:",
        atWarehousePrefix: "पर",
        currentStock: "वर्तमान स्टॉक:",
        quantityHint: "स्टॉक जोड़ने के लिए धनात्मक संख्या, हटाने के लिए ऋणात्मक का उपयोग करें",
        unknownProduct: "अज्ञात प्रोडक्ट",
        unknownWarehouse: "अज्ञात वेयरहाउस",
      },
    },
    page: {
      header: {
        title: "इन्वेंटरी",
        subtitle: "इन्वेंटरी स्टॉक स्तर और प्रोडक्ट वितरण प्रबंधित करें",
      },
      stats: {
        total: "कुल आइटम",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "इन्वेंटरी त्रुटि",
      },
    },
    transferForm: {
      title: "इन्वेंटरी ट्रांसफर करें",
      fields: {
        from_warehouse_id: {
          label: "स्रोत वेयरहाउस",
          placeholder: "स्रोत वेयरहाउस चुनें",
          errors: {
            required: "स्रोत वेयरहाउस आवश्यक है",
          },
        },
        to_warehouse_id: {
          label: "गंतव्य वेयरहाउस",
          placeholder: "गंतव्य वेयरहाउस चुनें",
          errors: {
            required: "गंतव्य वेयरहाउस आवश्यक है",
            sameWarehouse:
              "गंतव्य वेयरहाउस स्रोत वेयरहाउस से अलग होना चाहिए",
          },
        },
        product_size_id: {
          label: "प्रोडक्ट",
          placeholder: "प्रोडक्ट चुनें",
          errors: {
            required: "प्रोडक्ट आवश्यक है",
          },
        },
        quantity: {
          label: "मात्रा",
          placeholder: "ट्रांसफर करने के लिए मात्रा दर्ज करें",
          errors: {
            required: "मात्रा आवश्यक है",
            invalid: "मात्रा एक धनात्मक संख्या होनी चाहिए",
          },
        },
        reason: {
          label: "ट्रांसफर कारण",
          placeholder: "ट्रांसफर का कारण दर्ज करें",
          errors: {
            required: "ट्रांसफर कारण आवश्यक है",
          },
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: "स्टॉक ट्रांसफर करें",
        loading: "ट्रांसफर हो रहा है...",
      },
      loading: {
        options: "ट्रांसफर विकल्प लोड हो रहे हैं...",
      },
    },
  },
  productCatalogue: {
    actions: {
      searchPlaceholder: "प्रोडक्ट खोजें...",
      alerts: {
        noDataToExport: "एक्सपोर्ट करने के लिए कोई प्रोडक्ट उपलब्ध नहीं",
      },
      downloadPrefix: "product_catalogue_export_",
      buttons: {
        add: "प्रोडक्ट जोड़ें",
        bulkDelete: "चयनित हटाएं",
        import: "एक्सेल इम्पोर्ट करें",
        export: "एक्सेल एक्सपोर्ट करें",
        refresh: "रिफ्रेश",
        downloadTemplate: "टेम्पलेट डाउनलोड करें",
      },
      toolbar: {
        title: "प्रोडक्ट कार्रवाई",
        searchPlaceholder: "प्रोडक्ट खोजें...",
      },
    },
    page: {
      header: {
        title: "सब कैटेगरी",
        subtitle: "प्रोडक्ट इन्वेंटरी, कीमत और विनिर्देश प्रबंधित करें",
      },
      stats: {
        total: "कुल प्रोडक्ट",
        totalProducts: "कुल प्रोडक्ट",
        totalCategories: "कैटेगरी",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "त्रुटि",
      },
      accessDenied: {
        title: "पहुंच अस्वीकृत",
        message: "इस पेज को देखने के लिए आपको सुपर एडमिन होना चाहिए।",
      },
    },
    confirm: {
      deleteSingle: {
        title: "प्रोडक्ट हटाएं",
        message:
          "क्या आप वाकई {name} को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      deleteBulk: {
        title: "चयनित प्रोडक्ट हटाएं",
        messageSingle:
          "क्या आप वाकई चयनित प्रोडक्ट को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        messageMultiple:
          "क्या आप वाकई {count} चयनित प्रोडक्ट हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
        confirmLabel: "हटाएं",
        loadingLabel: "हटाया जा रहा है...",
      },
      buttons: {
        cancel: "रद्द करें",
      },
    },
    table: {
      columns: {
        select: "चुनें",
        productName: "प्रोडक्ट नाम",
        description: "विवरण",
        category: "कैटेगरी",

        hsnCode: "एचएसएन कोड",
        gstRate: "जीएसटी दर",
        actions: "कार्रवाई",
      },
      copy: {
        unnamedProduct: "अनाम प्रोडक्ट",
        noCategory: "कोई कैटेगरी नहीं",

        noHsnCode: "लागू नहीं",
        noGstRate: "0%",
        noDescription: "कोई विवरण नहीं",
      },
      emptyState: {
        title: "कोई प्रोडक्ट नहीं मिला",
        message: "दिखाने के लिए कोई प्रोडक्ट नहीं है।",
      },
      aria: {
        selectAll: "सभी प्रोडक्ट चुनें",
        selectProduct: "प्रोडक्ट चुनें",
        viewProduct: "प्रोडक्ट देखें",
        editProduct: "प्रोडक्ट संपादित करें",
        deleteProduct: "प्रोडक्ट हटाएं",
      },
    },
    forms: {
      title: {
        add: "नई सब कैटेगरी जोड़ें",
        edit: "सब कैटेगरी संपादित करें",
      },
      fields: {
        productName: {
          label: "प्रोडक्ट नाम",
          placeholder: "प्रोडक्ट नाम दर्ज करें",
          errors: {
            required: "प्रोडक्ट नाम आवश्यक है",
            minLength: "प्रोडक्ट नाम कम से कम 2 अक्षरों का होना चाहिए",
            maxLength: "प्रोडक्ट नाम 100 अक्षरों से अधिक नहीं हो सकता",
          },
        },
        description: {
          label: "विवरण",
          placeholder: "प्रोडक्ट विवरण दर्ज करें",
          errors: {
            maxLength: "विवरण 500 अक्षरों से अधिक नहीं हो सकता",
          },
        },

        productCategoryId: {
          label: "कैटेगरी",
          placeholder: "कैटेगरी चुनें",
          errors: {
            required: "कैटेगरी आवश्यक है",
          },
        },
        hsnCodeId: {
          label: "एचएसएन कोड",
          placeholder: "एचएसएन कोड चुनें",
          errors: {
            required: "एचएसएन कोड आवश्यक है",
          },
        },
      },
      buttons: {
        cancel: "रद्द करें",
        submit: {
          add: "प्रोडक्ट बनाएं",
          edit: "प्रोडक्ट अपडेट करें",
          loading: {
            add: "बनाया जा रहा है...",
            edit: "अपडेट हो रहा है...",
          },
        },
      },
      validation: {
        loadingOptions: "विकल्प लोड हो रहे हैं...",
        noCategories: "कोई कैटेगरी उपलब्ध नहीं",
        noHsnCodes: "कोई एचएसएन कोड उपलब्ध नहीं",
        selectCategory: "एक कैटेगरी चुनें",
        selectHsnCode: "एचएसएन कोड चुनें",
        dropdownError: "ड्रॉपडाउन विकल्प लोड करने में विफल",
      },
    },
    view: {
      header: {
        sectionTitle: "प्रोडक्ट विवरण",
        fallbackTitle: "प्रोडक्ट प्रोफ़ाइल",
      },
      loading: "प्रोडक्ट विवरण लोड हो रहा है...",
      empty: {
        title: "प्रोडक्ट डेटा अनुपलब्ध",
        message: "हम चयनित प्रोडक्ट लोड नहीं कर सके। कृपया फिर से प्रयास करें।",
      },
      fields: {
        productName: "प्रोडक्ट नाम",
        description: "विवरण",

        category: "कैटेगरी",
        hsnCode: "एचएसएन कोड",
        hsnDescription: "एचएसएन विवरण",
        gstRate: "जीएसटी दर",
        id: "प्रोडक्ट ID",
        createdAt: "बनाया गया",
        updatedAt: "अंतिम अपडेट",
      },
      buttons: {
        close: "बंद करें",
      },
    },
    notifications: {
      created: "{name} सफलतापूर्वक बनाया गया",
      updated: "{name} सफलतापूर्वक अपडेट किया गया",
      deleted: "{name} सफलतापूर्वक हटाया गया",
      bulkDeletedSingle: "{product} सफलतापूर्वक हटाया गया",
      bulkDeletedMultiple: "{name} प्रोडक्ट सफलतापूर्वक हटाए गए",
      imported: "प्रोडक्ट सफलतापूर्वक इम्पोर्ट किए गए",
      exported: "प्रोडक्ट सफलतापूर्वक एक्सपोर्ट किए गए",
    },
    alerts: {
      fetchFailed: "प्रोडक्ट लाने में विफल",
      unexpected: "एक अप्रत्याशित त्रुटि हुई",
      createFailed: "प्रोडक्ट बनाने में विफल",
      updateFailed: "प्रोडक्ट अपडेट करने में विफल",
      deleteFailed: "प्रोडक्ट हटाने में विफल",
      exportFailed: "प्रोडक्ट एक्सपोर्ट करने में विफल",
      importFailed: "प्रोडक्ट इम्पोर्ट करने में विफल",
      noSelection: "जारी रखने के लिए कम से कम एक प्रोडक्ट चुनें",
      categoriesFetchFailed: "कैटेगरी लाने में विफल",
      hsnCodesFetchFailed: "एचएसएन कोड लाने में विफल",
    },
    import: {
      title: "प्रोडक्ट इम्पोर्ट करें",
      subtitle: "प्रोडक्ट को बल्क इम्पोर्ट करने के लिए एक्सेल फ़ाइल अपलोड करें",
      dropzone: {
        title: "अपनी एक्सेल फ़ाइल यहां ड्रैग और ड्रॉप करें",
        subtitle: "या फ़ाइलें ब्राउज़ करने के लिए क्लिक करें",
        fileTypes: ".xlsx, .xls फ़ाइलें स्वीकार करता है",
      },
      buttons: {
        downloadSample: "नमूना टेम्पलेट डाउनलोड करें",
        import: "प्रोडक्ट इम्पोर्ट करें",
        cancel: "रद्द करें",
      },
      progress: {
        uploading: "फ़ाइल अपलोड हो रही है...",
        processing: "प्रोडक्ट प्रोसेस हो रहे हैं...",
        completed: "इम्पोर्ट सफलतापूर्वक पूर्ण हुआ",
      },
      errors: {
        invalidFile: "कृपया एक मान्य एक्सेल फ़ाइल चुनें",
        noFile: "कृपया इम्पोर्ट करने के लिए एक फ़ाइल चुनें",
        invalidType: "कृपया एक मान्य एक्सेल फ़ाइल चुनें (.xlsx या .xls)",
        uploadFailed: "फ़ाइल अपलोड करने में विफल",
        processingFailed: "फ़ाइल प्रोसेस करने में विफल",
      },
      status: {
        ready: "इम्पोर्ट के लिए तैयार",
        uploading: "अपलोड हो रहा है...",
        success: "इम्पोर्ट सफल",
        error: "इम्पोर्ट विफल",
      },
      notifications: {
        success: "प्रोडक्ट सफलतापूर्वक इम्पोर्ट किए गए",
        error: "प्रोडक्ट इम्पोर्ट करने में विफल",
      },
      config: {
        templateHeaders: [
          "उपनाम",
          "प्रोडक्ट साइज़",
          "प्रोडक्ट वज़न",
          "मानक लंबाई",
          "प्रोडक्ट मोटाई",
          "कैटेगरी",
          "प्रोडक्ट नाम",
        ],
        templateFilename: "product_import_template",
        inputId: "product-import-input",
      },
    },
    export: {
      status: {
        success: "एक्सपोर्ट सफल",
        error: "एक्सपोर्ट विफल",
      },
      notifications: {
        success: "प्रोडक्ट सफलतापूर्वक एक्सपोर्ट किए गए",
        error: "प्रोडक्ट एक्सपोर्ट करने में विफल",
      },
    },
  },

  // Categories Management
  productCategories: {
    title: "कैटेगरी",
    description: "कैटेगरी वर्गीकरण और टैक्स दरें प्रबंधित करें",
    page: {
      header: {
        title: "कैटेगरी",
        subtitle: "अपनी कैटेगरी प्रबंधित करें",
      },
      stats: {
        totalCategories: "कुल कैटेगरी",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "त्रुटि",
      },
      accessDenied: {
        title: "पहुंच अस्वीकृत",
        message: "आपको इस पेज को देखने की अनुमति नहीं है।",
      },
    },

    table: {
      gstRate: "जीएसटी दर",
      columns: {
        select: "चुनें",
        name: "कैटेगरी नाम",
        actions: "कार्रवाई",
      },
      aria: {
        selectAll: "सभी कैटेगरी चुनें",
        selectCategory: "कैटेगरी चुनें",
        viewCategory: "कैटेगरी देखें",
        editCategory: "कैटेगरी संपादित करें",
        deleteCategory: "कैटेगरी हटाएं",
      },
      copy: {
        unnamedCategory: "अनाम कैटेगरी",
        noGstRate: "लागू नहीं",
      },
      emptyState: {
        title: "कोई कैटेगरी नहीं मिली",
        message: "अपनी पहली कैटेगरी बनाकर शुरुआत करें।",
      },
    },

    form: {
      name: "कैटेगरी नाम",
      tax: "टैक्स दर",
      selectTax: "टैक्स दर चुनें",
      namePlaceholder: "कैटेगरी नाम दर्ज करें",
      addTitle: "कैटेगरी जोड़ें",
      editTitle: "कैटेगरी संपादित करें",
      addDescription: "टैक्स जानकारी के साथ एक नई कैटेगरी बनाएं",
      editDescription: "कैटेगरी जानकारी अपडेट करें",
    },

    validation: {
      nameRequired: "कैटेगरी नाम आवश्यक है",
      nameMinLength: "कैटेगरी नाम कम से कम 2 अक्षरों का होना चाहिए",
      nameMaxLength: "कैटेगरी नाम 100 अक्षरों से अधिक नहीं हो सकता",
      taxRequired: "टैक्स चयन आवश्यक है",
    },

    actions: {
      add: "कैटेगरी जोड़ें",
      buttons: {
        add: "कैटेगरी जोड़ें",
        import: "इम्पोर्ट",
        export: "एक्सपोर्ट",
        refresh: "रिफ्रेश",
        deleteSelected: "चयनित हटाएं",
      },
      alerts: {
        noDataToExport: "एक्सपोर्ट करने के लिए कोई कैटेगरी नहीं",
      },
      searchPlaceholder: "कैटेगरी खोजें...",
      downloadPrefix: "product_categories",
    },

    search: {
      placeholder: "कैटेगरी खोजें...",
    },

    import: {
      title: "कैटेगरी इम्पोर्ट करें",
      description:
        "एक साथ कई कैटेगरी इम्पोर्ट करने के लिए एक्सेल फ़ाइल अपलोड करें।",
      nameDescription: "कैटेगरी का नाम",
      taxPercentage: "टैक्स प्रतिशत",
      taxPercentageDescription: "टैक्स प्रतिशत (जैसे, 18 प्रतिशत के लिए 18%)",
      instruction1: "1. टेम्पलेट फ़ाइल डाउनलोड करें",
      instruction2: "2. कैटेगरी नाम और टैक्स प्रतिशत भरें",
      instruction3: "3. फ़ाइल सहेजें और इसे यहां अपलोड करें",
      instruction4: "4. टैक्स प्रतिशत मौजूदा टैक्स रिकॉर्ड से मेल खाने चाहिए",
      instruction5: "5. प्रत्येक कैटेगरी नाम अद्वितीय होना चाहिए",
    },

    confirmDialog: {
      deleteTitle: "कैटेगरी हटाएं",
      deleteMessage:
        'क्या आप वाकई "{name}" को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।',
      bulkDeleteTitle: "कैटेगरी हटाएं",
      bulkDeleteMessage:
        "क्या आप वाकई {count} कैटेगरी हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।",
      confirmLabel: "हटाएं",
      loadingLabel: "हटाया जा रहा है...",
      errorTitle: "त्रुटि",
      buttons: {
        cancel: "रद्द करें",
      },
    },

    details: {
      title: "कैटेगरी विवरण",
      description: "कैटेगरी जानकारी देखें",
      id: "ID",
      name: "नाम",
      tax: "टैक्स",
      createdAt: "बनाया गया",
      updatedAt: "अपडेट किया गया",
      createdBy: "द्वारा बनाया गया",
      updatedBy: "द्वारा अपडेट किया गया",
      status: "स्थिति",
    },

    messages: {
      noCategories: "कोई कैटेगरी नहीं मिली",
      noSelection: "कोई कैटेगरी चयनित नहीं",
      createSuccess: "{name} कैटेगरी सफलतापूर्वक बनाई गई",
      updateSuccess: "{name} कैटेगरी सफलतापूर्वक अपडेट की गई",
      deleteSuccess: "{name} कैटेगरी सफलतापूर्वक हटाई गई",
      bulkDeleteSuccess: "{name} कैटेगरी सफलतापूर्वक हटाई गईं",
      importSuccess: "कैटेगरी सफलतापूर्वक इम्पोर्ट की गईं",
      exportSuccess: "कैटेगरी सफलतापूर्वक एक्सपोर्ट की गईं",
      templateDownloadSuccess: "टेम्पलेट सफलतापूर्वक डाउनलोड हुआ",
    },

    errors: {
      fetchFailed: "कैटेगरी लोड करने में विफल",
      createFailed: "कैटेगरी बनाने में विफल",
      updateFailed: "कैटेगरी अपडेट करने में विफल",
      deleteFailed: "कैटेगरी हटाने में विफल",
      noIdToDelete: "हटाने के लिए कोई कैटेगरी id उपलब्ध नहीं",
      bulkDeleteFailed: "कैटेगरी हटाने में विफल",
      importFailed: "कैटेगरी इम्पोर्ट करने में विफल",
      exportFailed: "कैटेगरी एक्सपोर्ट करने में विफल",
      templateDownloadFailed: "टेम्पलेट डाउनलोड करने में विफल",
    },
  },
  purchaseAdvice: {
    statuses: {
      unloading: "अनलोडिंग",
    },
    table: {
      columns: {
        poNumber: "पीओ नंबर",
        date: "तारीख",
        adviceNumber: "एडवाइस नंबर",
        supplierName: "सप्लायर",
        supervisorName: "सुपरवाइज़र",
        tallyName: "टैली नाम",
        vehicleNumber: "वाहन नंबर",
        totalQuantity: "कुल मात्रा",
        status: "स्थिति",
        statusActions: "स्थिति क्रियाएं",
        actions: "क्रियाएं",
      },
      tooltips: {
        view: "एडवाइस देखें",
        print: "एडवाइस प्रिंट करें",
        edit: "एडवाइस संपादित करें",
        delete: "एडवाइस हटाएं",
        confirm: "ऑर्डर की पुष्टि करें",
        assignSupervisor: "सुपरवाइज़र नियुक्त करें",
        startUnloading: "अनलोडिंग शुरू करें",
        completeLoading: "लोडिंग पूरी करें",
        completeUnloading: "अनलोडिंग पूरी करें",
        generateChallan: "चालान जेनरेट करें",
        markReceived: "प्राप्त के रूप में चिह्नित करें",
        complete: "पूर्ण के रूप में चिह्नित करें",
        adviceReceived: "एडवाइस प्राप्त हुई",
      },
      empty: {
        title: "कोई परचेज़ एडवाइस नहीं मिली",
        message: "दिखाने के लिए कोई परचेज़ एडवाइस रिकॉर्ड नहीं है।",
      },
    },
    actions: {
      searchPlaceholder: "परचेज़ एडवाइस खोजें...",
      filters: {
        status: "स्थिति",
        all: "सभी स्थिति",
      },
    },
    view: {
      header: {
        sectionTitle: "परचेज़ एडवाइस विवरण",
        fallbackTitle: "परचेज़ एडवाइस",
      },
      loading: "परचेज़ एडवाइस विवरण लोड हो रहा है...",
      empty: {
        title: "परचेज़ एडवाइस डेटा उपलब्ध नहीं है",
        message:
          "हम चयनित परचेज़ एडवाइस लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
      },
      fields: {
        poNumber: "पीओ नंबर",
        serialNumber: "सीरियल नंबर",
        adviceNumber: "एडवाइस नंबर",
        date: "तारीख",
        adviceDate: "एडवाइस तारीख",
        supplierName: "सप्लायर नाम",
        tallyName: "टैली नाम",
        paymentTerms: "भुगतान शर्तें",
        supplierInvoiceNumber: "सप्लायर इनवॉइस नंबर",
        supplierInvoiceDate: "सप्लायर इनवॉइस तारीख",
        totalQuantity: "कुल मात्रा",
        pendingQuantity: "लंबित मात्रा",
        presentShipping: "वर्तमान शिपिंग",
        totalAmount: "कुल राशि",
        vehicleNumber: "वाहन नंबर",
        status: "स्थिति",
        remark: "टिप्पणी",
        items: "आइटम",
      },
      items: {
        product: "प्रोडक्ट",
        size: "साइज़",
        unit: "यूनिट",
        length: "लंबाई",
        quantity: "मात्रा",
        actualQuantity: "वास्तविक मात्रा",
        actualPieces: "वास्तविक पीस",
        poItemQuantity: "पीओ आइटम मात्रा",
        standardLength: "मानक लंबाई",
        standardWeight: "मानक वज़न",
        price: "कीमत",
        amount: "राशि",
        remark: "टिप्पणी",
        hsn: "एचएसएन",
        gst: "जीएसटी %",
      },
      buttons: {
        close: "बंद करें",
      },
    },
    edit: {
      header: {
        title: "परचेज़ एडवाइस संपादित करें",
      },
      loading: "एडवाइस विवरण लोड हो रहा है...",
      error: "एडवाइस विवरण लाने में विफल",
      fields: {
        adviceDate: "एडवाइस तारीख",
        paymentTerms: "भुगतान शर्तें",
        supplierInvoiceNumber: "सप्लायर इनवॉइस नंबर",
        supplierInvoiceDate: "सप्लायर इनवॉइस तारीख",
        presentShipping: "वर्तमान शिपिंग",
        remark: "टिप्पणी",
        items: "आइटम",
        product: "प्रोडक्ट",
        size: "साइज़",
        quantity: "मात्रा",
        price: "कीमत",
      },
      buttons: {
        cancel: "रद्द करें",
        save: "बदलाव सहेजें",
      },
    },
  },
  purchaseChallan: {
    table: {
      columns: {
        poNumber: "पीओ नंबर",
        challanNumber: "चालान नंबर",
        tallyName: "टैली नाम",
        supplierInvoiceNumber: "सप्लायर इनवॉइस नंबर",
        supplierInvoiceDate: "सप्लायर इनवॉइस तारीख",
        totalAmount: "कुल राशि",
        status: "स्थिति",
        actions: "क्रियाएं",
      },
      tooltips: {
        view: "चालान देखें",
        print: "चालान प्रिंट करें",
        edit: "चालान संपादित करें",
      },
      empty: {
        title: "कोई परचेज़ चालान नहीं मिला",
        message: "दिखाने के लिए कोई परचेज़ चालान रिकॉर्ड नहीं है।",
      },
    },
    actions: {
      searchPlaceholder: "परचेज़ चालान खोजें...",
      filters: {
        all: "सभी स्थिति",
        status: "स्थिति",
      },
    },
    statuses: {
      pending: "लंबित",
      ready_for_challan: "चालान के लिए तैयार",
      confirmed: "पुष्टि की गई",
      advice_created: "एडवाइस बनाई गई",
      loading: "लोडिंग",
      loaded: "लोड किया गया",
      in_transit: "ट्रांज़िट में",
      received: "प्राप्त हुआ",
      completed: "पूर्ण हुआ",
      cancelled: "रद्द किया गया",
      rejected: "अस्वीकृत",
      challan_generated: "चालान जेनरेट हुआ",
    },
    view: {
      header: {
        sectionTitle: "परचेज़ चालान विवरण",
        fallbackTitle: "परचेज़ चालान",
      },
      loading: "परचेज़ चालान विवरण लोड हो रहा है...",
      empty: {
        title: "परचेज़ चालान डेटा उपलब्ध नहीं है",
        message:
          "हम चयनित परचेज़ चालान लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
      },
      fields: {
        poNumber: "पीओ नंबर",
        serialNumber: "सीरियल नंबर",
        challanNumber: "चालान नंबर",
        date: "तारीख",
        challanDate: "चालान तारीख",
        supplierName: "सप्लायर नाम",
        tallyName: "टैली नाम",
        discountType: "डिस्काउंट प्रकार",
        discountValue: "डिस्काउंट वैल्यू",
        discountAmount: "डिस्काउंट राशि",
        emptyTruckWeight: "खाली ट्रक वज़न",
        finalTruckWeight: "अंतिम ट्रक वज़न",
        truckWeight: "ट्रक वज़न",
        loadingCost: "लोडिंग लागत",
        freight: "भाड़ा",
        subtotal: "उप-योग",
        totalAmount: "कुल राशि",
        actualQuantity: "वास्तविक मात्रा",
        vehicleNumber: "वाहन नंबर",
        unloadedByName: "अनलोड करने वाला",
        remark: "टिप्पणी",

        supplierInvoiceNumber: "सप्लायर इनवॉइस नंबर",
        supplierInvoiceDate: "सप्लायर इनवॉइस तारीख",
        status: "स्थिति",
        items: "आइटम",
        item_product: "प्रोडक्ट",
        item_size: "साइज़",
        item_unit: "यूनिट",
        item_length: "लंबाई",
        item_qty: "मात्रा",
        item_price: "कीमत",
        item_amount: "राशि",
        item_remark: "टिप्पणी",
        item_hsn: "एचएसएन",
        item_gst_percentage: "जीएसटी %",
        item_gst_amount: "जीएसटी राशि",
        item_actual_qty: "वास्तविक मात्रा",
        item_invoice_qty: "इनवॉइस मात्रा",
        item_avg_weight: "औसत वज़न",
        item_avg_qty: "औसत मात्रा",
      },
      messages: {
        no_items_available: "कोई आइटम उपलब्ध नहीं है",
      },
      buttons: {
        close: "बंद करें",
      },
    },
    edit: {
      title: "परचेज़ एडवाइस चालान संपादित करें",
      notifications: {
        success: "परचेज़ चालान सफलतापूर्वक अपडेट हुआ",
      },
      errors: {
        fetchFailed: "चालान विवरण लाने में विफल",
        updateFailed: "परचेज़ चालान अपडेट करने में विफल",
      },
      fields: {
        emptyTruckWeight: {
          label: "खाली ट्रक वज़न",
          placeholder: "खाली ट्रक वज़न दर्ज करें",
        },
        finalTruckWeight: {
          label: "अंतिम ट्रक वज़न",
          placeholder: "अंतिम ट्रक वज़न दर्ज करें",
        },
        loadingCost: {
          label: "लोडिंग लागत",
          placeholder: "लोडिंग लागत दर्ज करें",
        },
        freight: {
          label: "भाड़ा",
          placeholder: "भाड़ा दर्ज करें",
        },
        vehicleNumber: {
          label: "वाहन नंबर",
          placeholder: "वाहन नंबर दर्ज करें",
        },
        remark: {
          label: "टिप्पणी",
          placeholder: "टिप्पणी दर्ज करें",
        },
      },
      buttons: {
        cancel: "रद्द करें",
        save: "बदलाव सहेजें",
        saving: "सहेजा जा रहा है...",
      },
    },
  },
  purchaseDaybook: {
    page: {
      header: {
        title: "परचेज़ डेबुक",
        subtitle: "दैनिक परचेज़ प्रविष्टियां देखें और प्रबंधित करें",
      },
    },
    actions: {
      searchPlaceholder: "डेबुक खोजें...",
      fromPlaceholder: "से तारीख",
      toPlaceholder: "तक तारीख",
      filters: {
        all: "सभी स्थितियां",
        status: "स्थिति",
      },
    },
    table: {
      headers: {
        serialNumber: "क्र. सं.",
        date: "तारीख",
        poNumber: "पीओ नंबर",
        tallyName: "टैली नाम",
        vehicleNumber: "वाहन नंबर",
        deliveryLocation: "डिलीवरी लोकेशन",
        orderFor: "किसके लिए ऑर्डर",
        unloadedBy: "अनलोड करने वाला",
        labourCount: "मज़दूर संख्या",
        actualQuantity: "वास्तविक मात्रा",
        totalAmount: "कुल राशि",
        paymentTerms: "भुगतान शर्तें",
        remark: "टिप्पणी",
        status: "स्थिति",
      },
      empty: {
        title: "कोई प्रविष्टि नहीं मिली",
        message: "अपनी खोज या फ़िल्टर समायोजित करने का प्रयास करें",
      },
      supplierInvoiceNumber: "सप्लायर इनवॉइस नंबर",
      supplierInvoiceDate: "सप्लायर इनवॉइस तारीख",
      presentShipping: "वर्तमान शिपिंग",
      remark: "टिप्पणी",
      items: "आइटम",
      product: "प्रोडक्ट",
      size: "साइज़",
      quantity: "मात्रा",
      price: "कीमत",
    },
    buttons: {
      cancel: "रद्द करें",
      save: "बदलाव सहेजें",
    },
  },
  invoicePurchase: {
    page: {
      header: {
        title: "परचेज़ एस्टिमेट",
        subtitle: "दैनिक परचेज़ इनवॉइस देखें और प्रबंधित करें",
      },
    },
    actions: {
      searchPlaceholder: "प्रविष्टियां खोजें...",
      fromPlaceholder: "से तारीख",
      toPlaceholder: "तक तारीख",
      filters: {
        all: "सभी स्थितियां",
        status: "स्थिति",
      },
    },
    table: {
      headers: {
        serialNumber: "क्र. सं.",
        date: "तारीख",
        poNumber: "पीओ नंबर",
        tallyName: "टैली नाम",
        vehicleNumber: "वाहन नंबर",
        deliveryLocation: "डिलीवरी लोकेशन",
        orderFor: "किसके लिए ऑर्डर",
        unloadedBy: "अनलोड करने वाला",
        labourCount: "मज़दूर संख्या",
        actualQuantity: "वास्तविक मात्रा",
        totalAmount: "कुल राशि",
        paymentTerms: "भुगतान शर्तें",
        remark: "टिप्पणी",
        status: "स्थिति",
      },
      empty: {
        title: "कोई प्रविष्टि नहीं मिली",
        message: "अपनी खोज या फ़िल्टर समायोजित करने का प्रयास करें",
      },
      supplierInvoiceNumber: "सप्लायर इनवॉइस नंबर",
      supplierInvoiceDate: "सप्लायर इनवॉइस तारीख",
      presentShipping: "वर्तमान शिपिंग",
      remark: "टिप्पणी",
      items: "आइटम",
      product: "प्रोडक्ट",
      size: "साइज़",
      quantity: "मात्रा",
      price: "कीमत",
    },
    buttons: {
      cancel: "रद्द करें",
      save: "बदलाव सहेजें",
    },
  },
};

// Alias orders for cleaner component usage and backward compatibility
STRINGS.orders = {
  forms: {
    ...STRINGS.common.forms,
    title: {
      add: "ऑर्डर जोड़ें",
      edit: "ऑर्डर संपादित करें",
    },
    loading: {
      customers: "ग्राहक लोड हो रहे हैं...",
      locations: "लोकेशन लोड हो रहे हैं...",
    },
    labels: {
      customerMode: "ग्राहक मोड",
    },
    errors: {
      customerRequired: "ग्राहक आवश्यक है",
      locationRequired: "डिलीवरी लोकेशन आवश्यक है",
      sourceTypeRequired: "स्रोत प्रकार आवश्यक है",
      warehouseRequired: "वेयरहाउस आवश्यक है",
      supplierRequired: "सप्लायर आवश्यक है",
      itemsRequired: "कम से कम एक आइटम आवश्यक है",
      itemProductSizeRequired: "प्रोडक्ट आवश्यक है",
      itemQuantityRequired: "मात्रा आवश्यक है",
      itemPiecesRequired: "पीस आवश्यक हैं (कितने दर्ज करें)",
      itemPriceRequired: "कीमत आवश्यक है",
      itemPriceInvalid: "मान्य कीमत दर्ज करें (0 या अधिक)",
      itemUnitRequired: "यूनिट आवश्यक है",
      itemLengthRequired: "लंबाई आवश्यक है",
      itemLengthInvalid: "लंबाई एक मान्य संख्या होनी चाहिए",
      freightInvalid: "भाड़ा एक मान्य संख्या होनी चाहिए",
      discountTypeInvalid: "इनपुट 'fixed' या 'percentage' होना चाहिए",
      discountValueInvalid: "डिस्काउंट वैल्यू एक मान्य संख्या होनी चाहिए",
      loadingCostInvalid: "लोडिंग लागत एक मान्य संख्या होनी चाहिए",
      gstOptionInvalid: "इनपुट 'with_gstn', 'percentage' या 'none' होना चाहिए",
      gstnNumberInvalid: "जीएसटीएन नंबर मान्य होना चाहिए",
      gstPercentageInvalid: "जीएसटी प्रतिशत एक मान्य संख्या होनी चाहिए",
      tcsPercentageInvalid: "टीसीएस प्रतिशत एक मान्य संख्या होनी चाहिए",
    },
    customerModes: {
      existing: "मौजूदा ग्राहक",
      new: "नया ग्राहक",
    },
    fields: {
      loadingCost: "लोडिंग लागत",
      loadingCostNote: "लोडिंग के लिए अतिरिक्त लागत",
      sourceType: {
        label: "स्रोत प्रकार",
        options: {
          warehouse: "वेयरहाउस",
          supplier: "सप्लायर",
        },
      },
      gstApplicable: "जीएसटी लागू",

      unit: "यूनिट",
      length: "लंबाई",
      itemRemark: "टिप्पणी",
    },
    placeholders: {
      selectSource: "स्रोत चुनें",
      selectWarehouse: "वेयरहाउस चुनें",
      selectSupplier: "सप्लायर चुनें",
      noSupplier: "कोई सप्लायर उपलब्ध नहीं है",
      selectProductSize: "प्रोडक्ट चुनें",
    },
    headings: {
      customerInformation: "ग्राहक जानकारी",
      orderDetails: "ऑर्डर विवरण",
    },
    buttons: {
      addItem: "आइटम जोड़ें",
      back: "वापस",
      save: "सहेजें",
      next: "आगे",
    },
  },
  view: {
    loading: "विवरण लोड हो रहा है...",
    header: {
      sectionTitle: "ऑर्डर विवरण",
      fallbackTitle: "ऑर्डर",
    },
    empty: {
      title: "कोई ऑर्डर नहीं मिला",
      message: "अनुरोधित ऑर्डर नहीं मिल सका।",
    },
    fields: {
      orderNumber: "ऑर्डर नंबर",
      date: "तारीख",
      customerName: "ग्राहक",
      tallyName: "टैली नाम",
      deliveryLocation: "डिलीवरी लोकेशन",
      expectedDeliveryDate: "अपेक्षित डिलीवरी",
      totalItems: "कुल आइटम",
      subtotal: "उप-योग",
      discountAmount: "डिस्काउंट",

      totalAmount: "कुल राशि",
      freight: "भाड़ा",
      remark: "टिप्पणी",
      status: "स्थिति",
      items: "आइटम",
      item_product: "प्रोडक्ट",
      item_size: "साइज़",
      item_qty: "मात्रा",
      item_price: "कीमत",
      item_amount: "राशि",
      item_hsn: "एचएसएन",
    },
    buttons: {
      close: "बंद करें",
    },
  },
  actions: {
    buttons: {
      add: "ऑर्डर जोड़ें",
    },
    searchPlaceholder: "ऑर्डर खोजें...",
  },
  statuses: {
    pending: "लंबित",
    confirmed: "पुष्टि की गई",
    loading: "लोडिंग",
    loaded: "लोड किया गया",
    dispatched: "डिस्पैच किया गया",
    delivered: "डिलीवर किया गया",
    cancelled: "रद्द किया गया",
    invoiced: "इनवॉइस किया गया",
  },
  table: {
    columns: {
      orderNumber: "ऑर्डर नंबर",
      date: "तारीख",
      customer: "ग्राहक",
      tallyName: "टैली नाम",
      deliveryLocation: "डिलीवरी लोकेशन",
      totalAmount: "कुल राशि",
      status: "स्थिति",
      actions: "क्रियाएं",
    },
    tooltips: {
      view: "ऑर्डर देखें",
      edit: "ऑर्डर संपादित करें",
      delete: "ऑर्डर हटाएं",
      assignPersonnel: "डिलीवरी कर्मी नियुक्त करें",
      startTruckLoading: "ट्रक लोडिंग शुरू करें",
      completeTruckLoading: "ट्रक लोडिंग पूरी करें",
    },
  },
};

STRINGS.inquiries = {
  table: {
    statuses: {
      pending: "लंबित",
      approved: "स्वीकृत",
      confirmed: "पुष्टि की गई",
      rejected: "अस्वीकृत",
      cancelled: "रद्द किया गया",
      completed: "पूर्ण हुआ",
      converted: "रूपांतरित",
    },
    columns: {
      select: "चुनें",
      customerName: "ग्राहक",
      gst: "जीएसटी",
      total: "कुल",
      status: "स्थिति",
      deliveryDate: "डिलीवरी तारीख",
      customerType: "ग्राहक प्रकार",
      priceUpdatedBy: "मूल्य अपडेट किया",
      actions: "क्रियाएं",
    },
    customerTypes: {
      new: "नया",
      existing: "मौजूदा",
    },
    tooltips: {
      view: "पूछताछ देखें",
      approve: "पूछताछ स्वीकृत करें",
      reject: "पूछताछ अस्वीकार करें",
      edit: "पूछताछ संपादित करें",
      delete: "पूछताछ हटाएं",
      history: "मूल्य परिवर्तन इतिहास",
    },
    aria: {
      selectAll: "सभी पूछताछ चुनें",
      selectInquiry: "इसके लिए पूछताछ चुनें",
      viewInquiry: "पूछताछ देखें",
      editInquiry: "पूछताछ संपादित करें",
      deleteInquiry: "पूछताछ हटाएं",
    },
    copy: {
      noCustomer: "अज्ञात ग्राहक",
      noQuantity: "0",
      noPrice: "₹0.00",
      yes: "हाँ",
      no: "नहीं",
      newCustomerLabel: "ग्राहक: नया",
      existingCustomerLabel: "ग्राहक: मौजूदा",
      statuses: {
        pending: "लंबित",
        approved: "स्वीकृत",
        rejected: "अस्वीकृत",
        cancelled: "रद्द किया गया",
        completed: "पूर्ण हुआ",
        converted: "रूपांतरित",
      },
    },
    emptyState: {
      title: "कोई पूछताछ नहीं मिली",
      message: "इस समय दिखाने के लिए कोई पूछताछ नहीं है।",
    },
  },
  view: {
    header: {
      sectionTitle: "पूछताछ विवरण",
      fallbackTitle: "पूछताछ",
    },
    fields: {
      customerName: "ग्राहक नाम",
      customerCompany: "कंपनी",
      deliveryLocation: "डिलीवरी लोकेशन",
      productName: "प्रोडक्ट नाम",
      productAlias: "प्रोडक्ट उपनाम",
      category: "कैटेगरी",
      hsnCode: "एचएसएन कोड",
      quantity: "मात्रा",
      units: "यूनिट",
      price: "कीमत",
      freight: "भाड़ा",
      subtotal: "उप-योग",
      gstAmount: "जीएसटी राशि",
      totalWithoutFreight: "कुल (भाड़े के बिना)",
      total: "कुल",
      expectedDeliveryDate: "अपेक्षित डिलीवरी",
      status: "स्थिति",
      remark: "टिप्पणी",
      isNewCustomer: "ग्राहक प्रकार",
      createdBy: "बनाया गया",
      items: "आइटम",
    },
    empty: {
      title: "पूछताछ डेटा उपलब्ध नहीं है",
      message: "हम चयनित पूछताछ लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
    },
    loading: "पूछताछ विवरण लोड हो रहा है...",
    copy: {
      noData: "लागू नहीं",
      noPrice: "₹0.00",
    },
  },
  actions: {
    buttons: {
      refresh: "रिफ्रेश",
      add: "पूछताछ जोड़ें",
    },
    searchPlaceholder: "पूछताछ खोजें...",
  },
};

STRINGS.aiAdvisor = {
  chat: {
    title: "AI चैट",
      subtitle: "अपने हाल के सेल्स और परचेज़ ट्रेंड के बारे में पूछें",
      newChat: "नई चैट",
      newChatTitle: "नई चैट",
      threadsHeader: "बातचीत",
      emptyThreads: "अभी तक कोई बातचीत नहीं। एक नई चैट शुरू करें।",
      emptyMessages: "शुरू करने के लिए एक प्रश्न पूछें।",
      inputPlaceholder: "अपने सेल्स/परचेज़ ट्रेंड के बारे में पूछें…",
      send: "भेजें",
      generatePlan: "प्लान जेनरेट करें",
      thinking: "आपके ट्रेंड का विश्लेषण किया जा रहा है…",
      rename: "नाम बदलें",
      delete: "हटाएं",
      deleteConfirm: "इस बातचीत को हटाएं? इसे पूर्ववत नहीं किया जा सकता।",
      windowWeeks: "पिछले N सप्ताह",
      subCategory: "सब कैटेगरी",
      subCategoryAll: "सभी सब कैटेगरी",
      subCategoryPlaceholder: "सभी सब कैटेगरी",
      privacyNote:
        "यह असिस्टेंट केवल आपके अनामीकृत सेल्स और परचेज़ ट्रेंड का विश्लेषण करता है। यह कभी भी ग्राहक, सप्लायर, कीमत या व्यक्तिगत डेटा नहीं देखता।",
      basedOn: "हाल के सेल्स/परचेज़ ट्रेंड पर आधारित",
      noData:
        "इस अवधि में विश्लेषण करने के लिए कोई चालान गतिविधि नहीं है। एक व्यापक तारीख सीमा आज़माएं।",
    },
    plan: {
      title: "परचेज़ प्लान",
      subtitle: "आपके हाल के सेल्स और परचेज़ ट्रेंड से एक AI खरीद प्लान",
      generate: "परचेज़ प्लान जेनरेट करें",
      generating: "आपका परचेज़ प्लान बनाया जा रहा है…",
      windowWeeks: "अवधि (सप्ताह)",
      subCategory: "सब कैटेगरी",
      subCategoryPlaceholder: "सभी सब कैटेगरी",
      asOf: "इस तारीख तक",
      productsAnalysed: "विश्लेषित प्रोडक्ट",
      empty: "आने वाले सप्ताहों के लिए खरीद सुझाव देखने के लिए एक प्लान जेनरेट करें।",
      advisoryNote:
        "निर्णय-सहायता, ऑटोपायलट नहीं। केवल पिछले N सप्ताह के चालान डेटा को दर्शाता है।",
      noData:
        "इस अवधि में विश्लेषण करने के लिए कोई चालान गतिविधि नहीं है। एक व्यापक अवधि आज़माएं।",
    },
    errors: {
      notEnabled: "इस एनवायरनमेंट के लिए AI एडवाइज़र सक्षम नहीं है।",
      forbidden: "आपके पास AI एडवाइज़र तक पहुंच नहीं है।",
      serviceBusy: "AI सेवा अभी व्यस्त है। कृपया कुछ देर बाद पुनः प्रयास करें।",
      generic: "कुछ गलत हो गया। कृपया पुनः प्रयास करें।",
    },
};

STRINGS.channelSettings = {
    pageTitle: "सेटिंग्स",
    pageSubtitle: "पूरे सिस्टम पर लागू होने वाली कॉन्फ़िगरेशन। केवल सुपर एडमिन।",
    title: "चैनल सेटिंग्स",
    subtitle: "नियंत्रित करें कि कौन से नोटिफिकेशन चैनल सक्रिय हैं। बदलाव सभी सर्वरों पर ~60 सेकंड के भीतर प्रभावी होते हैं।",
    superAdminOnlyBanner: "केवल देखने के लिए — इन सेटिंग्स को बदलने के लिए सुपर एडमिन पहुंच आवश्यक है।",
    channels: {
      email: {
        label: "ईमेल",
        description: "ईमेल के माध्यम से ऑर्डर पुष्टि, कोट्स और चालान नोटिफिकेशन भेजें",
      },
      whatsapp: {
        label: "WhatsApp",
        description: "WhatsApp के माध्यम से ग्राहक/सप्लायर फोन नंबरों पर नोटिफिकेशन भेजें",
      },
    },
    status: {
      enabled: "सक्षम",
      disabled: "अक्षम",
    },
    notifications: {
      updated: "{channel} चैनल {state}",
      updateFailed: "{channel} चैनल अपडेट करने में विफल",
    },
};

STRINGS.tallySettings = {
    title: "टैली सेटिंग्स",
    subtitle: "टैली इम्पोर्टर द्वारा सेल्स और परचेज़ चालान पोस्ट करते समय प्रति वेयरहाउस उपयोग किए जाने वाले अकाउंट लेजर और वाउचर प्रकार को कॉन्फ़िगर करें।",
    superAdminOnlyBanner: "केवल देखने के लिए — इन सेटिंग्स को बदलने के लिए सुपर एडमिन पहुंच आवश्यक है।",
    noWarehousesFound: "कोई वेयरहाउस नहीं मिला।",
    warehouseFallback: "वेयरहाउस {id}",
    warehouseFallbackGeneric: "वेयरहाउस",
    notConfiguredBadge: "कॉन्फ़िगर नहीं किया गया",
    fields: {
      salesAccountLedger: { label: "सेल्स अकाउंट लेजर", placeholder: "उदा. Sales-Chakan" },
      salesVoucherType: { label: "सेल्स वाउचर प्रकार", placeholder: "उदा. Sale CHK" },
      purchaseAccountLedger: { label: "परचेज़ अकाउंट लेजर", placeholder: "उदा. Purchase-Chakan" },
      purchaseVoucherType: { label: "परचेज़ वाउचर प्रकार", placeholder: "उदा. Purchase CHK" },
    },
    charCounterSuffix: "अनसेट करने के लिए साफ़ करें",
    ariaUnsavedChange: "असहेजा बदलाव",
    notifications: {
      saved: "{name} के लिए टैली सेटिंग्स सहेजी गईं",
      saveFailed: "टैली सेटिंग्स सहेजने में विफल",
    },
};

STRINGS.movementAnalytics = {
  labels: {
    purchased: "खरीदा गया",
    sold: "बेचा गया",
    netInflow: "शुद्ध आवक",
    netOutflow: "शुद्ध जावक",
    productsSuffix: "प्रोडक्ट",
    posSuffix: "पीओ",
    ordersSuffix: "ऑर्डर",
    busiestSku: "सबसे व्यस्त SKU · ",
    combinedInOut: "संयुक्त आवक+जावक",
    netInflowText: "शुद्ध आवक",
    netOutflowText: "शुद्ध जावक",
    kgIn: "किग्रा आवक",
    kgOut: "किग्रा जावक",
    subCategoryFallback: "सब कैटेगरी",
    exporting: "एक्सपोर्ट हो रहा है…",
    productFallback: "प्रोडक्ट",
  },
  headings: {
    productsSizesClickToSeeOrders: "प्रोडक्ट (साइज़) — ऑर्डर देखने के लिए क्लिक करें",
    productMovement: "प्रोडक्ट मूवमेंट",
  },
  emptyStates: {
    noProductsMoved: "इस अवधि में इस सब कैटेगरी के लिए कोई प्रोडक्ट मूव नहीं हुआ।",
    noRecordsInPeriod: "इस अवधि में कोई रिकॉर्ड नहीं है।",
    loadingProductMovement: "प्रोडक्ट मूवमेंट लोड हो रहा है...",
    noProductMovement: "चयनित फ़िल्टर के लिए कोई प्रोडक्ट मूवमेंट नहीं है।",
  },
  columns: {
    size: "साइज़",
    purchase: "परचेज़",
    sales: "सेल्स",
    net: "शुद्ध",
    poNo: "पीओ नं.",
    orderNo: "ऑर्डर नं.",
    product: "प्रोडक्ट",
    date: "तारीख",
    challans: "चालान",
    weight: "वज़न",
    purchaseKg: "परचेज़ (किग्रा)",
    salesKg: "सेल्स (किग्रा)",
    netKg: "शुद्ध (किग्रा)",
    rank: "#",
  },
  sections: {
    salesOrders: "सेल्स ऑर्डर",
    purchaseOrders: "परचेज़ ऑर्डर",
    flowOverview: "फ्लो अवलोकन",
    topMover: "टॉप मूवर",
    biggestBuySellGap: "सबसे बड़ा खरीद/बिक्री अंतर",
  },
  subtitles: {
    parentOrders: "मूल ऑर्डर और पीओ (केवल वज़न)",
    productSizeSplit: "प्रोडक्ट (साइज़) विभाजन — केवल वज़न",
    purchaseVsSalesPerProduct: "प्रति प्रोडक्ट परचेज़ बनाम सेल्स — विस्तार से देखने के लिए एक पंक्ति पर क्लिक करें",
  },
  sortOptions: {
    totalMovement: "कुल मूवमेंट",
    purchase: "परचेज़",
    sales: "सेल्स",
    biggestGap: "सबसे बड़ा अंतर",
  },
  cards: {
    purchaseInbound: "परचेज़ (आवक)",
    salesOutbound: "सेल्स (जावक)",
    netInflow: "शुद्ध आवक",
    netOutflow: "शुद्ध जावक",
    boughtMoreByWeight: "वज़न के हिसाब से अधिक खरीदा",
    soldMoreByWeight: "वज़न के हिसाब से अधिक बेचा",
    productsMoved: "मूव किए गए प्रोडक्ट",
    boughtLabel: "खरीदा",
    soldLabel: "बेचा",
  },
  filters: {
    from: "से",
    to: "तक",
    sortBy: "क्रमबद्ध करें",
    apply: "लागू करें",
  },
  buttons: {
    exportCsv: "CSV एक्सपोर्ट करें",
  },
};

STRINGS.supervisorAnalytics = {
  tabs: {
    summary: "सारांश",
    loadingActivity: "लोडिंग गतिविधि",
    unloadingActivity: "अनलोडिंग गतिविधि",
    workload: "कार्यभार",
    leaderboard: "लीडरबोर्ड",
  },
  pagination: {
    totalLabels: {
      loadingRecords: "लोडिंग रिकॉर्ड",
      unloadingRecords: "अनलोडिंग रिकॉर्ड",
      supervisors: "सुपरवाइज़र",
    },
  },
  leaderboard: {
    title: "सुपरवाइज़र लीडरबोर्ड",
    subtitle: "कुल पूर्ण गतिविधि के आधार पर रैंक किया गया",
    supervisorsCountSuffix: "सुपरवाइज़र",
  },
  columns: {
    supervisor: "सुपरवाइज़र",
    assigned: "असाइन किया गया",
    completed: "पूर्ण",
    totalWeight: "कुल वज़न",
    loadingCompleted: "लोडिंग ✓",
    unloadingCompleted: "अनलोडिंग ✓",
  },
  labels: {
    completedSuffix: "पूर्ण",
  },
  filters: {
    selectSupervisor: "सुपरवाइज़र चुनें",
    allStatuses: "सभी स्थितियाँ",
    loaded: "लोडेड",
    inTransit: "ट्रांज़िट में",
    delivered: "डिलीवर किया गया",
    pending: "लंबित",
    unloaded: "अनलोडेड",
    assigned: "असाइन किया गया",
    loading: "लोडिंग",
    status: "स्थिति",
    fromDate: "शुरू तारीख",
    toDate: "अंत तारीख",
    apply: "लागू करें",
    loadingText: "लोड हो रहा है...",
  },
  summaryCards: {
    loadingActivity: "लोडिंग गतिविधि",
    unloadingActivity: "अनलोडिंग गतिविधि",
    totalAssigned: "कुल असाइन किया गया",
    loadingOrdersAssigned: "असाइन किए गए लोडिंग ऑर्डर",
    unloadingOrdersAssigned: "असाइन किए गए अनलोडिंग ऑर्डर",
    completed: "पूर्ण",
    successfullyLoaded: "सफलतापूर्वक लोड किया गया",
    successfullyUnloaded: "सफलतापूर्वक अनलोड किया गया",
    pending: "लंबित",
    yetToBeLoaded: "अभी लोड होना बाकी",
    yetToBeUnloaded: "अभी अनलोड होना बाकी",
    weightLoaded: "लोड किया गया वज़न",
    weightUnloaded: "अनलोड किया गया वज़न",
  },
  workloadDetail: {
    statusAssigned: "असाइन किया गया",
    statusLoading: "लोडिंग",
    statusLoaded: "लोडेड",
    statusPending: "लंबित",
    statusInTransit: "ट्रांज़िट में",
    statusDelivered: "डिलीवर किया गया",
    tileAssigned: "असाइन किया गया",
    tileLoading: "लोडिंग",
    tileLoaded: "लोडेड",
    pendingLoadWt: "लंबित लोड वज़न",
    alreadyLoaded: "पहले से लोड किया गया",
    kg: "किग्रा",
    subOrders: "सब-ऑर्डर ({count})",
    noSubOrders: "कोई सब-ऑर्डर नहीं",
    loading: "लोड हो रहा है…",
  },
};

STRINGS.challanAnalytics = {
  tabs: {
    summary: "सारांश",
    products: "प्रोडक्ट",
    customers: "ग्राहक",
    dailyTrend: "दैनिक ट्रेंड",
    challanList: "चालान सूची",
  },
  pagination: {
    totalLabels: {
      products: "प्रोडक्ट",
      customers: "ग्राहक",
      challans: "चालान",
    },
  },
  cards: {
    totalChallans: "कुल चालान",
    totalWeight: "कुल वज़न",
    printed: "प्रिंट किया गया",
    subs: {
      successfullyDelivered: "सफलतापूर्वक डिलीवर किया गया",
      currentlyMoving: "वर्तमान में चल रहा है",
      loadedNotDispatched: "लोड किया गया, डिस्पैच नहीं हुआ",
      cancelledChallans: "रद्द किए गए चालान",
      awaitingAction: "कार्रवाई की प्रतीक्षा में",
    },
  },
  labels: {
    uniqueCustomersSuffix: "अद्वितीय ग्राहक",
    notPrintedSuffix: "प्रिंट नहीं किया गया",
    groupedByPrefix: "इसके अनुसार समूहित ",
    productSizeSku: "प्रोडक्ट साइज़ (SKU)",
    productCatalogue: "प्रोडक्ट कैटलॉग",
    productsCountSuffix: "प्रोडक्ट",
    customersCountSuffix: "ग्राहक",
    daysCountSuffix: "दिन",
    product: "प्रोडक्ट",
    challans: "चालान",
    qty: "मात्रा",
    weight: "वज़न",
  },
  sections: {
    overview: "अवलोकन",
    byStatus: "स्थिति के अनुसार",
    topProduct: "शीर्ष प्रोडक्ट",
  },
  tables: {
    productBreakdown: "प्रोडक्ट विवरण",
    customerBreakdown: "ग्राहक विवरण",
    dailyTrend: "दैनिक ट्रेंड",
  },
  subtitles: {
    perCustomer: "प्रति ग्राहक चालान सारांश",
    dailyActivity: "दिन-प्रतिदिन चालान गतिविधि",
  },
  columns: {
    skuSize: "SKU / साइज़",
    qtyUnits: "मात्रा / यूनिट",
    weightT: "वज़न (टन)",
  },
  emptyStates: {
    loadingProducts: "प्रोडक्ट लोड हो रहे हैं...",
    noProductData: "चयनित फ़िल्टर के लिए कोई प्रोडक्ट डेटा नहीं मिला।",
    loadingCustomers: "ग्राहक लोड हो रहे हैं...",
    noCustomerData: "चयनित फ़िल्टर के लिए कोई ग्राहक डेटा नहीं मिला।",
    loadingDaily: "दैनिक डेटा लोड हो रहा है...",
    noDailyData: "चयनित फ़िल्टर के लिए कोई दैनिक डेटा नहीं मिला।",
  },
};

STRINGS.salesAnalytics = deepMerge(STRINGS.salesAnalytics, {
  tabs: {
    summary: "सारांश",
    orders: "ऑर्डर",
    dailyTrend: "दैनिक ट्रेंड",
    leaderboard: "लीडरबोर्ड",
    deliveries: "डिलीवरी",
  },
  pagination: {
    totalLabels: {
      orders: "ऑर्डर",
      subOrders: "सब-ऑर्डर",
    },
  },
  sections: {
    byWarehouse: "वेयरहाउस के अनुसार",
    byCategory: "कैटेगरी के अनुसार",
    salesTrend: "सेल्स ट्रेंड",
    topProducts: "शीर्ष प्रोडक्ट",
    orderStatusDistribution: "ऑर्डर स्थिति वितरण",
  },
  subtitles: {
    quantityDistribution: "मात्रा वितरण",
    byQuantityOrdered: "इस अवधि में ऑर्डर की गई मात्रा के अनुसार",
    dailyActivity: "दिन-प्रतिदिन सेल्स गतिविधि",
  },
  cards: {
    totalOrders: "कुल ऑर्डर",
    totalWeight: "कुल वज़न",
    totalQuantity: "कुल मात्रा",
    subs: {
      inSelectedPeriod: "चयनित अवधि में",
      dispatchedWeight: "डिस्पैच किया गया वज़न",
      unitsOrdered: "ऑर्डर की गई यूनिट",
    },
  },
  filters: {
    allSalesStaff: "सभी सेल्स स्टाफ",
  },
  chart: {
    weightTon: "वज़न (टन)",
  },
  emptyStates: {
    loadingDeliveries: "डिलीवरी लोड हो रही हैं…",
    noActiveDeliverySubOrders: "कोई सक्रिय डिलीवरी सब-ऑर्डर नहीं",
  },
});

STRINGS.deliveryAnalytics = deepMerge(STRINGS.deliveryAnalytics, {
  sections: {
    challanOverview: "चालान अवलोकन",
    challanTrend: "चालान ट्रेंड",
    deliveryPerformance: "डिलीवरी प्रदर्शन",
    personnelPerformance: "कर्मचारी प्रदर्शन",
  },
  subtitles: {
    orderFulfilmentBreakdown: "ऑर्डर पूर्ति विवरण",
    supervisorsActivity: "इस अवधि में डिलीवरी सुपरवाइज़र की गतिविधि",
  },
  cards: {
    totalChallans: "कुल चालान",
    totalWeight: "कुल वज़न",
    subs: {
      inSelectedPeriod: "चयनित अवधि में",
      dispatchedWeight: "डिस्पैच किया गया वज़न",
    },
  },
  labels: {
    deliveryRate: "डिलीवरी दर",
    deliveries: "डिलीवरी",
  },
  chart: {
    weightTon: "वज़न (टन)",
  },
});

STRINGS.inventoryAnalytics = deepMerge(STRINGS.inventoryAnalytics, {
  sections: {
    warehouseSummary: "वेयरहाउस सारांश",
    warehouseDetails: "वेयरहाउस विवरण",
  },
  subtitles: {
    warehouseDetails: "वेयरहाउस के अनुसार स्टॉक, लंबित ऑर्डर और अलर्ट",
  },
  labels: {
    stock: "स्टॉक",
    available: "उपलब्ध",
    lowStock: "कम स्टॉक",
    outOfStock: "स्टॉक में नहीं",
    warehousesCountSuffix: "वेयरहाउस",
  },
  charts: {
    lowStock: {
      subtitle: "न्यूनतम स्टॉक स्तर से नीचे के प्रोडक्ट",
    },
    warehouseStock: {
      subtitle: "प्रति वेयरहाउस स्टॉक मात्रा बनाम लंबित ऑर्डर",
    },
  },
});

STRINGS.assignedToChallan = {
  page: {
    title: "ऑपरेशंस",
    subtitle: "ट्रैक करें कि सुपरवाइज़र असाइनमेंट और चालान जनरेशन के बीच सब-ऑर्डर कितनी देर रहते हैं — लाइव बैकलॉग के साथ ऐतिहासिक अवधि आँकड़े। वेयरहाउस साइडबार से चुना जाता है।",
  },
  tabs: {
    liveBacklog: "लाइव बैकलॉग",
    durationStats: "अवधि आँकड़े",
  },
  pagination: {
    totalLabels: {
      subOrders: "सब-ऑर्डर",
    },
  },
  cards: {
    completed: "पूर्ण",
    subOrdersChallaned: "चालान किए गए सब-ऑर्डर",
    average: "औसत",
    meanAssignToChallan: "औसत असाइन → चालान",
    median: "माध्यिका",
    outlierResistant: "आउटलायर-रोधी",
    fastest: "सबसे तेज़",
    shortestWindow: "सबसे छोटी अवधि",
    slowest: "सबसे धीमा",
    longestWindow: "सबसे लंबी अवधि",
    totalPending: "कुल लंबित",
    awaitingChallan: "असाइन किया गया, चालान की प्रतीक्षा में",
    over1Day: "1 दिन से अधिक",
    waiting24h: "24 घंटे से अधिक प्रतीक्षा",
    over7Days: "7 दिन से अधिक",
    criticallyStuck: "गंभीर रूप से अटका हुआ",
  },
  sections: {
    durationOverview: "अवधि अवलोकन",
    bySupervisor: "सुपरवाइज़र के अनुसार",
    dailyTrend: "दैनिक ट्रेंड",
    backlogOverview: "बैकलॉग अवलोकन",
    agingBuckets: "एजिंग बकेट",
  },
  subtitles: {
    avgDurationPerSupervisor: "प्रति सुपरवाइज़र औसत अवधि",
    perChallanDate: "प्रति चालान तारीख औसत अवधि",
  },
  labels: {
    firstAssignmentToChallan: "(पहला असाइनमेंट → चालान जनरेट)",
  },
  values: {
    unassigned: "असाइन नहीं किया गया",
  },
  emptyStates: {
    loadingDuration: "अवधि आँकड़े लोड हो रहे हैं...",
    noBreakdown: "चयनित अवधि के लिए कोई विवरण डेटा नहीं।",
  },
  buckets: {
    today: "आज (<1दि)",
    oneToTwoDays: "1–2 दिन",
    twoToSevenDays: "2–7 दिन",
    overSevenDays: "7 दिन से अधिक",
  },
  filters: {
    unitType: "यूनिट प्रकार",
    groupBy: "समूहित करें",
    options: {
      all: "सभी",
      kg: "किग्रा",
      meter: "मीटर",
      none: "कोई नहीं",
      day: "दिन",
      supervisor: "सुपरवाइज़र",
    },
  },
};

STRINGS.vehicleTracking = {
  columns: {
    orderId: "ऑर्डर ID",
    subOrderId: "सब ऑर्डर ID",
    vehicleNumber: "वाहन नंबर",
    supervisorName: "सुपरवाइज़र नाम",
    location: "लोकेशन",
    orderDate: "ऑर्डर तारीख",
  },
  emptyStates: {
    noData: "कोई वाहन ट्रैकिंग डेटा नहीं मिला",
    noTracking: "इस वाहन के लिए कोई ट्रैकिंग जानकारी उपलब्ध नहीं है।",
  },
  emptyMessage: "कोई वाहन ट्रैकिंग डेटा नहीं मिला",
  values: {
    notAssigned: "असाइन नहीं किया गया",
  },
  filters: {
    searchPlaceholder: "वाहन नंबर खोजें...",
  },
  modal: {
    title: "वाहन ट्रैकिंग विवरण",
    orderPrefix: "ऑर्डर #",
    subOrderPrefix: "सब-ऑर्डर #",
  },
  sections: {
    vehiclePersonnel: "वाहन और कर्मचारी",
    loadingWeights: "लोडिंग वज़न",
    orderContext: "ऑर्डर संदर्भ",
    customerDelivery: "ग्राहक और डिलीवरी",
    materialWeights: "सामग्री वज़न",
    associatedDocuments: "संबंधित दस्तावेज़",
  },
  labels: {
    vehicleNumber: "वाहन नंबर",
    driverContact: "ड्राइवर संपर्क",
    supervisor: "सुपरवाइज़र",
    emptyTruckWeight: "खाली ट्रक वज़न",
    finalTruckWeight: "अंतिम ट्रक वज़न",
    vehicleRemark: "वाहन टिप्पणी",
    orderNumber: "ऑर्डर नंबर",
    subOrderNumber: "सब ऑर्डर नंबर",
    orderDate: "ऑर्डर तारीख",
    orderStatus: "ऑर्डर स्थिति",
    subOrderStatus: "सब ऑर्डर स्थिति",
    totalAmount: "कुल राशि",
    customerName: "ग्राहक नाम",
    tallyName: "टैली नाम",
    deliveryLocation: "डिलीवरी लोकेशन",
    totalOrderWeight: "कुल ऑर्डर वज़न",
    subOrderWeight: "सब ऑर्डर वज़न",
    unitType: "यूनिट प्रकार",
    challanNumber: "चालान नंबर",
    challanDate: "चालान तारीख",
    invoiceNumber: "इनवॉइस नंबर",
    invoiceDate: "इनवॉइस तारीख",
  },
};

STRINGS.dashboard = deepMerge(STRINGS.dashboard, {
  title: "डैशबोर्ड",
  orderStatus: {
    title: "ऑर्डर स्थिति",
    breakdownTitle: "ऑर्डर स्थिति विवरण",
    totalPrefix: "कुल: ",
    ordersSuffix: "ऑर्डर",
  },
  kpi: {
    sections: {
      todayActivity: "आज की गतिविधि",
      thisMonth: "इस महीने",
      operations: "ऑपरेशंस",
      inventoryAlerts: "इन्वेंटरी अलर्ट",
    },
    labels: {
      weightDispatched: "डिस्पैच किया गया वज़न",
      ordersPlaced: "दिए गए ऑर्डर",
      delivered: "डिलीवर किया गया",
      orders: "ऑर्डर",
      pendingOrders: "लंबित ऑर्डर",
      inTransit: "ट्रांज़िट में",
      expectedDeliveries: "अपेक्षित डिलीवरी",
      pendingPurchaseOrders: "लंबित परचेज़ ऑर्डर",
      lowStockAlerts: "कम स्टॉक अलर्ट",
      outOfStock: "स्टॉक में नहीं",
    },
    subs: {
      today: "आज",
      month: "महीना",
      awaitingDispatch: "डिस्पैच की प्रतीक्षा में",
      onTheWay: "रास्ते में",
      incomingToday: "आज आने वाला",
      purchase: "परचेज़",
      belowMinimum: "न्यूनतम से नीचे",
      itemsUnavailable: "आइटम अनुपलब्ध",
    },
    trend: {
      vsLastMonth: "पिछले महीने की तुलना में {value}%",
    },
  },
  charts: {
    salesTrend: {
      title: "सेल्स ट्रेंड",
      noData: "कोई डेटा उपलब्ध नहीं",
      orders: "ऑर्डर",
    },
  },
});

STRINGS.customerWarehouses = {
  headings: {
    title: "ग्राहक वेयरहाउस",
    fallback: "वेयरहाउस",
    details: "वेयरहाउस विवरण",
    updateDetails: "वेयरहाउस विवरण अपडेट करें",
    optionalStep: "वैकल्पिक चरण",
    editWarehouse: "वेयरहाउस संपादित करें",
    assign: "ग्राहक वेयरहाउस असाइन करें",
  },
  labels: {
    useCustomerAddress: "ग्राहक का पता उपयोग करें",
    needsDetails: "कुछ विवरण आवश्यक",
    useExistingLocation: "मौजूदा डिलीवरी लोकेशन का उपयोग करें",
    setAsDefault: "डिफ़ॉल्ट वेयरहाउस के रूप में सेट करें",
    countLabel: "वेयरहाउस{s}",
    stateCity: "राज्य / शहर",
  },
  buttons: {
    addWarehouse: "वेयरहाउस जोड़ें",
    setAsDefault: "डिफ़ॉल्ट के रूप में सेट करें",
    viewOnMap: "मैप पर देखें",
    openInMaps: "Google Maps में खोलें",
    saveAndAddAnother: "सहेजें और एक और जोड़ें",
    updateWarehouse: "वेयरहाउस अपडेट करें",
    saveWarehouse: "वेयरहाउस सहेजें",
  },
  messages: {
    loading: "वेयरहाउस लोड हो रहे हैं...",
    noWarehousesFound: "कोई वेयरहाउस नहीं मिला",
    noWarehousesAssigned: "इस ग्राहक को अभी तक कोई वेयरहाउस असाइन नहीं किया गया है।",
    fromLocation: "(लोकेशन से)",
    selectToAutofill: "— पता विवरण स्वतः भरने के लिए एक लोकेशन चुनें",
    enterManually: "— लोकेशन विवरण मैन्युअल रूप से दर्ज करें",
    addedToList: "{name} वेयरहाउस सूची में जोड़ दिया गया है",
    assigned: "{name} वेयरहाउस सफलतापूर्वक असाइन किया गया है",
    updated: "{name} वेयरहाउस सफलतापूर्वक अपडेट किया गया है",
    deleteConfirm: 'क्या आप वाकई वेयरहाउस "{name}" को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।',
  },
  errors: {
    updateFailed: "वेयरहाउस अपडेट करने में विफल",
    createFailed: "वेयरहाउस बनाने में विफल",
    loadFailed: "वेयरहाउस लोड करने में विफल",
  },
};

STRINGS.bulkInventoryEdit = {
    dialogs: {
      deleteInventoryTitle: "इन्वेंटरी आइटम हटाएं",
      deleteInventoryMessage: "क्या आप वाकई {name} को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
      deleteFailedTitle: "हटाना विफल",
      deleteFailedMessage: "इन्वेंटरी हटाने में विफल",
      saveFailedTitle: "सहेजना विफल",
      saveFailedMessage: "इन्वेंटरी सहेजने में विफल",
      adjustFailedTitle: "समायोजन विफल",
      adjustFailedMessage: "स्टॉक समायोजित करने में विफल",
      partialUpdateErrorsTitle: "आंशिक अपडेट त्रुटियाँ",
      updateFailedTitle: "अपडेट विफल",
      updateFailedMessage: "वेयरहाउस वितरण अपडेट करने में विफल",
      transferFailedTitle: "ट्रांसफर विफल",
      transferFailedMessage: "स्टॉक ट्रांसफर करने में विफल",
      saveErrorsTitle: "सहेजने की त्रुटियाँ",
      partialSaveTitle: "आंशिक सहेजना",
    },
    fallbacks: {
      inventory: "इन्वेंटरी {id}",
      warehouse: "वेयरहाउस {id}",
      product: "प्रोडक्ट {id}",
      subCategory: "सब कैटेगरी {id}",
    },
    footer: {
      dirtyCount: "{products} प्रोडक्ट में {count} बदलाव",
      savingProgress: "{total} में से {current} सहेजा जा रहा है...",
      hiddenHint: "संपादन वाले {n} प्रोडक्ट वर्तमान फ़िल्टर द्वारा छिपे हैं — समीक्षा के लिए फ़िल्टर साफ़ करें।",
      footerHint: "प्रति वेयरहाउस स्टॉक, न्यूनतम-स्टॉक और लंबित मान संपादित करें, फिर सभी सहेजें पर क्लिक करें।",
    },
    buttons: {
      bulkEdit: "बल्क संपादन",
      exitBulkEdit: "बल्क संपादन से बाहर निकलें",
      saveAll: "सभी सहेजें",
      discard: "खारिज करें",
    },
    discardConfirm: {
      title: "बदलाव खारिज करें?",
      message: "आपके पास बिना सहेजे बदलाव हैं। क्या आप वाकई बल्क संपादन मोड से बाहर निकलना चाहते हैं?",
    },
    messages: {
      allSuccess: "{products} प्रोडक्ट में {count} बदलाव सहेजे गए।",
      allFailed: "कोई भी बदलाव सहेजने में विफल।",
      partialSaveBody: "{total} में से {succeeded} प्रोडक्ट सहेजे गए। विफल:\n\n",
    },
    pagination: {
      pageOfTotal: "पृष्ठ {current} / {total} — कुल {count}",
      previous: "पिछला",
      next: "अगला",
    },
    perWarehouseTable: {
      warehouse: "वेयरहाउस",
      stockQty: "स्टॉक मात्रा",
      minStock: "न्यूनतम स्टॉक",
      pendingOrder: "लंबित ऑर्डर",
      lowStock: "कम स्टॉक",
      noWarehouses: "इस प्रोडक्ट के लिए कोई वेयरहाउस डेटा उपलब्ध नहीं है।",
    },
};

STRINGS.common.export = {
  title: "एक्सपोर्ट",
  fromDate: "शुरू तारीख",
  toDate: "अंत तारीख",
  format: "फ़ॉर्मेट",
  csv: "CSV",
  excel: "Excel",
  exporting: "एक्सपोर्ट हो रहा है...",
  exportButton: "एक्सपोर्ट",
  cancel: "रद्द करें",
};

STRINGS.forgotPassword = {
  errors: {
    emailRequired: "ईमेल आवश्यक है",
    invalidEmail: "कृपया एक मान्य ईमेल पता दर्ज करें",
  },
  messages: {
    resetLinkSent: "पासवर्ड रीसेट लिंक आपके ईमेल पर भेज दिया गया है।",
    resetLinkFailed: "पासवर्ड रीसेट ईमेल भेजने में विफल।",
  },
  placeholders: {
    email: "अपना ईमेल पता दर्ज करें",
  },
  buttons: {
    sendResetLink: "रीसेट लिंक भेजें",
  },
};

STRINGS.deliveryChallans.table.tooltips = {
  ...STRINGS.deliveryChallans.table.tooltips,
  printChallan: "चालान प्रिंट करें",
  sendChallanEmail: "ईमेल के माध्यम से चालान भेजें",
  sendChallanWhatsApp: "WhatsApp के माध्यम से चालान भेजें",
  auditLogs: "ऑडिट लॉग",
};

STRINGS.deliveryChallans.actions.confirmLabels = {
  yesDelivered: "हाँ, डिलीवर किया गया",
};

STRINGS.inquiries.errors = {
  failedToLoad: "पूछताछ लोड करने में विफल",
  failedToApprove: "पूछताछ स्वीकृत करने में विफल",
  approvalErrorTitle: "स्वीकृति त्रुटि",
  failedToReject: "पूछताछ अस्वीकार करने में विफल",
  rejectionErrorTitle: "अस्वीकृति त्रुटि",
  deleteFailed: "हटाना विफल",
  deletionErrorTitle: "हटाने की त्रुटि",
  failedToConvert: "पूछताछ परिवर्तित करने में विफल",
  conversionErrorTitle: "परिवर्तन त्रुटि",
  operationFailed: "ऑपरेशन विफल",
  formErrorTitle: "फ़ॉर्म त्रुटि",
  successTitle: "सफल",
};

STRINGS.deliveryOrders.errors = {
  perPieceWeightRequired: '"{product}" के लिए प्रति-पीस वज़न डालें — इसका स्टॉक किलोग्राम में रखा जाता है, इसके बिना चालान कोई स्टॉक नहीं घटाएगा।',
  errorTitle: "त्रुटि",
  failedToFetchOrderDetails: "ऑर्डर विवरण लाने में विफल",
  failedToGenerateChallan: "चालान जनरेट करने में विफल",
};

STRINGS.deliveryChallans.errors = {
  errorTitle: "त्रुटि",
  exportFailed: "एक्सपोर्ट विफल",
  failedToFetchOrderDetails: "ऑर्डर विवरण लाने में विफल",
  paymentsExceedTotal: "दर्ज रसीदें (₹{{paid}}) अब चालान कुल (₹{{total}}) से अधिक हैं। उन्हें Payments Tracking से ठीक करें।",
};
STRINGS.deliveryChallans.labels = STRINGS.deliveryChallans.labels || {};
STRINGS.deliveryChallans.labels.vehicleNumber = "वाहन नंबर";
STRINGS.deliveryChallans.export = STRINGS.deliveryChallans.export || {};
STRINGS.deliveryChallans.export.title = "डिलीवरी चालान एक्सपोर्ट करें";

STRINGS.profile.superDelete = {
  successMessage: "सभी ऑर्डर और परचेज़ ऑर्डर सफलतापूर्वक हटा दिए गए।",
  failureMessage: "सुपर डिलीट करने में विफल।",
};

STRINGS.users.errors = {
  errorTitle: "त्रुटि",
  noUserIdToDelete: "हटाने के लिए कोई यूज़र id उपलब्ध नहीं",
  exportFailed: "एक्सपोर्ट विफल",
  exportErrorTitle: "एक्सपोर्ट त्रुटि",
};

STRINGS.purchaseChallan.errors = STRINGS.purchaseChallan.errors || {};
STRINGS.purchaseChallan.errors.exportFailed = "एक्सपोर्ट विफल";

STRINGS.productCatalogue.errors = STRINGS.productCatalogue.errors || {};
STRINGS.productCatalogue.errors.noProductIdToDelete = "हटाने के लिए कोई प्रोडक्ट id उपलब्ध नहीं";

STRINGS.productSizes.errors = STRINGS.productSizes.errors || {};
STRINGS.productSizes.errors.noProductSizeIdToDelete = "हटाने के लिए कोई प्रोडक्ट साइज़ id उपलब्ध नहीं";

STRINGS.superAdminDelete = {
  warning: "चेतावनी: यह सभी {typeLabel} और उनके संबंधित उप-इकाइयों को हटा देगा। यह क्रिया पूर्ववत नहीं की जा सकती।",
  step1Label: "चरण 1: सुपर एडमिन पासवर्ड",
  passwordPlaceholder: "जारी रखने के लिए पासवर्ड दर्ज करें",
  confirmDeletionHeading: "हटाने की पुष्टि करें",
  typeToConfirmPrefix: "कृपया टाइप करें ",
  typeToConfirmWord: "DELETE",
  typeToConfirmSuffix: " नीचे दिए गए बॉक्स में हटाने की पुष्टि करने के लिए ",
  copyPasteDisabledNote: "(कॉपी-पेस्ट अक्षम है)",
  confirmPlaceholder: 'यहाँ "DELETE" टाइप करें',
  titleStep1: "सुपर एडमिन डिलीट (1/2)",
  titleStep2: "सुपर एडमिन डिलीट (2/2)",
  nextButton: "अगला",
  confirmDeleteAllButton: "सभी हटाने की पुष्टि करें",
  cancelButton: "रद्द करें",
};

STRINGS.auditLogs = {
  hideDetails: "विवरण छिपाएं",
  showDetails: "विवरण दिखाएं",
  entityLabels: {
    order: "ऑर्डर",
    purchase_order: "परचेज़ ऑर्डर",
    delivery_sub_order: "डिलीवरी सब-ऑर्डर",
    purchase_advice: "परचेज़ एडवाइस",
    inquiry: "पूछताछ",
  },
  heading: "ऑडिट लॉग",
  tabs: {
    auditLog: "ऑडिट लॉग",
    timeline: "टाइमलाइन",
  },
  failedToLoad: "लॉग लोड करने में विफल: {error}",
  noLogsFound: "कोई लॉग नहीं मिला",
  close: "बंद करें",
  entry: "प्रविष्टि",
  entries: "प्रविष्टियाँ",
  yes: "हाँ",
  no: "नहीं",
  itemsCount: "{count} आइटम{plural}",
  itemFallback: "आइटम",
  productFallback: "प्रोडक्ट #{id}",
  userFallback: "यूज़र #{id}",
  valueLabel: "मान",
  noFieldChanges: "कोई फ़ील्ड बदलाव नहीं",
  qtyLabel: "मात्रा",
  sections: {
    added: "जोड़ा गया",
    removed: "हटाया गया",
    modified: "संशोधित",
  },
};

// Shared text for the downloadable import templates' "Instructions" sheet
// (src/utils/xlsxExport.js) and the per-column notes shown there.
STRINGS.importTemplate = {
  sheetName: "निर्देश",
  heading: "इस टेम्पलेट को कैसे भरें",
  intro1: '• अपना डेटा "Sheet1" टैब में भरें, पंक्ति 2 से शुरू करते हुए (पंक्ति 1 हेडर को जस का तस रखें)।',
  intro2: '• "आवश्यक" कॉलम खाली नहीं छोड़े जाने चाहिए।',
  intro3: '• "अनुमत मान" कॉलम केवल सूचीबद्ध मानों को स्वीकार करते हैं — उन्हें ठीक वैसे ही कॉपी करें (केस-संवेदी)।',
  columns: {
    column: "कॉलम",
    required: "आवश्यक",
    example: "उदाहरण",
    allowedValues: "अनुमत मान",
    notes: "टिप्पणियाँ",
  },
  yes: "हाँ",
  no: "नहीं",
  notes: {
    // Customers
    customerTypeAllowed: "अनुमत मानों में से ठीक एक (केस-संवेदी)।",
    phoneTenDigit: "10-अंकीय फ़ोन नंबर।",
    gstin: "15-अक्षरों का GSTIN. वैकल्पिक — न हो तो खाली छोड़ें।",
    pinCode: "6-अंकीय पिन कोड।",
    email: "मान्य ईमेल पता।",
    creditPeriod: "दिनों की संख्या (पूर्णांक)।",
    // Product sizes / catalogue
    weightKg: "किग्रा में वज़न (संख्या)।",
    weightKgPerPiece: "किग्रा में वज़न (संख्या)। मानक लंबाई पर एक पीस का वज़न।",
    standardLengthM: "मीटर में मानक लंबाई (संख्या)।",
    wholeNumber: "पूर्ण संख्या (पूर्णांक)।",
    coilTracked: "सामान्य स्टॉक के लिए खाली या FALSE रखें। TRUE रखने पर स्टॉक अलग-अलग कॉइल रोल के रूप में रहेगा — केवल तभी जब आपकी कंपनी के लिए कॉइल ट्रैकिंग चालू हो।",
    matchWarehouse: "किसी ऐसे सक्रिय वेयरहाउस के नाम से मेल खाना चाहिए जो आपको असाइन है। केस से फ़र्क नहीं पड़ता।",
    matchProduct: "किसी मौजूदा प्रोडक्ट के Alias Name से मेल खाना चाहिए। केस से फ़र्क नहीं पड़ता।",
    stockAbsolute: "केवल सामान्य प्रोडक्ट के लिए। पूरे किलो में क्लोज़िंग बैलेंस, कभी ऋणात्मक नहीं — यह मौजूदा स्टॉक को बदल देता है, जोड़ता नहीं। मौजूदा बैलेंस रखना हो तो खाली छोड़ें (वेयरहाउस में नया प्रोडक्ट 0 से शुरू होगा); खाली करने के लिए 0 लिखें। कॉइल-ट्रैक्ड प्रोडक्ट के लिए हमेशा खाली।",
    coilBatch: "केवल कॉइल-ट्रैक्ड प्रोडक्ट के लिए। हर फ़िज़िकल कॉइल की एक पंक्ति; टैग पर लिखा बैच नंबर। प्रति प्रोडक्ट प्रति वेयरहाउस यूनिक होना चाहिए।",
    coilWeight: "केवल कॉइल-ट्रैक्ड प्रोडक्ट के लिए। इस कॉइल का वज़न पूरे किलो में।",
    coilLength: "केवल कॉइल-ट्रैक्ड प्रोडक्ट के लिए। इस कॉइल की लंबाई मीटर में।",
    coilOptional: "केवल कॉइल पंक्तियों के लिए। वैकल्पिक।",
    coilReceivedDate: "केवल कॉइल पंक्तियों के लिए। वैकल्पिक — डिफ़ॉल्ट आज।",
    minimumStock: "वैकल्पिक री-ऑर्डर स्तर, पूरे किलो में।",
    matchCategory: "किसी मौजूदा कैटेगरी नाम से मेल खाना चाहिए। केस से फ़र्क नहीं पड़ता।",
    matchSubCategory: "किसी मौजूदा सब कैटेगरी (प्रोडक्ट कैटलॉग) नाम से मेल खाना चाहिए। केस से फ़र्क नहीं पड़ता।",
    // Product category
    categoryNameUnique: "कैटेगरी नाम। अद्वितीय होना चाहिए।",
    taxPercentage: "टैक्स प्रतिशत (जैसे 18% के लिए 18)। किसी मौजूदा टैक्स रिकॉर्ड से मेल खाना चाहिए।",
  },
};

// Consolidated user-facing error / status messages surfaced from hooks, services
// and analytics pages (previously hardcoded English strings).
STRINGS.hookErrors = {
  common: {
    unexpected: "एक अप्रत्याशित त्रुटि हुई",
    dateRangeRequired: "शुरू और अंत दोनों तारीखें आवश्यक हैं",
  },
  noSelection: {
    users: "कोई यूज़र चयनित नहीं",
    customers: "कोई ग्राहक चयनित नहीं",
    sales: "कोई सेल्स चयनित नहीं",
    taxes: "हटाने के लिए कोई टैक्स चयनित नहीं",
    locations: "हटाने के लिए कोई लोकेशन चयनित नहीं",
    hsnCodes: "हटाने के लिए कोई एचएसएन कोड चयनित नहीं",
    customer: "कोई ग्राहक चयनित नहीं",
    userIds: "कोई यूज़र ID प्रदान नहीं किया गया",
  },
  users: {
    fetchList: "यूज़र लाने में विफल",
    fetchOne: "यूज़र लाने में विफल",
  },
  customers: {
    fetchList: "ग्राहक लोड करने में विफल",
    fetchOne: "ग्राहक लोड करने में विफल",
  },
  customerWarehouses: {
    fetchList: "वेयरहाउस लोड करने में विफल",
    fetchOne: "वेयरहाउस लोड करने में विफल",
  },
  labours: {
    fetchList: "मज़दूर लाने में विफल",
  },
  taxes: {
    fetchList: "टैक्स लाने में विफल",
    fetchOne: "टैक्स लाने में विफल",
    create: "टैक्स बनाने में विफल",
    update: "टैक्स अपडेट करने में विफल",
    deleteOne: "टैक्स हटाने में विफल",
    deleteMany: "टैक्स हटाने में विफल",
  },
  locations: {
    fetchList: "लोकेशन लाने में विफल",
    fetchOne: "लोकेशन लाने में विफल",
    create: "लोकेशन बनाने में विफल",
    update: "लोकेशन अपडेट करने में विफल",
    deleteOne: "लोकेशन हटाने में विफल",
    deleteMany: "लोकेशन हटाने में विफल",
    toggleStatus: "लोकेशन स्थिति टॉगल करने में विफल",
    export: "लोकेशन एक्सपोर्ट करने में विफल",
  },
  hsnCodes: {
    fetchList: "एचएसएन कोड लाने में विफल",
    fetchOne: "एचएसएन कोड लाने में विफल",
    create: "एचएसएन कोड बनाने में विफल",
    update: "एचएसएन कोड अपडेट करने में विफल",
    deleteOne: "एचएसएन कोड हटाने में विफल",
    deleteMany: "एचएसएन कोड हटाने में विफल",
  },
  products: {
    fetchList: "प्रोडक्ट लाने में विफल",
    fetchOne: "प्रोडक्ट लाने में विफल",
  },
  orders: {
    fetchList: "ऑर्डर लाने में विफल",
    fetchDetails: "ऑर्डर विवरण लाने में विफल",
  },
  deliveryOrders: {
    fetchList: "डिलीवरी ऑर्डर लाने में विफल",
  },
  ipAddresses: {
    fetchOne: "IP पता लाने में विफल",
  },
  statesCities: {
    fetchStates: "राज्य लाने में विफल",
    fetchCities: "शहर लाने में विफल",
  },
  sales: {
    fetchData: "सेल्स डेटा लाने में विफल",
    bulkDelete: "सेल्स बल्क डिलीट करने में विफल",
    export: "एक्सपोर्ट करने में विफल",
    exportFailed: "EXPORT_FAILED",
    importFailed: "IMPORT_FAILED",
    exportSuccess: "एक्सपोर्ट सफलतापूर्वक पूर्ण हुआ",
    importSuccess: "इम्पोर्ट सफलतापूर्वक पूर्ण हुआ",
    emailSuccess: "ईमेल सफलतापूर्वक भेजा गया",
    whatsappSuccess: "WhatsApp सफलतापूर्वक भेजा गया",
  },
  purchaseAdvice: {
    markAllAdvised: "सभी को एडवाइज़ के रूप में चिह्नित करने में विफल",
  },
  challanAnalytics: {
    fetchList: "चालान सूची लाने में विफल",
    fetchDaily: "दैनिक डेटा लाने में विफल",
    fetchSummary: "सारांश लाने में विफल",
  },
  movementAnalytics: {
    fetchProducts: "मूवमेंट प्रोडक्ट लाने में विफल",
    fetchSummary: "मूवमेंट सारांश लाने में विफल",
    fetchProductSplit: "प्रोडक्ट स्प्लिट लाने में विफल",
    fetchProductOrders: "प्रोडक्ट ऑर्डर लाने में विफल",
  },
  salesAnalytics: {
    fetchSummary: "सारांश लाने में विफल",
    fetchDaily: "दैनिक डेटा लाने में विफल",
    fetchLeaderboard: "लीडरबोर्ड लाने में विफल",
  },
  supervisorAnalytics: {
    fetchSummary: "सारांश लाने में विफल",
    fetchLoading: "लोडिंग डेटा लाने में विफल",
    fetchUnloading: "अनलोडिंग डेटा लाने में विफल",
    fetchLeaderboard: "लीडरबोर्ड लाने में विफल",
  },
  assignedToChallan: {
    fetchPendingBacklog: "लंबित बैकलॉग लाने में विफल",
    fetchDurationStats: "अवधि आँकड़े लाने में विफल",
  },
  analyticsPage: {
    fetchFailed: "एनालिटिक्स डेटा लाने में विफल",
  },
};

// Shared form placeholders / inline labels that were previously hardcoded.
STRINGS.commonForm = {
  referencePartyPlaceholder: "संदर्भ पार्टी का नाम दर्ज करें (वैकल्पिक)",
  loading: "लोड हो रहा है...",
  selectProduct: "प्रोडक्ट चुनें",
  skipAndNext: "छोड़ें और अगला",
  internalRemarksOptional: "आंतरिक टिप्पणियाँ (वैकल्पिक)",
  shortRemarkOptional: "संक्षिप्त टिप्पणी / विवरण (वैकल्पिक)",
};

// Service-surfaced success / status messages.
STRINGS.serviceMessages = {
  auth: {
    passwordUpdated: "पासवर्ड सफलतापूर्वक अपडेट किया गया",
    passwordResetEmailSent: "पासवर्ड रीसेट ईमेल भेजा गया",
    passwordReset: "पासवर्ड सफलतापूर्वक रीसेट किया गया",
  },
};

STRINGS.ordersModule = {
    common: {
        symbols: {
            required: '*',
            emDash: '—',
            currency: '₹',
        },
        buttons: {
            refresh: 'रिफ्रेश',
            delete: 'हटाएं',
            cancel: 'रद्द करें',
            ok: 'ठीक है',
            loading: 'मार्क हो रहा है...',
            save: 'सहेजें',
        },
        units: {
            kg: 'kg',
            m: 'm',
        },
        loading: 'लोड हो रहा है...',
        dialogs: {
            delete: {
                confirm: 'हटाएं',
                cancel: 'रद्द करें',
            },
        },
        status: {
            error: 'त्रुटि',
            success: 'सफल',
            processing: 'प्रोसेस हो रहा है...',
        },
    },
    orders: {
        page: {
            header: {
                title: 'ऑर्डर',
                subtitle: 'सेल्स ऑर्डर, डिलीवरी और इनवॉइस प्रबंधित करें',
            },
            stats: {
                total: 'कुल ऑर्डर',
                selected: 'चयनित',
            },
            alerts: {
                errorTitle: 'त्रुटि',
            },
        },
        table: {
            columns: {
                select: 'चुनें',
                orderNumber: 'ऑर्डर नंबर',
                serialNumber: 'सीरियल नंबर',
                date: 'तारीख',
                customer: 'ग्राहक',
                tallyName: 'टैली नाम',
                deliveryLocation: 'डिलीवरी लोकेशन',
                totalWeight: 'कुल वज़न',
                vehicleNumber: 'वाहन नंबर',
                totalAmount: 'कुल राशि',
                status: 'स्थिति',
                statusActions: 'स्थिति अपडेट करें',
                actions: 'कार्रवाई',
            },
            emptyState: {
                title: 'कोई ऑर्डर नहीं मिला',
                message: 'दिखाने के लिए अभी कोई ऑर्डर नहीं है।',
            },
            empty: {
                title: 'कोई ऑर्डर नहीं मिला',
                message: 'दिखाने के लिए अभी कोई ऑर्डर नहीं है।',
            },
            aria: {
                selectAll: 'सभी ऑर्डर चुनें',
                selectOrder: 'ऑर्डर चुनें',
                viewOrder: 'ऑर्डर देखें',
                editOrder: 'ऑर्डर संपादित करें',
                deleteOrder: 'ऑर्डर हटाएं',
            },
            tooltips: {
                view: 'ऑर्डर देखें',
                edit: 'ऑर्डर संपादित करें',
                delete: 'ऑर्डर हटाएं',
                assignPersonnel: 'कर्मचारी असाइन करें',
                changePersonnel: 'कर्मचारी बदलें',
                startTruckLoading: 'ट्रक लोडिंग शुरू करें',
                completeTruckLoading: 'ट्रक लोडिंग पूर्ण करें',
                updateDeliveryChallan: 'डिलीवरी चालान अपडेट करें',
                printDeliveryChallan: 'Print Delivery Challan',
                printDeliveryOrder: 'Print Delivery Order',
                markDelivered: 'डिलीवर मार्क करें',
                generateInvoice: 'इनवॉइस जनरेट करें',
                printInvoice: 'Print Invoice',
            },
        },

        statuses: {
            pending: 'लंबित',
            confirmed: 'पुष्ट',
            delivery_assigned: 'डिलीवरी असाइन',
            loading: 'ट्रक लोडिंग',
            loaded: 'ट्रक लोड हो गया',
            in_transit: 'रास्ते में',
            delivered: 'डिलीवर हो गया',
            invoiced: 'इनवॉइस हो गया',
        },
        messages: {
            confirmMarkDelivered: 'इस डिलीवरी चालान को डिलीवर मार्क करें?',
            markDeliveredSuccess: 'ऑर्डर डिलीवर मार्क किया गया',
            markDeliveredFailed: 'डिलीवर मार्क करने में विफल',
            invoiceGenerated: 'इनवॉइस जनरेट हुआ',
            invoiceGenerateFailed: 'इनवॉइस जनरेट करने में विफल',
            confirmOrderSuccess: 'ऑर्डर सफलतापूर्वक पुष्ट हुआ',
            confirmOrderFailed: 'ऑर्डर पुष्ट करने में विफल',
            completeSubOrders: 'सब-ऑर्डर पूर्ण करें',
        },
        actions: {
            searchPlaceholder: 'ऑर्डर खोजें...',
            buttons: {
                add: 'ऑर्डर जोड़ें',
                export: 'ऑर्डर एक्सपोर्ट करें',
                bulkDelete: 'चयनित हटाएं',
                confirm: 'पुष्टि करें',
                sendEmail: 'ईमेल भेजें',
            },
            challan: {
                title: 'डिलीवरी चालान',
                selectDateLabel: 'चालान तारीख चुनें',
                onlyPastNote: 'केवल आज या पिछली तारीखें अनुमत हैं।',
                labels: {
                    expenses: 'खर्च',
                    discount: 'छूट',
                    remark: 'टिप्पणी',
                    subOrder: 'सब-ऑर्डर #',
                    customer: 'ग्राहक',
                    supervisor: 'सुपरवाइज़र',
                    vehicle: 'वाहन',
                    itemsCount: 'आइटम संख्या',
                    totalQty: 'कुल मात्रा',
                    subtotal: 'उप-योग',
                    freight: 'भाड़ा',
                    loading: 'लोडिंग',
                    gst: 'जीएसटी',
                    totalAmount: 'कुल राशि',
                    items: 'आइटम',
                    challanDetails: 'चालान विवरण',
                },
                placeholders: {
                    expenseRemark: 'खर्च का कारण...',
                    discountRemark: 'छूट का कारण...',
                    amount: '0.00',
                },
                buttons: {
                    cancel: 'रद्द करें',
                    generate: 'जीआरएन जनरेट करें',
                    generating: 'जनरेट हो रहा है...'
                }
            },
        },

        daily: {
            page: {
                header: {
                    title: 'दैनिक ऑर्डर',
                    subtitle: "आज की ऑर्डर रिपोर्ट देखें और प्रबंधित करें",
                },
            },
            alerts: {
                viewTitle: 'ऑर्डर विवरण',
            },
            viewMessageTemplate: 'ऑर्डर {order_number} - ग्राहक: {customer_name}',
        },
        confirm: {
            deleteSingle: {
                title: 'ऑर्डर हटाएं',
                message: 'क्या आप वाकई इस ऑर्डर को हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।',
                confirmLabel: 'हटाएं',
            },
            deleteBulk: {
                title: 'चयनित ऑर्डर हटाएं',
                messageMultiple: 'क्या आप वाकई {count} चयनित ऑर्डर हटाना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।',
                confirmLabel: 'हटाएं',
            },
            confirmOrder: {
                title: 'ऑर्डर पुष्ट करें',
                message: 'क्या आप वाकई इस ऑर्डर को पुष्ट करना चाहते हैं?',
                confirmLabel: 'ऑर्डर पुष्ट करें',
            },
            cancelOrder: {
                title: 'ऑर्डर रद्द करें',
                message: 'क्या आप वाकई इस ऑर्डर को रद्द करना चाहते हैं? यह कार्रवाई पूर्ववत नहीं की जा सकती।',
                confirmLabel: 'ऑर्डर रद्द करें',
            },
            sendConfirmEmail: {
                title: 'पुष्टि भेजें',
                message: 'ग्राहक को ऑर्डर पुष्टि भेजें?',
                confirmLabel: 'भेजें',
                loading: 'भेजा जा रहा है...',
                cancel: 'रद्द करें',
                success: 'ऑर्डर पुष्टि सफलतापूर्वक भेजी गई',
                error: 'ऑर्डर पुष्टि भेजने में विफल',
            },
        },
        view: {
            header: {
                sectionTitle: 'ऑर्डर विवरण',
                fallbackTitle: 'ऑर्डर',
            },
            loading: 'ऑर्डर विवरण लोड हो रहा है...',
            empty: {
                title: 'ऑर्डर डेटा उपलब्ध नहीं',
                message: 'हम चयनित ऑर्डर लोड नहीं कर सके। कृपया पुनः प्रयास करें।',
            },
            fields: {
                orderNumber: 'ऑर्डर नंबर',
                challanNumber: 'चालान नंबर',
                challanDate: 'चालान तारीख',
                challanGenerated: 'चालान जनरेट हुआ',
                invoiceNumber: 'इनवॉइस नंबर',
                invoiceDate: 'इनवॉइस तारीख',
                invoiced: 'इनवॉइस हो गया',
                serialNumber: 'सीरियल नंबर',
                date: 'तारीख',
                expectedDeliveryDate: 'अपेक्षित डिलीवरी तारीख',
                customerName: 'ग्राहक',
                supervisorName: 'सुपरवाइज़र',
                deliveryBoyName: 'डिलीवरी बॉय',
                vehicleNumber: 'वाहन नंबर',
                driverContact: 'ड्राइवर संपर्क',
                tallyName: 'टैली नाम',
                deliveryLocation: 'डिलीवरी लोकेशन',
                sourceType: 'स्रोत प्रकार',
                warehouseName: 'वेयरहाउस',
                supplierName: 'सप्लायर',
                totalItems: 'कुल आइटम',
                subtotal: 'उप-योग',
                discountType: 'छूट प्रकार',
                discountValue: 'छूट मान',
                discountAmount: 'छूट राशि',

                gstOption: 'जीएसटी विकल्प',
                gstIncluded: 'जीएसटी शामिल',
                gstnNumber: 'जीएसटीएन नंबर',
                gstPercentage: 'जीएसटी प्रतिशत',
                totalGstAmount: 'कुल जीएसटी राशि',
                freight: 'भाड़ा',
                freightGstAmount: 'भाड़ा जीएसटी राशि',
                loadingCost: 'प्रति किग्रा लोडिंग लागत',
                loadingCostGstAmount: 'लोडिंग लागत जीएसटी राशि',
                totalLoadingCost: 'कुल लोडिंग लागत',
                emptyTruckWeight: 'खाली ट्रक वज़न',
                finalTruckWeight: 'अंतिम ट्रक वज़न',
                truckWeight: 'ट्रक वज़न',
                totalWeight: 'कुल वज़न',
                presentShipping: 'वर्तमान शिपिंग',
                pendingOrder: 'लंबित ऑर्डर',
                actualQuantity: 'वास्तविक मात्रा',
                remark: 'टिप्पणी',
                status: 'स्थिति',
                totalAmount: 'कुल राशि',
                createdBy: 'द्वारा बनाया गया',
                edited: 'संपादित',
                isNewCustomer: 'नया ग्राहक',
                contactPerson: 'संपर्क व्यक्ति',
                mobileNumber: 'मोबाइल नंबर',
                items: 'आइटम',
                item_product: 'प्रोडक्ट',
                item_size: 'साइज़',
                item_category: 'कैटेगरी',
                item_unit: 'यूनिट',
                item_length: 'लंबाई',
                item_qty: 'ऑर्डर मात्रा',
                item_qty_in_pieces: 'पीस में मात्रा',
                item_price: 'कीमत',
                item_actual_price: 'वास्तविक कीमत',
                item_rate: 'दर',
                item_amount: 'राशि',
                item_amount_incl_gst: 'राशि (जीएसटी सहित)',
                item_hsn: 'एचएसएन',
                item_hsn_description: 'एचएसएन विवरण',
                item_gst_percent: 'जीएसटी %',
                item_gst_amount: 'जीएसटी राशि',
                item_order_weight: 'ऑर्डर वज़न',
                item_actual_weight: 'वास्तविक वज़न',
                item_per_piece_weight: 'प्रति पीस वज़न',
                item_actual_pieces: 'वास्तविक पीस',
                item_present_shipping: 'वर्तमान शिपिंग',
                item_pending_order: 'लंबित ऑर्डर',
                item_actual_quantity: 'वास्तविक मात्रा',
                item_remark: 'टिप्पणी',
                item_product_weight: 'प्रोडक्ट वज़न',
                item_standard_length: 'मानक लंबाई',
                item_standard_weight: 'मानक वज़न',
            },
            noItems: 'कोई आइटम उपलब्ध नहीं',
            buttons: {
                close: 'बंद करें',
            },
        },
        notifications: {
            created: 'ऑर्डर {id} सफलतापूर्वक बनाया गया',
            updated: 'ऑर्डर {id} सफलतापूर्वक अपडेट हुआ',
            deleted: 'ऑर्डर {id} सफलतापूर्वक हटाया गया',
            bulkDeleted: '{count} ऑर्डर सफलतापूर्वक हटाए गए',
            statusUpdated: 'ऑर्डर {id} की स्थिति सफलतापूर्वक अपडेट हुई',
            approved: 'ऑर्डर {id} सफलतापूर्वक स्वीकृत हुआ',
            rejected: 'ऑर्डर {id} सफलतापूर्वक अस्वीकृत हुआ',
        },
        alerts: {
            fetchFailed: 'ऑर्डर लोड करने में विफल',
            deleteFailed: 'ऑर्डर हटाने में विफल',
            operationFailed: 'कार्रवाई विफल',
            updateFailed: 'ऑर्डर अपडेट करने में विफल',
        },
        forms: {
            title: {
                add: 'ऑर्डर बनाएं',
                edit: 'ऑर्डर संपादित करें',
            },
            labels: {
                customerMode: 'ग्राहक मोड',
            },
            headings: {
                customerInformation: 'ग्राहक जानकारी',
                orderDetails: 'ऑर्डर विवरण',
            },
            customerModes: {
                existing: 'मौजूदा ग्राहक',
                new: 'नया ग्राहक',
            },
            loading: {
                customers: 'ग्राहक लोड हो रहे हैं...',
                locations: 'लोकेशन लोड हो रहे हैं...',
            },
            placeholders: {
                selectProductSize: 'साइज़ चुनें',
                selectSource: 'स्रोत चुनें',
                selectWarehouse: 'वेयरहाउस चुनें',
                selectSupplier: 'सप्लायर चुनें',
                noSupplier: 'कोई सप्लायर नहीं है',
            },
            fields: {
                customerName: { label: 'ग्राहक', placeholder: 'ग्राहक चुनें' },
                deliveryLocation: { label: 'डिलीवरी लोकेशन', placeholder: 'डिलीवरी लोकेशन चुनें' },
                freight: { label: 'भाड़ा', placeholder: 'जैसे, 500.00' },
                discountType: { label: 'छूट प्रकार', placeholder: 'जैसे, percentage', options: { fixed: { label: 'फिक्स्ड' }, percentage: { label: 'प्रतिशत' } } },
                discountValue: { label: 'छूट मान', placeholder: 'जैसे, 5.0' },
                expectedDeliveryDate: { label: 'अपेक्षित डिलीवरी तारीख' },
                sourceType: { label: 'स्रोत प्रकार', placeholder: 'स्रोत चुनें', options: { warehouse: { label: 'वेयरहाउस' }, supplier: { label: 'सप्लायर' } } },
                warehouse: { label: 'वेयरहाउस' },
                loadingCost: { label: 'लोडिंग लागत', placeholder: 'जैसे, 200.00' },
                loadingCostNote: 'प्रति किग्रा',
                gstOption: { label: 'जीएसटी विकल्प', placeholder: 'जैसे, with_gstn', options: { withGstn: { label: 'जीएसटीएन के साथ' }, withoutGstn: { label: 'जीएसटीएन के बिना' }, percentage: { label: 'जीएसटी प्रतिशत' }, none: { label: 'कोई नहीं' } } },
                gstnNumber: { label: 'जीएसटीएन नंबर', placeholder: '27AAAAA0000A1Z5' },
                gstPercentage: { label: 'जीएसटी प्रतिशत', placeholder: 'जैसे, 18.0' },
                tcsApplicable: { label: 'टीसीएस लागू' },
                tcsPercentage: { label: 'टीसीएस प्रतिशत', placeholder: 'जैसे, 0.1' },
                smsNotification: { label: 'एसएमएस सूचना' },
                remark: { label: 'टिप्पणी', placeholder: 'कोई नोट या टिप्पणी' },
                items: {
                    label: 'आइटम',
                    approxWeight: 'अनुमानित वज़न',
                    loadingCost: 'लोडिंग लागत',
                    amount: 'राशि',
                },
                item_qty: { label: 'मात्रा', placeholder: 'जैसे, 100' },
                item_price: { label: 'कीमत', placeholder: 'जैसे, 500.00' },
                itemRemark: { label: 'आइटम टिप्पणी', placeholder: 'आइटम नोट' },
                unit: { label: 'मात्रा की यूनिट', placeholder: 'जैसे, PCS' },
                length: { label: 'लंबाई', placeholder: 'जैसे, 6.0' },
                pieces: { label: 'पीस', placeholder: 'जैसे, 50' },
                totalAmount: { label: 'कुल राशि' },
                gstApplicable: { label: 'जीएसटी शामिल' },
                addItem: 'आइटम जोड़ें',
            },
            buttons: {
                next: 'अगला',
                back: 'पीछे',
                addItem: 'आइटम जोड़ें',
                save: 'सहेजें',
                cancel: 'रद्द करें',
            },
            messages: {
                noStock: 'चयनित वेयरहाउस में कोई स्टॉक उपलब्ध नहीं',
            },
            errors: {
                customerRequired: 'ग्राहक आवश्यक है',
                locationRequired: 'डिलीवरी लोकेशन आवश्यक है',
                sourceTypeRequired: 'स्रोत प्रकार आवश्यक है',
                warehouseRequired: 'वेयरहाउस आवश्यक है',
                supplierRequired: 'सप्लायर आवश्यक है',
                itemsRequired: 'कम से कम एक आइटम आवश्यक है',
                itemProductSizeRequired: 'प्रोडक्ट आवश्यक है',
                itemQuantityRequired: 'मात्रा आवश्यक है',
                itemPiecesRequired: 'पीस आवश्यक हैं (कितने दर्ज करें)',
                itemPriceRequired: 'कीमत आवश्यक है',
                itemPriceInvalid: 'मान्य कीमत दर्ज करें (0 या अधिक)',
                itemUnitRequired: 'यूनिट आवश्यक है',
                itemLengthRequired: 'लंबाई आवश्यक है',
                itemLengthInvalid: 'लंबाई एक मान्य संख्या होनी चाहिए',
                freightInvalid: 'भाड़ा एक मान्य संख्या होनी चाहिए',
                discountTypeInvalid: "इनपुट 'fixed' या 'percentage' होना चाहिए",
                discountValueInvalid: 'छूट मान एक मान्य संख्या होनी चाहिए',
                loadingCostInvalid: 'लोडिंग लागत एक मान्य संख्या होनी चाहिए',
                gstOptionInvalid: "इनपुट 'with_gstn', 'percentage' या 'none' होना चाहिए",
                gstnNumberInvalid: 'जीएसटीएन नंबर मान्य होना चाहिए',
                gstPercentageInvalid: 'जीएसटी प्रतिशत एक मान्य संख्या होनी चाहिए',
                tcsPercentageInvalid: 'टीसीएस प्रतिशत एक मान्य संख्या होनी चाहिए',
            },
            assignPersonnel: {
                title: 'डिलीवरी कर्मचारी असाइन करें',
                subtitle: 'इस ऑर्डर के लिए डिलीवरी सुपरवाइज़र और डिलीवरी बॉय चुनें।',
                labels: {
                    supervisor: 'डिलीवरी सुपरवाइज़र',
                    deliveryBoy: 'डिलीवरी बॉय',
                    vehicleNumber: 'वाहन नंबर',
                    driverContact: 'ड्राइवर संपर्क',
                    vehicleNumberHint: 'प्रारूप: KA-53-1234, RJ14CA4241, RJ 14 CA 1424, RJ-14-CA-2414'
                },
                buttons: {
                    cancel: 'रद्द करें',
                    assign: 'असाइन करें',
                    assigning: 'असाइन हो रहा है...'
                },
                messages: {
                    loadingUsers: 'यूज़र लोड हो रहे हैं...',
                    selectAtLeastOne: 'कम से कम एक व्यक्ति चुनें',
                    invalidOrder: 'अमान्य ऑर्डर',
                    assignFailed: 'कर्मचारी असाइन करने में विफल',
                    vehicleRequired: 'वाहन नंबर आवश्यक है',
                    invalidVehicleNumber: 'कृपया एक मान्य वाहन नंबर दर्ज करें।',
                    driverContactRequired: 'ड्राइवर संपर्क आवश्यक है',
                    invalidDriverContact: 'कृपया एक मान्य नंबर दर्ज करें।'
                }
            },
            changePersonnel: {
                title: 'डिलीवरी कर्मचारी बदलें',
                subtitle: 'इस ऑर्डर के लिए डिलीवरी सुपरवाइज़र और डिलीवरी बॉय अपडेट करें।',
                buttons: {
                    cancel: 'रद्द करें',
                    change: 'बदलें',
                    changing: 'बदला जा रहा है...'
                }
            },
            startTruck: {
                title: 'ट्रक लोडिंग शुरू करें',
                subtitle: 'ट्रक विवरण दर्ज करें और लोडिंग के लिए मज़दूर असाइन करें।',
                labels: {
                    vehicleNumber: 'वाहन नंबर',
                    driverContact: 'ड्राइवर संपर्क',
                    emptyTruckWeight: 'खाली ट्रक वज़न (kg)',
                    labours: 'मज़दूर',
                    remark: 'टिप्पणी'
                },
                placeholders: {
                    vehicleNumber: 'जैसे, KA-53-1234',
                    driverContact: 'जैसे, 9876543210',
                    emptyTruckWeight: 'जैसे, 5000',
                    remark: 'जैसे, टायर प्रेशर जांचें'
                },
                buttons: {
                    cancel: 'रद्द करें',
                    start: 'लोडिंग शुरू करें',
                    starting: 'शुरू हो रहा है...'
                },
                messages: {
                    loadingUsers: 'मज़दूर लोड हो रहे हैं...',
                    selectAtLeastOneLabour: 'कम से कम एक मज़दूर चुनें',
                    vehicleRequired: 'वाहन नंबर आवश्यक है',
                    invalidDriverContact: 'कृपया एक मान्य ड्राइवर संपर्क नंबर दर्ज करें',
                    invalidEmptyTruckWeight: 'कृपया एक मान्य खाली ट्रक वज़न दर्ज करें',
                    formErrors: 'कृपया हाइलाइट किए गए फ़ील्ड ठीक करें',
                    invalidOrder: 'अमान्य ऑर्डर',
                    startFailed: 'ट्रक लोडिंग शुरू करने में विफल'
                }
            },
            completeTruckLoading: {
                title: 'ट्रक लोडिंग पूर्ण करें',
                subtitle: 'आइटम मात्रा अपडेट करें और अंतिम ट्रक वज़न दर्ज करें।',
                labels: {
                    orderId: 'ऑर्डर आईडी',
                    truckWeight: 'अंतिम ट्रक वज़न (kg)',
                    productName: 'प्रोडक्ट नाम',
                    quantity: 'मात्रा',
                    actualQuantity: 'वास्तविक मात्रा',
                    items: 'आइटम',
                    emptyTruckWeight: 'खाली ट्रक वज़न (kg)',
                    theoreticalPerUnitWeight: 'सैद्धांतिक प्रति यूनिट (kg)',
                    actualPerUnitWeight: 'वास्तविक प्रति यूनिट (kg)',
                    loadingCost: 'लोडिंग लागत',
                    unknownProduct: 'अज्ञात प्रोडक्ट',
                    unitLabel: 'यूनिट',
                    lengthLabel: 'लंबाई',
                    piecesLabel: 'पीस',
                    approxWeight: 'अनुमानित वज़न',
                    totalLoadingCost: 'कुल लोडिंग लागत',
                    approxPerUnitWeight: 'अनुमानित प्रति यूनिट वज़न',
                    approxPerPieceWeight: 'अनुमानित प्रति पीस वज़न',
                    actualPerUnitWeightLabel: 'समायोजित प्रति यूनिट वज़न',
                    actualPieces: 'वास्तविक पीस',
                    perPieceWeight: 'प्रति पीस वज़न',
                    adjustedQuantity: 'समायोजित मात्रा',
                    // unitLabel: 'Unit',
                },
                placeholders: {
                    emptyTruckWeight: 'खाली ट्रक वज़न दर्ज करें',
                    truckWeight: 'अंतिम ट्रक वज़न दर्ज करें'
                },
                buttons: {
                    cancel: 'रद्द करें',
                    complete: 'लोडिंग पूर्ण करें',
                    completing: 'पूर्ण हो रहा है...',
                    saveDraft: 'ड्राफ्ट सहेजें',
                    saving: 'सहेजा जा रहा है...',
                },
                messages: {
                    loadingOrder: 'ऑर्डर विवरण लोड हो रहा है...',
                    invalidOrder: 'अमान्य ऑर्डर आईडी',
                    completeFailed: 'ट्रक लोडिंग पूर्ण करने में विफल',
                    noItems: 'ऑर्डर में कोई आइटम नहीं मिला',
                    invalidWeight: 'कृपया एक मान्य ट्रक वज़न दर्ज करें',
                    invalidQuantity: 'कृपया सभी आइटम के लिए मान्य मात्रा दर्ज करें',
                    weightsAdjusted: 'वज़न इस फैक्टर से समायोजित: {factor}'
                }
            },
            updateDeliveryChallan: {
                title: 'डिलीवरी चालान अपडेट करें',
                subtitle: 'ट्रक वज़न, लागत और डिलीवरी विवरण अपडेट करें।',
                labels: {
                    emptyTruckWeight: 'खाली ट्रक वज़न (kg)',
                    finalTruckWeight: 'अंतिम ट्रक वज़न (kg)',
                    loadingCost: 'लोडिंग लागत',
                    freight: 'भाड़ा',
                    vehicleNumber: 'वाहन नंबर',
                    remark: 'टिप्पणी'
                },
                placeholders: {
                    emptyTruckWeight: 'जैसे, 5100.0',
                    finalTruckWeight: 'जैसे, 15200.0',
                    loadingCost: 'जैसे, 250.0',
                    freight: 'जैसे, 550.0',
                    vehicleNumber: 'जैसे, KA-53-1234',
                    remark: 'जैसे, समय पर डिलीवर हुआ'
                },
                buttons: {
                    cancel: 'रद्द करें',
                    update: 'चालान अपडेट करें',
                    updating: 'अपडेट हो रहा है...'
                },
                messages: {
                    loadingOrder: 'ऑर्डर विवरण लोड हो रहा है...',
                    invalidOrder: 'अमान्य ऑर्डर आईडी',
                    updateFailed: 'डिलीवरी चालान अपडेट करने में विफल',
                    updateSuccess: 'डिलीवरी चालान सफलतापूर्वक अपडेट हुआ',
                    emptyTruckWeightRequired: 'खाली ट्रक वज़न आवश्यक है',
                    finalTruckWeightRequired: 'अंतिम ट्रक वज़न आवश्यक है',
                    vehicleNumberRequired: 'वाहन नंबर आवश्यक है',
                    invalidEmptyTruckWeight: 'कृपया एक मान्य खाली ट्रक वज़न दर्ज करें',
                    invalidFinalTruckWeight: 'कृपया एक मान्य अंतिम ट्रक वज़न दर्ज करें',
                    invalidLoadingCost: 'कृपया एक मान्य लोडिंग लागत दर्ज करें',
                    invalidFreight: 'कृपया एक मान्य भाड़ा मान दर्ज करें'
                }
            },
        },
    },
    locations: {
        defaults: {
            country: 'भारत',
        },
    },
    customers: {
        forms: {
            fields: {
                ownerName: { label: 'मालिक का नाम', placeholder: 'मालिक का नाम दर्ज करें' },
                companyName: { label: 'कंपनी नाम', placeholder: 'कंपनी नाम दर्ज करें' },
                gstNumber: { label: 'जीएसटी नंबर', placeholder: 'जीएसटी नंबर दर्ज करें' },
                tallyName: { label: 'टैली नाम', placeholder: 'टैली नाम दर्ज करें' },
                contactPerson: { label: 'संपर्क व्यक्ति', placeholder: 'संपर्क व्यक्ति दर्ज करें' },
                phoneNumber1: { label: 'फ़ोन नंबर 1', placeholder: 'फ़ोन नंबर दर्ज करें' },
                sisterCompany: { label: 'सहयोगी कंपनी', placeholder: 'सहयोगी कंपनी दर्ज करें' },
                email: { label: 'ईमेल', placeholder: 'ईमेल दर्ज करें' },
                address1: { label: 'पता पंक्ति 1', placeholder: 'पता दर्ज करें' },
                city: { label: 'शहर', placeholder: 'शहर दर्ज करें' },
                state: { label: 'राज्य', placeholder: 'राज्य दर्ज करें' },
                country: { label: 'देश', placeholder: 'देश दर्ज करें' },
                zipCode: { label: 'ज़िप कोड', placeholder: 'ज़िप कोड दर्ज करें' },
                creditPeriod: { label: 'क्रेडिट अवधि', placeholder: 'क्रेडिट अवधि दर्ज करें' },
            },
        },
    },
    productSizes: {
        forms: {
            fields: {
                product_size: { label: 'प्रोडक्ट', placeholder: 'प्रोडक्ट साइज़ चुनें' },
            },
        },
    },
};

STRINGS.inquiriesModule = {
  inquiries: {
    notifications: {
      created: "पूछताछ {id} सफलतापूर्वक बनाई गई",
      updated: "पूछताछ {id} सफलतापूर्वक अपडेट की गई",
      deleted: "पूछताछ {id} सफलतापूर्वक हटाई गई",
      bulkDeleted: "{count} पूछताछ सफलतापूर्वक हटाई गईं",
      statusUpdated: "पूछताछ {id} की स्थिति सफलतापूर्वक अपडेट की गई",
      approved: "पूछताछ {id} सफलतापूर्वक स्वीकृत की गई",
      rejected: "पूछताछ {id} सफलतापूर्वक अस्वीकृत की गई",
      emailSent: "ईमेल सफलतापूर्वक भेजा गया",
    },
    table: {
      columns: {
        customerName: "ग्राहक",
        customerCompany: "कंपनी",
        productAlias: "प्रोडक्ट",
        quantity: "मात्रा",
        price: "कीमत",
        includeGst: "जीएसटी शामिल करें",
        total: "कुल",
        status: "स्थिति",
        expectedDeliveryDate: "अपेक्षित डिलीवरी",
        isNewCustomer: "ग्राहक प्रकार",
        actions: "क्रियाएं",
        closeApi: "कन्वर्ट / बंद करें",
      },
      copy: {
        noCustomer: "लागू नहीं",
        noCompany: "लागू नहीं",
        noProduct: "लागू नहीं",
        noQuantity: "0",
        noPrice: "₹0.00",
        noTotal: "₹0.00",
        yes: "हाँ",
        no: "नहीं",
        newCustomerLabel: "ग्राहक: नया",
        existingCustomerLabel: "ग्राहक: मौजूदा",
        statuses: {
          pending: "लंबित",
          approved: "स्वीकृत",
          rejected: "अस्वीकृत",
          cancelled: "रद्द किया गया",
          completed: "पूर्ण",
          converted: "कन्वर्ट किया गया",
        },
      },
      customerTypes: {
        new: "नया",
        existing: "मौजूदा",
      },
      emptyState: {
        title: "कोई पूछताछ नहीं मिली",
        message: "दिखाने के लिए कोई पूछताछ नहीं है।",
      },
      aria: {
        selectAll: "सभी पूछताछ चुनें",
        selectInquiry: "पूछताछ चुनें",
        viewInquiry: "पूछताछ देखें",
        editInquiry: "पूछताछ संपादित करें",
        deleteInquiry: "पूछताछ हटाएं",
      },
      actions: {
        buttons: {
          view: "देखें",
          edit: "संपादित करें",
          delete: "हटाएं",
          refresh: "रिफ्रेश",
          add: "पूछताछ जोड़ें",
          convert: "कन्वर्ट करें",
          close: "बंद करें",
          quote: "कोटेशन भेजें",
        },
        searchPlaceholder: "पूछताछ खोजें...",
      },
    },
    view: {
      header: {
        sectionTitle: "पूछताछ विवरण",
        fallbackTitle: "पूछताछ",
      },
      fields: {
        customerName: "ग्राहक का नाम",
        customerCompany: "कंपनी",
        deliveryLocation: "डिलीवरी लोकेशन",
        productName: "प्रोडक्ट का नाम",
        productAlias: "प्रोडक्ट उपनाम",
        category: "कैटेगरी",
        hsnCode: "एचएसएन कोड",
        quantity: "मात्रा",
        units: "इकाइयाँ",
        length: "लंबाई",
        price: "कीमत",
        freight: "भाड़ा",
        subtotal: "उप-योग",
        gstAmount: "जीएसटी राशि",
        totalWithoutFreight: "कुल (भाड़े के बिना)",
        total: "कुल",
        expectedDeliveryDate: "अपेक्षित डिलीवरी",
        status: "स्थिति",
        remark: "टिप्पणी",
        isNewCustomer: "ग्राहक प्रकार",
        createdBy: "द्वारा बनाया गया",
      },
      empty: {
        title: "पूछताछ डेटा उपलब्ध नहीं है",
        message: "हम चयनित पूछताछ लोड नहीं कर सके। कृपया पुनः प्रयास करें।",
      },
      loading: "पूछताछ विवरण लोड हो रहा है...",
      copy: {
        noData: "लागू नहीं",
        noPrice: "₹0.00",
      },
    },
    actions: {
      searchPlaceholder: "पूछताछ खोजें...",
      buttons: {
        add: "पूछताछ जोड़ें",
        refresh: "रिफ्रेश",
        approve: "स्वीकृत करें",
        reject: "अस्वीकृत करें",
        bulkDelete: "चयनित हटाएं",
      },
      alerts: {
        noSelection: "जारी रखने के लिए कम से कम एक पूछताछ चुनें।",
      },
    },
    dialogs: {
      approve: {
        title: "पूछताछ स्वीकृत करें",
        message:
          "क्या आप वाकई इसे स्वीकृत करना चाहते हैं? यह पूछताछ एक ऑर्डर में कन्वर्ट कर दी जाएगी।",
        confirm: "ठीक है",
        cancel: "रद्द करें",
      },
      reject: {
        title: "पूछताछ अस्वीकृत करें",
        message: "क्या आप वाकई इसे अस्वीकृत करना चाहते हैं?",
        confirm: "अस्वीकृत करें",
        cancel: "रद्द करें",
      },
      sendQuote: {
        title: "कोटेशन भेजें",
        message: "क्या आपने मात्रा/कीमतें अपडेट कर दी हैं?",
        confirm: "कोटेशन भेजें",
        loading: "भेजा जा रहा है...",
        cancel: "रद्द करें",
      },
    },
    page: {
      header: {
        title: "पूछताछ",
        subtitle: "ग्राहक पूछताछ और कोटेशन प्रबंधित करें",
      },
      stats: {
        total: "कुल पूछताछ",
        selected: "चयनित",
      },
      alerts: {
        errorTitle: "पूछताछ त्रुटि",
      },
    },
  },
  inquiriesForm: {
    title: {
      add: "पूछताछ बनाएं",
      edit: "पूछताछ संपादित करें",
      convert: "ऑर्डर में कन्वर्ट करें",
    },
    steps: {
      customerInfo: "ग्राहक विवरण",
      orderInfo: "ऑर्डर विवरण",
    },
    fields: {
      mode: {
        label: "ग्राहक प्रकार",
        options: {
          new: "नया ग्राहक",
          existing: "मौजूदा ग्राहक",
        },
      },
      customer_id: {
        label: "ग्राहक",
        placeholder: "ग्राहक चुनें",
      },
      location_id: {
        label: "डिलीवरी लोकेशन",
        placeholder: "डिलीवरी लोकेशन चुनें",
      },
      freight: { label: "भाड़ा", placeholder: "उदा., 500.00" },
      expected_delivery_date: { label: "अपेक्षित डिलीवरी तारीख" },
      product_size_id: { label: "प्रोडक्ट", placeholder: "प्रोडक्ट चुनें" },
      units: { label: "इकाइयाँ", placeholder: "उदा., पीस" },
      length: { label: "लंबाई", placeholder: "उदा., 6.00" },
      quantity: { label: "मात्रा", placeholder: "उदा., 10.000" },
      price: {
        label: "कीमत",
        placeholder: "उदा., 850.50",
        note: "यह कीमत प्रति इकाई है",
      },
      include_gst: { label: "जीएसटी शामिल करें" },
      gst: { label: "जीएसटी (%)", placeholder: "उदा., 18.00" },
      // Approval/Conversion fields
      sourceType: {
        label: "स्रोत प्रकार",
        placeholder: "स्रोत चुनें",
        options: {
          warehouse: { label: "वेयरहाउस" },
          supplier: { label: "सप्लायर" },
        },
      },
      warehouse_id: { label: "वेयरहाउस", placeholder: "वेयरहाउस चुनें" },
      supplier_id: { label: "सप्लायर", placeholder: "सप्लायर चुनें" },
      loading_cost_per_kg: {
        label: "लोडिंग लागत (प्रति किग्रा)",
        placeholder: "उदा., 2.00",
      },

      remark: { label: "टिप्पणी", placeholder: "कोई नोट्स या टिप्पणियाँ" },
      // New customer fields
      customer: {
        owner_name: { label: "मालिक का नाम", placeholder: "नए ग्राहक का नाम" },
        company_name: { label: "कंपनी का नाम", placeholder: "New Company Ltd" },
        gst_number: { label: "जीएसटी नंबर", placeholder: "27AAAAA0000A1Z5" },
        contact_person: {
          label: "संपर्क व्यक्ति",
          placeholder: "संपर्क व्यक्ति",
        },
        customer_type: { label: "ग्राहक प्रकार", placeholder: "ग्राहक" },
        address_1: { label: "पता पंक्ति 1", placeholder: "123 स्ट्रीट" },
        state: { label: "राज्य", placeholder: "महाराष्ट्र" },
        city: { label: "शहर", placeholder: "मुंबई" },
        country: { label: "देश", placeholder: "भारत" },
        zip_code: { label: "ज़िप कोड", placeholder: "400001" },
        email: { label: "ईमेल", placeholder: "newcustomer@example.com" },
        phone_number_1: { label: "फ़ोन नंबर", placeholder: "9876543210" },
        tally_name: { label: "टैली नाम", placeholder: "नए ग्राहक की टैली" },
        credit_period: { label: "क्रेडिट अवधि (दिन)", placeholder: "30" },
      },
    },
    buttons: {
      next: "आगे",
      back: "पीछे",
      submit: "सबमिट करें",
      cancel: "रद्द करें",
      convert: "कन्वर्ट करें",
      addItem: "आइटम जोड़ें",
    },
    validation: {
      required: "यह फ़ील्ड आवश्यक है",
      invalidNumber: "कृपया एक मान्य संख्या दर्ज करें",
      invalidEmail: "कृपया एक मान्य ईमेल पता दर्ज करें",
    },
    loading: {
      submitting: "सबमिट हो रहा है...",
      loadingDetails: "पूछताछ विवरण लोड हो रहा है...",
      converting: "कन्वर्ट हो रहा है...",
    },
    notifications: {
      converted: "पूछताछ सफलतापूर्वक ऑर्डर में कन्वर्ट की गई",
    },
    locationModal: {
      title: "लोकेशन जोड़ें",
      fields: {
        state: { label: "राज्य", placeholder: "राज्य चुनें" },
        city: { label: "शहर", placeholder: "शहर दर्ज करें" },
        deliveryLocation: {
          label: "डिलीवरी लोकेशन",
          placeholder: "विस्तृत डिलीवरी लोकेशन",
        },
      },
      buttons: {
        cancel: "रद्द करें",
        save: "लोकेशन सहेजें",
      },
      messages: {
        success: "लोकेशन सफलतापूर्वक जोड़ा गया",
        error: "लोकेशन जोड़ने में विफल",
      },
      validation: {
        stateRequired: "राज्य आवश्यक है",
        cityRequired: "शहर आवश्यक है",
        locationRequired: "डिलीवरी लोकेशन आवश्यक है",
      },
      addButtonLabel: "लोकेशन जोड़ें",
    },
  },
};

STRINGS.deliveryOrdersModule = {
    common: {
        symbols: {
            required: '*',
            emDash: '—',
            currency: '₹',
        },
        buttons: {
            close: 'बंद करें',
            download: 'डाउनलोड',
            sendChallanEmail: 'चालान भेजें',
            sendingEmail: 'भेजा जा रहा है...',
        },
        units: {
            kg: 'kg',
            m: 'm',
        },
    },
    page: {
        header: {
            title: 'डिलीवरी ऑर्डर',
            subtitle: 'डिलीवरी सब-ऑर्डर प्रबंधित करें और स्थिति ट्रैक करें',
        },
        stats: {
            total: 'कुल',
        }
    },
    table: {
        columns: {
            subOrderNumber: 'सब-ऑर्डर नंबर',
            orderNumber: 'ऑर्डर नंबर',
            customer: 'ग्राहक',
            tallyName: 'टैली नाम',
            location: 'लोकेशन',
            totalQty: 'कुल मात्रा',
            status: 'स्थिति',
            actions: 'कार्रवाई',
        },
        actions: {
            view: 'विवरण देखें',
        },
        empty: {
            title: 'कोई डिलीवरी ऑर्डर नहीं',
            message: 'इस समय कोई डिलीवरी ऑर्डर नहीं मिला।',
        }
    },
    actions: {
        searchPlaceholder: 'ऑर्डर खोजें...',
        refresh: 'रिफ्रेश',
        assignSupervisor: 'सुपरवाइज़र असाइन करें',
        editSupervisor: 'सुपरवाइज़र संपादित करें',
        startLoading: 'लोडिंग शुरू करें',
        completeLoading: 'लोडिंग पूर्ण करें',
        generateChallan: 'चालान बनाएं',
        markInTransit: 'ट्रांज़िट में चिह्नित करें',
        markDelivered: 'डिलीवर चिह्नित करें',
        confirmMarkDelivered: 'क्या आप वाकई इसे डिलीवर के रूप में चिह्नित करना चाहते हैं?',
        markDeliveredSuccess: 'ऑर्डर सफलतापूर्वक डिलीवर चिह्नित किया गया',
        markDeliveredFailed: 'डिलीवर चिह्नित करने में विफल',
        orderDelivered: 'ऑर्डर डिलीवर हो गया',
        editTruckWeight: 'ट्रक वज़न संपादित करें',
        operationFailed: 'ऑपरेशन विफल',
        sendChallanEmail: 'चालान भेजें',
        sendChallanEmailConfirm: 'क्या आप वाकई ग्राहक को चालान भेजना चाहते हैं?',
        sendChallanEmailSuccess: 'चालान सफलतापूर्वक भेजा गया',
        sendChallanEmailFailed: 'चालान भेजने में विफल',
        cancelDO: 'डीओ रद्द करें',
        cancelDOConfirm: 'इस डिलीवरी ऑर्डर को रद्द करें? यह इसकी इन्वेंटरी को उलट देता है और इसे पूर्ववत नहीं किया जा सकता। उसी ऑर्डर के अन्य सब-ऑर्डर प्रभावित नहीं होते।',
        cancelDOReasonPlaceholder: 'कारण (वैकल्पिक) — आंतरिक टिप्पणी, प्रिंट नहीं होती',
        cancelDOPaidWarning: "इस चालान के विरुद्ध ₹{{amount}} पहले ही प्राप्त हो चुके हैं। रद्द करने पर वे रसीदें बिना लागू रह जाएँगी — उन्हें Payments Tracking से उलटें या दोबारा लागू करें।",
        cancelDOSuccess: 'डिलीवरी ऑर्डर सफलतापूर्वक रद्द किया गया',
        cancelDOFailed: 'डिलीवरी ऑर्डर रद्द करने में विफल',
        cancelDOSuperAdminOnly: 'चालान बनने के बाद केवल सुपर एडमिन ही रद्द कर सकता है',
        filters: {
            status: 'स्थिति',
            customer: 'ग्राहक',
            all: 'सभी',
            supervisor: 'सुपरवाइज़र',
            allSupervisors: 'सभी सुपरवाइज़र',
        },
        status: {
            pending: 'लंबित',
            ready_for_challan: 'चालान के लिए तैयार',
            supervisor_assigned: 'सुपरवाइज़र असाइन किया गया',
            loading: 'लोडिंग',
            loaded: 'लोड हो गया',
            in_transit: 'ट्रांज़िट में',
            delivered: 'डिलीवर हो गया',
            approved: 'स्वीकृत',
            completed: 'पूर्ण',
            rejected: 'अस्वीकृत',
            cancelled: 'रद्द',
            failed: 'विफल',
            delivery_assigned: 'डिलीवरी असाइन की गई',
        }
    },
    forms: {
        assignPersonnel: {
            title: 'डिलीवरी कर्मचारी असाइन करें',
            editTitle: 'डिलीवरी कर्मचारी संपादित करें',
            subtitle: 'इस ऑर्डर के लिए डिलीवरी सुपरवाइज़र और डिलीवरी बॉय चुनें।',
            labels: {
                supervisor: 'डिलीवरी सुपरवाइज़र',
                deliveryBoy: 'डिलीवरी बॉय',
                vehicleNumber: 'वाहन नंबर',
                driverContact: 'ड्राइवर संपर्क',
                vehicleNumberHint: 'फॉर्मेट: KA-53-1234, RJ14CA4241, RJ 14 CA 1424, RJ-14-CA-2414'
            },
            buttons: {
                cancel: 'रद्द करें',
                assign: 'असाइन करें',
                assigning: 'असाइन किया जा रहा है...',
                change: 'बदलें',
                changing: 'बदला जा रहा है...',
            },
            messages: {
                loadingUsers: 'यूज़र लोड हो रहे हैं...',
                selectAtLeastOne: 'कम से कम एक व्यक्ति चुनें',
                invalidOrder: 'अमान्य ऑर्डर',
                assignFailed: 'कर्मचारी असाइन करने में विफल',
                vehicleRequired: 'वाहन नंबर आवश्यक है',
                invalidVehicleNumber: 'कृपया एक वैध वाहन नंबर दर्ज करें।',
                driverContactRequired: 'ड्राइवर संपर्क आवश्यक है',
                invalidDriverContact: 'कृपया एक वैध नंबर दर्ज करें।'
            }
        },
        startTruck: {
            title: 'ट्रक लोडिंग शुरू करें',
            subtitle: 'ट्रक का विवरण दर्ज करें और लोडिंग शुरू करने के लिए मज़दूर चुनें।',
            labels: {
                vehicleNumber: 'वाहन नंबर',
                driverContact: 'ड्राइवर मोबाइल',
                emptyTruckWeight: 'खाली ट्रक वज़न (KG)',
                labours: 'मज़दूर चुनें',
                remark: 'टिप्पणी'
            },
            placeholders: {
                vehicleNumber: 'उदा., KA-53-1234',
                driverContact: 'उदा., 9876543210',
                emptyTruckWeight: 'वज़न KG में दर्ज करें',
                selectLabour: 'मज़दूर खोजें और चुनें...'
            },
            buttons: {
                cancel: 'रद्द करें',
                start: 'लोडिंग शुरू करें',
                starting: 'शुरू हो रहा है...'
            },
            messages: {
                loadingUsers: 'मज़दूर लोड हो रहे हैं...',
                selectAtLeastOneLabour: 'कृपया कम से कम एक मज़दूर चुनें',
                invalidOrder: 'अमान्य ऑर्डर',
                startFailed: 'लोडिंग शुरू करने में विफल',
                vehicleRequired: 'वाहन नंबर आवश्यक है',
                invalidEmptyTruckWeight: 'कृपया एक वैध खाली ट्रक वज़न दर्ज करें',
                invalidDriverContact: 'कृपया एक वैध 10-अंकीय मोबाइल नंबर दर्ज करें',
                noLaboursSelected: 'अभी तक कोई मज़दूर नहीं चुना गया'
            }
        },
        challan: {
            title: 'डिलीवरी चालान',
            subtitle: 'सब-ऑर्डर विवरण की समीक्षा करें और डिलीवरी चालान बनाएं।',
            selectDateLabel: 'चालान तारीख',
            onlyPastNote: 'केवल आज या पिछली तारीखों की अनुमति है।',
            labels: {
                expenses: 'खर्च',
                expenseGst: 'खर्च जीएसटी',
                discount: 'छूट',
                discountGst: 'छूट जीएसटी',
                remark: 'टिप्पणी',
                subOrder: 'सब-ऑर्डर #',
                customer: 'ग्राहक',
                supervisor: 'सुपरवाइज़र',
                vehicle: 'वाहन',
                itemsCount: 'आइटम संख्या',
                totalQty: 'कुल मात्रा',
                subtotal: 'उप-योग',
                freight: 'भाड़ा',
                loading: 'लोडिंग',
                gst: 'जीएसटी',
                totalAmount: 'कुल राशि',
                items: 'आइटम',
                challanDetails: 'चालान विवरण',
                requested: 'अनुरोधित',
                actualQty: 'वास्तविक मात्रा',
                weight: 'वज़न',
                price: 'कीमत',
                amount: 'राशि',
            },
            placeholders: {
                expenseRemark: 'खर्च का कारण...',
                discountRemark: 'छूट का कारण...',
                amount: '0.00',
            },
            buttons: {
                cancel: 'रद्द करें',
                generate: 'GRN बनाएं',
                generating: 'बनाया जा रहा है...'
            },
            messages: {
                fetching: 'सब-ऑर्डर विवरण लोड हो रहा है...',
                fetchError: 'ऑर्डर विवरण लाने में विफल',
            }
        },
        completeTruckLoading: {
            title: 'ट्रक लोडिंग पूर्ण करें',
            subtitle: 'आइटम मात्रा अपडेट करें और अंतिम ट्रक वज़न दर्ज करें।',
            labels: {
                orderId: 'ऑर्डर आईडी',
                truckWeight: 'अंतिम ट्रक वज़न (kg)',
                productName: 'प्रोडक्ट नाम',
                quantity: 'मात्रा',
                actualQuantity: 'वास्तविक मात्रा',
                items: 'आइटम',
                emptyTruckWeight: 'खाली ट्रक वज़न (kg)',
                theoreticalPerUnitWeight: 'सैद्धांतिक प्रति यूनिट (kg)',
                actualPerUnitWeight: 'वास्तविक प्रति यूनिट (kg)',
                actualWeight: 'वास्तविक वज़न',
                weightKg: 'कुल वज़न (kg)',
                lengthM: 'कुल लंबाई (m)',
                itemTotalWeight: 'आइटम कुल वज़न',
                itemTotalLength: 'आइटम कुल लंबाई',
                combinedTotalWeight: 'संयुक्त कुल वज़न',
                loadingCost: 'लोडिंग लागत',
                unknownProduct: 'अज्ञात प्रोडक्ट',
                unitLabel: 'यूनिट',
                lengthLabel: 'लंबाई',
                piecesLabel: 'पीस',
                approxWeight: 'अनुमानित वज़न',
                totalQuantity: 'कुल वज़न (m)',
                totalLoadingCost: 'कुल लोडिंग लागत',
                approxPerUnitWeight: 'अनुमानित प्रति यूनिट वज़न',
                approxPerPieceWeight: 'अनुमानित प्रति पीस वज़न',
                actualPerUnitWeightLabel: 'समायोजित प्रति यूनिट वज़न',
                actualPieces: 'वास्तविक पीस',
                perPieceWeight: 'प्रति पीस वज़न',
                adjustedQuantity: 'समायोजित मात्रा',
                remark: 'टिप्पणी',
            },
            messages: {
                loadingOrder: 'ऑर्डर विवरण लाया जा रहा है...',
                invalidOrder: 'ऑर्डर विवरण नहीं मिला या अमान्य है।',
                loadingTimeout: 'कनेक्शन का समय समाप्त हो गया। पुनः प्रयास किया जा रहा है...',
                noItems: 'इस सब-ऑर्डर में कोई आइटम नहीं मिला।',
                submitting: 'लोडिंग विवरण सहेजा जा रहा है...',
                success: 'ट्रक लोडिंग सफलतापूर्वक पूर्ण हुई।',
                error: 'लोडिंग पूर्ण करने में विफल।',
                weightsAdjusted: 'स्केल वज़न इस कारक से समायोजित: {factor}',
                dontLoadPartial: '* आंशिक लोड न करें',
                noPartialLoadingError: 'आइटम या तो पूरी तरह लोड होना चाहिए या पूरी तरह छोड़ा जाना चाहिए। लंबित मात्रा 0 या पूरे कुल मान के बराबर होनी चाहिए।',
                invalidWeight: 'कृपया एक वैध वज़न दर्ज करें।',
                invalidQuantity: 'कृपया वैध पीस और प्रति-पीस वज़न मान दर्ज करें।',
                completeFailed: 'ट्रक लोडिंग पूर्ण करने में विफल।',
            },
            placeholders: {
                emptyTruckWeight: 'खाली ट्रक वज़न दर्ज करें',
                truckWeight: 'अंतिम ट्रक वज़न दर्ज करें',
                remark: 'वैकल्पिक टिप्पणी...'
            },
            buttons: {
                cancel: 'रद्द करें',
                complete: 'लोडिंग पूर्ण करें',
                completing: 'पूर्ण किया जा रहा है...',
                saveDraft: 'ड्राफ्ट सहेजें',
                saving: 'सहेजा जा रहा है...',
            },
            multiStep: {
                weightEntriesTitle: 'वज़न प्रविष्टियाँ',
                saveIntermediateWeight: 'मध्यवर्ती वज़न सहेजें (वैकल्पिक)',
                saveButton: 'सहेजें',
                resetAll: 'सभी रीसेट करें',
                stepLabel: 'चरण',
                truckWeightLabel: 'ट्रक वज़न',
                weightDiffLabel: 'जोड़ा गया वज़न',
                itemsCountLabel: 'आइटम',
                savedInStep: 'चरण में सहेजा गया',
                willBeSaved: 'सहेजा जाएगा',
                itemsToSave: '{n} आइटम इस वज़न प्रविष्टि से लिंक किए जाएंगे',
                noItemsToSave: 'आइटम सहेजने के लिए वास्तविक मात्रा भरें',
                intermediateWeightPlaceholder: 'वर्तमान ट्रक वज़न दर्ज करें',
                confirmResetTitle: 'रीसेट की पुष्टि करें',
                confirmResetMessage: 'क्या आप वाकई सभी वज़न प्रविष्टियाँ रीसेट करना चाहते हैं? इससे सभी आइटम अनलॉक हो जाएंगे और उनके मान साफ़ हो जाएंगे।',
                confirmResetButton: 'रीसेट करें',
                cancelButton: 'रद्द करें',
                entrySavedSuccess: 'वज़न प्रविष्टि सहेजी गई',
                maxEntriesReached: 'अधिकतम 10 वज़न प्रविष्टियों की अनुमति है',
            },
        },
        updateTruckWeight: {
            title: 'ट्रक वज़न संपादित करें',
            subtitle: 'इस लोडिंग के लिए खाली और अंतिम ट्रक वज़न अपडेट करें।',
            labels: {
                emptyWeight: 'खाली ट्रक वज़न (kg)',
                finalWeight: 'अंतिम ट्रक वज़न (kg)',
            },
            placeholders: {
                emptyWeight: 'खाली वज़न दर्ज करें',
                finalWeight: 'अंतिम वज़न दर्ज करें',
            },
            buttons: {
                cancel: 'रद्द करें',
                update: 'वज़न अपडेट करें',
                updating: 'अपडेट किया जा रहा है...',
            },
            messages: {
                success: 'ट्रक वज़न सफलतापूर्वक अपडेट किया गया',
                error: 'ट्रक वज़न अपडेट करने में विफल',
            }
        }
    },
    view: {
        fields: {
            orderNumber: 'ऑर्डर नंबर',
            challanNumber: 'चालान नंबर',
            challanDate: 'चालान तारीख',
            customerName: 'ग्राहक',
            deliveryLocation: 'डिलीवरी लोकेशन',
            supervisorName: 'सुपरवाइज़र',
            vehicleNumber: 'वाहन नंबर',
            driverContact: 'ड्राइवर संपर्क',
            expectedDeliveryDate: 'अपेक्षित डिलीवरी तारीख',
            subtotal: 'उप-योग',
            freight: 'भाड़ा',
            loading: 'हैंडलिंग शुल्क',
            gst: 'जीएसटी',
            expenses: 'खर्च',
            expenseGst: 'खर्च जीएसटी',
            discount: 'छूट',
            discountGst: 'छूट जीएसटी',
            totalPayable: 'कुल देय',
            totalWeight: 'कुल वज़न',
            additionalInformation: 'अतिरिक्त जानकारी',
            hsnCodes: 'एचएसएन कोड',
            subOrderNumber: 'सब-ऑर्डर #',
            discountAmount: 'छूट राशि',
            totalAmount: 'कुल राशि',
            items: 'आइटम',
            item_product: 'प्रोडक्ट',
            item_amount: 'राशि',
            loadingInfo: 'लोडिंग जानकारी',
            metrics: 'मेट्रिक्स',
            status: 'स्थिति',
            emptyTruckWeight: 'खाली ट्रक वज़न',
            finalTruckWeight: 'अंतिम ट्रक वज़न',
            calculatedTruckWeight: 'गणना किया गया ट्रक वज़न',
            loadingCostPerKg: 'लोडिंग लागत (प्रति kg)',
            remark: 'टिप्पणी',
            vehicleRemark: 'वाहन टिप्पणी',
            price: 'कीमत',
            noItems: 'कोई आइटम नहीं मिला',
            headerTitle: 'डिलीवरी विवरण',
            item_qty: 'ऑर्डर मात्रा',
            item_weight: 'वज़न',
            item_length: 'लंबाई',
            item_actual_quantity: 'वास्तविक मात्रा',
            item_remark: 'टिप्पणी',
        }
    }
};

STRINGS.deliveryChallanModule = {
  common: {
    symbols: {
      required: "*",
      emDash: "—",
      currency: "₹",
    },
    buttons: {
      close: "बंद करें",
      download: "डाउनलोड",
      sendChallanEmail: "चालान भेजें",
      sendingEmail: "भेजा जा रहा है...",
    },
    units: {
      kg: "kg",
      m: "m",
    },
  },
  page: {
    header: {
      title: "डिलीवरी चालान",
      subtitle: "ट्रांज़िट और डिलीवर किए गए ऑर्डर ट्रैक करें",
    },
    stats: {
      total: "कुल",
    },
  },
  table: {
    columns: {
      subOrderNumber: "सब ऑर्डर नंबर",
      challanNumber: "डिलीवरी चालान नंबर",
      orderNumber: "ऑर्डर नंबर",
      customer: "ग्राहक",
      tallyName: "टैली नाम",
      location: "लोकेशन",
      totalQty: "कुल मात्रा",
      status: "स्थिति",
      actions: "कार्रवाई",
    },
    actions: {
      view: "विवरण देखें",
    },
    empty: {
      title: "कोई डिलीवरी चालान नहीं",
      message: "इस समय कोई ट्रांज़िट या डिलीवर किया गया ऑर्डर नहीं मिला।",
    },
  },
  actions: {
    searchPlaceholder: "चालान खोजें...",
    refresh: "रिफ्रेश",
    markDelivered: "डिलीवर के रूप में चिह्नित करें",
    confirmMarkDelivered: "क्या आप वाकई इसे डिलीवर के रूप में चिह्नित करना चाहते हैं?",
    markDeliveredSuccess: "ऑर्डर सफलतापूर्वक डिलीवर के रूप में चिह्नित किया गया",
    markDeliveredFailed: "डिलीवर के रूप में चिह्नित करने में विफल",
    orderDelivered: "ऑर्डर डिलीवर हो गया",
    operationFailed: "ऑपरेशन विफल",
    sendChallanEmail: "चालान भेजें",
    sendChallanEmailConfirm: "क्या आप वाकई ग्राहक को चालान भेजना चाहते हैं?",
    sendChallanEmailSuccess: "चालान सफलतापूर्वक भेजा गया",
    sendChallanEmailFailed: "चालान भेजने में विफल",
    editChallan: "चालान संपादित करें",
    editChallanSuccess: "चालान सफलतापूर्वक अपडेट किया गया",
    editChallanFailed: "चालान अपडेट करने में विफल",
    filters: {
      status: "स्थिति",
      all: "सभी",
    },
    status: {
      in_transit: "ट्रांज़िट में",
      delivered: "डिलीवर हो गया",
    },
  },
  editModal: {
    title: "डिलीवरी चालान संपादित करें",
    subtitle: "वाहन और वज़न विवरण अपडेट करें",
    labels: {
      vehicleNumber: "वाहन नंबर",
      emptyWeight: "खाली ट्रक वज़न (kg)",
      finalWeight: "अंतिम ट्रक वज़न (kg)",
      loadingCost: "प्रति KG लोडिंग लागत",
      freight: "फ्रेट",
      freightType: "फ्रेट प्रकार",
      pricingType: "प्राइसिंग प्रकार",
      remark: "टिप्पणी",
      orderRemark: "ऑर्डर टिप्पणी",
      itemRemark: "आइटम टिप्पणी",
    },
    placeholders: {
      remark: "एक टिप्पणी जोड़ें...",
    },
    buttons: {
      save: "बदलाव सहेजें",
      saving: "सहेजा जा रहा है...",
      cancel: "रद्द करें",
    }
  }
};

STRINGS.purchaseOrdersModule = {
    common: {
        symbols: {
            required: '*',
            emDash: '—',
            currency: '₹',
        },
        buttons: {
            refresh: 'रिफ्रेश',
            delete: 'हटाएं',
            cancel: 'रद्द करें',
            ok: 'ठीक है',
            save: 'सहेजें',
        },
        units: {
            kg: 'kg',
            m: 'm',
        },
        loading: 'लोड हो रहा है...',
        table: {
            empty: {
                title: 'कोई परचेज़ ऑर्डर नहीं मिला',
                message: 'अभी दिखाने के लिए कोई परचेज़ ऑर्डर नहीं है।',
            },
        },
    },
    purchaseOrders: {
        page: {
            header: {
                title: 'परचेज़ ऑर्डर',
                subtitle: 'परचेज़ ऑर्डर, डिलीवरी और इनवॉइस प्रबंधित करें',
            },
        },
        table: {
            columns: {
                poNumber: 'पीओ नंबर',
                date: 'तारीख',
                supplier: 'सप्लायर',
                deliveryLocation: 'डिलीवरी लोकेशन',
                totalAmount: 'कुल राशि',
                status: 'स्थिति',
                statusActions: 'स्थिति क्रियाएं',
                actions: 'क्रियाएं',
            },
            tooltips: {
                view: 'परचेज़ ऑर्डर देखें',
                edit: 'परचेज़ ऑर्डर संपादित करें',
                delete: 'परचेज़ ऑर्डर हटाएं',
                confirm: 'परचेज़ ऑर्डर की पुष्टि करें',
                cancel: 'परचेज़ ऑर्डर रद्द करें',
                complete: 'पूर्ण चिह्नित करें',
                printChallan: 'चालान प्रिंट करें',
                generateAdvice: 'एडवाइस जेनरेट करें',
                assignSupervisor: 'सुपरवाइज़र असाइन करें',
                startLoading: 'लोडिंग शुरू करें',
                completeLoading: 'लोडिंग पूर्ण करें',
                generateChallan: 'चालान जेनरेट करें',
                markReceived: 'प्राप्त चिह्नित करें',
                goToAdvice: 'एडवाइस पर जाएं',
                markAllAdvised: 'सभी को एडवाइज़ चिह्नित करें',
            },
            messages: {
                notAllReceived: 'सभी ऑर्डर अभी तक प्राप्त नहीं हुए हैं।',
                purchaseCompleted: 'परचेज़ पूर्ण हुई',
                supervisorUnloading: 'सुपरवाइज़र इसे करेंगे',
                purchaseCancelled: 'परचेज़ ऑर्डर रद्द किया गया',
            },
        },
        statuses: {
            pending: 'लंबित',
            ready_for_challan: 'चालान के लिए तैयार',
            confirmed: 'पुष्टि हुई',
            partial_advice_created: 'आंशिक एडवाइस बनाई गई',
            advice_created: 'एडवाइस बनाई गई',
            supervisor_assigned: 'सुपरवाइज़र असाइन किया गया',
            loading: 'लोडिंग',
            loaded: 'लोड हो गया',
            in_progress: 'प्रगति में',
            in_transit: 'ट्रांज़िट में',
            unloading: 'अनलोडिंग',
            unloaded: 'अनलोड हो गया',
            received: 'प्राप्त हुआ',
            challan_generated: 'चालान जेनरेट हुआ',
            completed: 'पूर्ण हुआ',
            cancelled: 'रद्द किया गया',
        },
        actions: {
            searchPlaceholder: 'परचेज़ ऑर्डर खोजें...',
            buttons: {
                add: 'परचेज़ ऑर्डर जोड़ें',
            },
            filters: {
                all: 'सभी',
                status: 'स्थिति',
                supplier: 'सप्लायर',
                location: 'लोकेशन',
            },
        },
        view: {
            header: {
                sectionTitle: 'परचेज़ ऑर्डर विवरण',
                fallbackTitle: 'परचेज़ ऑर्डर',
            },
            loading: 'परचेज़ ऑर्डर विवरण लोड हो रहा है...',
            empty: {
                title: 'परचेज़ ऑर्डर डेटा उपलब्ध नहीं है',
                message: 'हम चयनित परचेज़ ऑर्डर लोड नहीं कर सके। कृपया पुनः प्रयास करें।',
            },
            fields: {
                poNumber: 'पीओ नंबर',
                date: 'तारीख',
                supplierName: 'सप्लायर का नाम',
                contactPerson: 'संपर्क व्यक्ति',
                mobileNumber: 'मोबाइल नंबर',
                warehouseName: 'वेयरहाउस',
                deliveryLocation: 'डिलीवरी लोकेशन',
                expectedDeliveryDate: 'अपेक्षित डिलीवरी तारीख',
                status: 'स्थिति',
                remark: 'टिप्पणी',
                subtotal: 'उप-योग',
                discountType: 'डिस्काउंट प्रकार',
                discountValue: 'डिस्काउंट मूल्य',
                discountAmount: 'डिस्काउंट राशि',
                gstIncluded: 'जीएसटी शामिल',
                pricingOption: 'प्राइसिंग विकल्प',

                totalAmount: 'कुल राशि',
                freight: 'फ्रेट',
                loadingCost: 'लोडिंग लागत',
                paymentTerms: 'भुगतान शर्तें',
                adviceNumber: 'एडवाइस नंबर',
                adviceDate: 'एडवाइस तारीख',
                adviceGenerated: 'एडवाइस जेनरेट हुई',
                challanNumber: 'चालान नंबर',
                challanDate: 'चालान तारीख',
                challanGenerated: 'चालान जेनरेट हुआ',
                supplierInvoiceNumber: 'सप्लायर इनवॉइस नंबर',
                supplierInvoiceDate: 'सप्लायर इनवॉइस तारीख',
                vehicleNumber: 'वाहन नंबर',
                emptyTruckWeight: 'खाली ट्रक वज़न',
                finalTruckWeight: 'अंतिम ट्रक वज़न',
                truckWeight: 'ट्रक वज़न',
                presentShipping: 'वर्तमान शिपिंग',
                unloadedByName: 'अनलोड करने वाला',
                totalQuantity: 'कुल मात्रा',
                pendingQuantity: 'लंबित मात्रा',
                actualQuantity: 'वास्तविक मात्रा',
                totalItems: 'कुल आइटम',
                items: 'आइटम',
                item_product: 'प्रोडक्ट',
                item_size: 'साइज़',
                item_qty: 'मात्रा',
                item_pieces: 'पीस',
                item_price: 'कीमत',
                item_amount: 'राशि',
                item_hsn: 'एचएसएन',
                item_gst_percentage: 'जीएसटी %',
                item_remark: 'टिप्पणी',
            },
            advices: {
                title: 'एडवाइस',
                labels: {
                    adviceNumber: 'एडवाइस नंबर',
                    date: 'तारीख',
                    status: 'स्थिति',
                    quantityInAdvice: 'एडवाइस मात्रा',
                    actualReceived: 'प्राप्त',
                    vehicleNumber: 'वाहन',
                    challanNumber: 'चालान',
                },
                empty: "एडवाइस अभी तक जेनरेट नहीं हुई हैं।",
            },
            messages: {
                no_items_available: 'कोई आइटम उपलब्ध नहीं',
            },
            buttons: {
                close: 'बंद करें',
            },
        },
        confirm: {
            cancel: {
                title: 'परचेज़ ऑर्डर रद्द करें',
                message: 'क्या आप वाकई परचेज़ ऑर्डर {poNumber} रद्द करना चाहते हैं?',
                confirmLabel: 'हाँ, रद्द करें',
                cancelLabel: 'नहीं, रखें',
            },
            confirm: {
                title: 'परचेज़ ऑर्डर की पुष्टि करें',
                message: 'क्या आप वाकई परचेज़ ऑर्डर {poNumber} की पुष्टि करना चाहते हैं?',
                confirmLabel: 'हाँ, पुष्टि करें',
                cancelLabel: 'रद्द करें',
            },
        },
        confirmations: {
            complete: {
                title: 'पूर्ण चिह्नित करें',
                message: 'क्या आप वाकई इस परचेज़ ऑर्डर को पूर्ण चिह्नित करना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।',
                confirm: 'हाँ, पूर्ण चिह्नित करें',
            },
        },
        forms: {
            title: {
                add: 'परचेज़ ऑर्डर जोड़ें',
                edit: 'परचेज़ ऑर्डर संपादित करें',
            },
            headings: {
                supplierInformation: 'सप्लायर जानकारी',
                orderDetails: 'ऑर्डर विवरण',
            },
            customerModes: {
                existing: 'मौजूदा सप्लायर',
                new: 'नया सप्लायर',
            },
            labels: {
                customerMode: 'सप्लायर मोड',
            },
            adviceForm: {
                title: 'परचेज़ एडवाइस जेनरेट करें',
                headings: {
                    items: 'एडवाइज़ करने के लिए आइटम',
                },
                fields: {
                    adviceDate: {
                        label: 'एडवाइस तारीख',
                        placeholder: 'एडवाइस तारीख चुनें',
                    },
                    paymentTerms: {
                        label: 'भुगतान शर्तें',
                        placeholder: 'भुगतान शर्तें दर्ज करें',
                    },
                    vehicleNumber: {
                        label: 'वाहन नंबर',
                        placeholder: 'जैसे, KA-53-1234',
                        hint: 'फॉर्मेट: KA-53-1234, RJ14CA4241, RJ 14 CA 1424, RJ-14-CA-2414',
                    },
                    supplierInvoiceNumber: {
                        label: 'सप्लायर इनवॉइस नंबर',
                        placeholder: 'इनवॉइस नंबर दर्ज करें',
                    },
                    supplierInvoiceDate: {
                        label: 'सप्लायर इनवॉइस तारीख',
                        placeholder: 'इनवॉइस तारीख चुनें',
                    },
                    remark: {
                        label: 'टिप्पणी',
                        placeholder: 'यहां कोई अतिरिक्त नोट जोड़ें...',
                    },
                },
                buttons: {
                    generate: 'एडवाइस जेनरेट करें',
                    cancel: 'रद्द करें',
                },
                table: {
                    columns: {
                        product: 'प्रोडक्ट',
                        orderedQty: 'ऑर्डर की गई मात्रा',
                        pendingQty: 'लंबित मात्रा',
                        adviceQty: 'एडवाइस मात्रा',
                        remainingQty: 'शेष मात्रा',
                    },
                },
                errors: {
                    adviceDateRequired: 'एडवाइस तारीख आवश्यक है',
                    vehicleNumberRequired: 'वाहन नंबर आवश्यक है',
                    invalidQuantity: 'अमान्य मात्रा',
                    quantityExceeds: 'ऑर्डर की गई मात्रा से अधिक नहीं हो सकता',
                    supplierInvoiceNumberRequired: 'सप्लायर इनवॉइस नंबर आवश्यक है',
                },
            },
            assignSupervisor: {
                title: 'सुपरवाइज़र असाइन करें',
                subtitle: 'डिलीवरी सुपरवाइज़र और ड्राइवर विवरण असाइन करें',
                labels: {
                    supervisor: 'डिलीवरी सुपरवाइज़र',
                    driverContact: 'ड्राइवर संपर्क',
                    vehicleRemark: 'वाहन टिप्पणी',
                },
                placeholders: {
                    supervisor: 'सुपरवाइज़र चुनें',
                    driverContact: 'ड्राइवर संपर्क दर्ज करें',
                    vehicleRemark: 'वाहन टिप्पणी दर्ज करें',
                },
                buttons: {
                    assign: 'सुपरवाइज़र असाइन करें',
                    assigning: 'असाइन हो रहा है...',
                    cancel: 'रद्द करें',
                },
                messages: {
                    loadingUsers: 'सुपरवाइज़र लोड हो रहे हैं...',
                    invalidAdvice: 'अमान्य एडवाइस आईडी',
                    selectSupervisor: 'कृपया एक सुपरवाइज़र चुनें',
                    driverContactRequired: 'ड्राइवर संपर्क आवश्यक है',
                    invalidDriverContact: 'अमान्य ड्राइवर संपर्क नंबर',
                    assignFailed: 'सुपरवाइज़र असाइन करने में विफल',
                },
            },
            startTruckLoadingForm: {
                title: 'ट्रक लोडिंग शुरू करें',
                fields: {
                    poNumber: 'पीओ नंबर',
                    adviceNumber: 'एडवाइस नंबर',
                    vehicleNumber: {
                        label: 'वाहन नंबर',
                        placeholder: 'वाहन नंबर दर्ज करें',
                    },
                    emptyWeight: {
                        label: 'खाली ट्रक वज़न (kg)',
                        placeholder: 'वज़न kg में दर्ज करें',
                    },
                    remark: {
                        label: 'टिप्पणी',
                        placeholder: 'टिप्पणी दर्ज करें',
                    },
                },
                buttons: {
                    start: 'लोडिंग शुरू करें',
                    cancel: 'रद्द करें',
                },
                errors: {
                    vehicleNumberRequired: 'वाहन नंबर आवश्यक है',
                    emptyWeightRequired: 'खाली ट्रक वज़न आवश्यक है',
                    invalidWeight: 'अमान्य वज़न',
                },
            },
            completeLoadingForm: {
                title: 'ट्रक लोडिंग पूर्ण करें',
                subtitle: 'लोड किए गए आइटम की समीक्षा करें और ट्रक वज़न अंतिम करें।',
                headings: {
                    items: 'लोड किए गए आइटम',
                },
                fields: {
                    poNumber: 'पीओ नंबर',
                    adviceNumber: 'एडवाइस नंबर',
                    emptyWeight: {
                        label: 'खाली ट्रक वज़न (kg)',
                    },
                    finalWeight: {
                        label: 'अंतिम ट्रक वज़न (kg)',
                        placeholder: 'वज़न kg में दर्ज करें',
                    },
                },
                buttons: {
                    complete: 'लोडिंग पूर्ण करें',
                    cancel: 'रद्द करें',
                },
                errors: {
                    finalWeightRequired: 'अंतिम ट्रक वज़न आवश्यक है',
                    invalidWeight: 'अमान्य वज़न',
                    invalidQuantity: 'अमान्य मात्रा',
                },
            },
            completeUnloadingForm: {
                title: 'ट्रक अनलोडिंग पूर्ण करें',
                subtitle: 'अनलोड किए गए आइटम की समीक्षा करें और ट्रक वज़न अंतिम करें।',
                headings: {
                    items: 'अनलोड किए गए आइटम',
                },
                placeholders: {
                    finalWeight: 'वज़न kg में दर्ज करें',
                    truckWeight: 'गणना किया गया ट्रक वज़न',
                },
                fields: {
                    poNumber: 'पीओ नंबर',
                    adviceNumber: 'एडवाइस नंबर',
                    loadedWeight: 'लोड किया गया वज़न',
                    emptyWeight: {
                        label: 'खाली ट्रक वज़न (kg)',
                    },
                    finalWeight: {
                        label: 'अंतिम ट्रक वज़न (kg)',
                        placeholder: 'वज़न kg में दर्ज करें',
                    },
                    unloadedQty: 'अनलोड की गई मात्रा',
                    ordered: 'ऑर्डर किया गया',
                    approx: 'लगभग',
                    totalWeight: 'कुल वज़न',
                },
                buttons: {
                    complete: 'अनलोडिंग पूर्ण करें',
                    cancel: 'रद्द करें',
                },
                errors: {
                    finalWeightRequired: 'अंतिम ट्रक वज़न आवश्यक है',
                    invalidWeight: 'अमान्य वज़न',
                    invalidQuantity: 'अमान्य मात्रा',
                },
            },
            startUnloadingForm: {
                title: 'अनलोडिंग शुरू करें',
                subtitle: 'अनलोडिंग प्रक्रिया शुरू करने के लिए अंतिम ट्रक वज़न दर्ज करें।',
                fields: {
                    poNumber: 'पीओ नंबर',
                    adviceNumber: 'एडवाइस नंबर',
                    finalWeight: {
                        label: 'लोड किया गया ट्रक वज़न (kg)',
                        placeholder: 'वज़न kg में दर्ज करें',
                    },
                    remark: {
                        label: 'टिप्पणी',
                        placeholder: 'टिप्पणी दर्ज करें',
                    },
                },
                buttons: {
                    start: 'अनलोडिंग शुरू करें',
                    cancel: 'रद्द करें',
                },
                errors: {
                    finalWeightRequired: 'अंतिम ट्रक वज़न आवश्यक है',
                    invalidWeight: 'अमान्य वज़न',
                },
            },
            generateChallanForm: {
                title: 'परचेज़ चालान जेनरेट करें',
                subtitle: 'एडवाइस विवरण की समीक्षा करें और समायोजन दर्ज करें',
                fields: {
                    adviceNumber: 'एडवाइस नंबर',
                    supplier: 'सप्लायर',
                    poNumber: 'पीओ नंबर',
                    challanDate: 'चालान तारीख',
                    expenses: 'खर्च',
                    expenseRemark: 'खर्च टिप्पणी',
                    discount: 'डिस्काउंट',
                    discountRemark: 'डिस्काउंट टिप्पणी',
                    totalQuantity: 'कुल मात्रा',
                    totalValue: 'कुल मूल्य',
                },
                headings: {
                    details: 'एडवाइस विवरण',
                    items: 'आइटम',
                    adjustments: 'वित्तीय समायोजन',
                    summary: 'वित्तीय सारांश',
                },
                table: {
                    columns: {
                        product: 'प्रोडक्ट',
                        size: 'साइज़',
                        requested: 'अनुरोधित',
                        actualQty: 'वास्तविक मात्रा',
                        poQty: 'पीओ आइटम मात्रा',
                        rate: 'दर',
                        total: 'कुल कीमत',
                        remark: 'टिप्पणी',
                    },
                },
                summaryLabels: {
                    itemsTotal: 'आइटम कुल',
                    gst: 'जीएसटी कुल',
                    subtotal: 'उप-योग (आइटम + जीएसटी)',
                    expenses: 'खर्च (+)',
                    discount: 'डिस्काउंट (-)',
                    grandTotal: 'महायोग',
                },
                buttons: {
                    generate: 'चालान जेनरेट करें',
                    generating: 'जेनरेट हो रहा है...',
                    cancel: 'रद्द करें',
                },
                messages: {
                    loading: 'एडवाइस विवरण लोड हो रहा है...',
                    fetchFailed: 'एडवाइस विवरण लोड करने में विफल',
                },
                errors: {
                    dateRequired: 'चालान तारीख आवश्यक है',
                },
            },
            markAsReceivedForm: {
                title: 'प्राप्त चिह्नित करें',
                subtitle: 'प्राप्त मात्रा और अनलोडिंग विवरण सत्यापित करें।',
                confirmMessage: 'क्या आप वाकई इस परचेज़ एडवाइस को प्राप्त चिह्नित करना चाहते हैं?',
                headings: {
                    items: 'प्राप्त आइटम',
                },
                fields: {
                    unloadedBy: {
                        label: 'अनलोड करने वाला',
                        placeholder: 'सुपरवाइज़र चुनें',
                    },
                    freight: {
                        label: 'फ्रेट लागत',
                    },
                    loadingCost: {
                        label: 'अनलोडिंग लागत',
                    },
                    labours: {
                        label: 'मज़दूर चुनें',
                    },
                    receivedQuantity: {
                        label: 'प्राप्त मात्रा',
                    },
                    remark: {
                        label: 'टिप्पणी',
                        placeholder: 'कोई टिप्पणी...',
                    },
                },
                messages: {
                    labourHelp: 'सूची में जोड़ने के लिए ड्रॉपडाउन से एक मज़दूर चुनें।',
                    noLaboursSelected: 'कोई मज़दूर चयनित नहीं',
                    noItemsFound: 'कोई आइटम नहीं मिला।',
                    orderedPrefix: 'ऑर्डर किया गया:',
                    approxWeight: 'लगभग वज़न:',
                    unloadingCost: 'अनलोडिंग लागत:',
                },
                buttons: {
                    markReceived: 'प्राप्त चिह्नित करें',
                    cancel: 'रद्द करें',
                },
                errors: {
                    unloadedByRequired: 'अनलोड करने वाला आवश्यक है',
                    invalidQuantity: 'अमान्य मात्रा',
                },
            },
            fields: {
                supplier: {
                    label: 'सप्लायर',
                    placeholder: 'सप्लायर चुनें',
                },
                shippingDestination: {
                    label: 'शिपिंग गंतव्य',
                    options: {
                        warehouse: 'वेयरहाउस',
                        customer: 'ग्राहक',
                    },
                },
                customer: {
                    label: 'ग्राहक',
                    placeholder: 'ग्राहक चुनें',
                },
                warehouse: {
                    label: 'वेयरहाउस',
                    placeholder: 'वेयरहाउस चुनें',
                },
                deliveryLocation: {
                    label: 'डिलीवरी लोकेशन',
                    placeholder: 'डिलीवरी लोकेशन चुनें',
                },
                contactPerson: {
                    label: 'संपर्क व्यक्ति',
                    placeholder: 'संपर्क व्यक्ति का नाम दर्ज करें',
                },
                mobileNumber: {
                    label: 'मोबाइल नंबर',
                    placeholder: 'मोबाइल नंबर दर्ज करें',
                },
                expectedDeliveryDate: {
                    label: 'अपेक्षित डिलीवरी तारीख',
                    placeholder: 'DD/MM/YYYY',
                },
                paymentTerms: {
                    label: 'भुगतान शर्तें',
                    placeholder: 'भुगतान शर्तें दर्ज करें',
                },
                discountType: {
                    label: 'डिस्काउंट प्रकार',
                    options: {
                        percentage: 'प्रतिशत',
                        flat: 'फ्लैट राशि',
                    },
                },
                discountValue: {
                    label: 'डिस्काउंट मूल्य',
                    placeholder: 'डिस्काउंट दर्ज करें',
                },
                gstIncluded: {
                    label: 'जीएसटी शामिल',
                },
                pricingOption: {
                    label: 'प्राइसिंग विकल्प',
                    options: {
                        all_inclusive: 'सभी शामिल',
                        plus_taxes: 'प्लस टैक्स',
                    },
                },
                tcsApplicable: {
                    label: 'टीसीएस लागू',
                },
                tcsPercentage: {
                    label: 'टीसीएस प्रतिशत',
                    placeholder: 'टीसीएस % दर्ज करें',
                },
                smsNotification: {
                    label: 'एसएमएस नोटिफिकेशन',
                },
                remark: {
                    label: 'टिप्पणी',
                    placeholder: 'टिप्पणी दर्ज करें',
                },
                items: {
                    label: 'आइटम',
                    productSize: {
                        label: 'प्रोडक्ट साइज़',
                        placeholder: 'प्रोडक्ट साइज़ चुनें',
                    },
                    unit: {
                        label: 'यूनिट',
                        placeholder: 'यूनिट चुनें',
                    },
                    length: {
                        label: 'लंबाई',
                        placeholder: 'लंबाई दर्ज करें',
                    },
                    quantity: {
                        label: 'मात्रा',
                        placeholder: 'मात्रा दर्ज करें',
                    },
                    price: {
                        label: 'कीमत',
                        placeholder: 'कीमत दर्ज करें',
                    },
                    remark: {
                        label: 'टिप्पणी',
                        placeholder: 'आइटम टिप्पणी दर्ज करें',
                    },
                    approxWeight: 'लगभग वज़न',
                    pieces: {
                        label: 'पीस',
                        placeholder: 'मात्रा',
                    },
                },
            },
            buttons: {
                next: 'आगे',
                addItem: 'आइटम जोड़ें',
                submit: {
                    add: 'परचेज़ ऑर्डर बनाएं',
                    edit: 'परचेज़ ऑर्डर अपडेट करें',
                },
            },
            loading: {
                suppliers: 'सप्लायर लोड हो रहे हैं...',
                warehouses: 'वेयरहाउस लोड हो रहे हैं...',
                locations: 'लोकेशन लोड हो रहे हैं...',
            },
            errors: {
                supplierRequired: 'सप्लायर आवश्यक है',
                customerRequired: 'ग्राहक आवश्यक है',
                warehouseRequired: 'वेयरहाउस आवश्यक है',
                locationRequired: 'डिलीवरी लोकेशन आवश्यक है',
                productSizeRequired: 'प्रोडक्ट साइज़ आवश्यक है',
                quantityRequired: 'मात्रा आवश्यक है',
                priceRequired: 'कीमत आवश्यक है',
            },
        },
    },
    purchaseChallanPrint: {
        title: 'Print Purchase Advice Challan',
        loading: 'Loading challan details...',
        noData: 'No data available',
        fields: {
            challanDate: 'Challan Date',
            vehicleNumber: 'Vehicle Number',
            driverContact: 'Driver Contact',
            emptyTruckWeight: 'Empty Truck Weight',
            finalTruckWeight: 'Final Truck Weight',
            orderFor: 'Order For',
        },
        items: {
            title: 'Items',
            qty: 'Qty',
            price: 'Price',
            amount: 'Amount',
        },
    },
    locations: {
        defaults: {
            country: 'India',
        },
    },
    customers: {
        forms: {
            fields: {
                ownerName: { label: 'मालिक का नाम', placeholder: 'मालिक का नाम दर्ज करें', errors: { required: 'मालिक का नाम आवश्यक है' } },
                companyName: { label: 'कंपनी का नाम', placeholder: 'कंपनी का नाम दर्ज करें', errors: { required: 'कंपनी का नाम आवश्यक है' } },
                tallyName: { label: 'टैली नाम', placeholder: 'टैली नाम दर्ज करें', errors: { required: 'टैली नाम आवश्यक है' } },
                gstNumber: { label: 'जीएसटी नंबर', placeholder: 'जीएसटी नंबर दर्ज करें', errors: { required: 'जीएसटी नंबर आवश्यक है' } },
                contactPerson: { label: 'संपर्क व्यक्ति', placeholder: 'संपर्क व्यक्ति दर्ज करें', errors: { required: 'संपर्क व्यक्ति आवश्यक है' } },
                phoneNumber1: { label: 'फोन नंबर 1', placeholder: 'फोन नंबर दर्ज करें', errors: { invalid: 'अमान्य फोन नंबर' } },
                email: { label: 'ईमेल', placeholder: 'ईमेल दर्ज करें', errors: { invalid: 'अमान्य ईमेल' } },
                sisterCompany: { label: 'सिस्टर कंपनी', placeholder: 'सिस्टर कंपनी दर्ज करें', errors: { required: 'सिस्टर कंपनी आवश्यक है' } },
                address1: { label: 'पता पंक्ति 1', placeholder: 'पता दर्ज करें', errors: { required: 'पता आवश्यक है' } },
                city: { label: 'शहर', placeholder: 'शहर दर्ज करें', errors: { required: 'शहर आवश्यक है' } },
                state: { label: 'राज्य', placeholder: 'राज्य दर्ज करें', errors: { required: 'राज्य आवश्यक है' } },
                country: { label: 'देश', placeholder: 'देश दर्ज करें', errors: { required: 'देश आवश्यक है' } },
                zipCode: { label: 'ज़िप कोड', placeholder: 'ज़िप कोड दर्ज करें', errors: { required: 'ज़िप कोड आवश्यक है' } },
                creditPeriod: { label: 'क्रेडिट अवधि', placeholder: 'क्रेडिट अवधि दर्ज करें', errors: { required: 'क्रेडिट अवधि आवश्यक है' } },
            },
        },
    },
};

STRINGS.orders = STRINGS.ordersModule.orders;
STRINGS.purchaseOrders = STRINGS.purchaseOrdersModule.purchaseOrders;

// i18n keys added to fill migration gaps
STRINGS.customers = deepMerge(STRINGS.customers || {}, {"notifications":{"bulkDeleted":"{count} ग्राहक हटाए गए"}});
STRINGS.hsnCodes = deepMerge(STRINGS.hsnCodes || {}, {"notifications":{"created":"एचएसएन कोड सफलतापूर्वक बनाया गया","updated":"एचएसएन कोड सफलतापूर्वक अपडेट किया गया","deleted":"एचएसएन कोड सफलतापूर्वक हटाया गया","bulkDeleted":"{count} एचएसएन कोड हटाए गए"}});
STRINGS.inquiries = deepMerge(STRINGS.inquiries || {}, {"notifications":{"converted":"पूछताछ सफलतापूर्वक ऑर्डर में बदली गई"}});
STRINGS.inquiriesModule = deepMerge(STRINGS.inquiriesModule || {}, {"inquiries":{"errors":{"noneSelected":"कोई पूछताछ चयनित नहीं"},"notifications":{"whatsappSent":"WhatsApp सफलतापूर्वक भेजा गया"}}});
STRINGS.ipAddresses = deepMerge(STRINGS.ipAddresses || {}, {"notifications":{"added":"आईपी एड्रेस सफलतापूर्वक जोड़ा गया","updated":"आईपी एड्रेस सफलतापूर्वक अपडेट किया गया","removed":"आईपी एड्रेस सफलतापूर्वक हटाया गया","activated":"आईपी एड्रेस सक्रिय किया गया","deactivated":"आईपी एड्रेस निष्क्रिय किया गया","bulkAdded":"{count} आईपी एड्रेस जोड़े गए"}});
STRINGS.locations = deepMerge(STRINGS.locations || {}, {"notifications":{"created":"लोकेशन सफलतापूर्वक बनाई गई","updated":"लोकेशन सफलतापूर्वक अपडेट की गई","deleted":"लोकेशन सफलतापूर्वक हटाई गई","statusUpdated":"लोकेशन स्थिति अपडेट की गई","bulkDeletedByName":"हटाया गया: {names}"}});
STRINGS.orders = deepMerge(STRINGS.orders || {}, {"notifications":{"draftSaved":"ड्राफ्ट सहेजा गया"}});
STRINGS.ordersModule = deepMerge(STRINGS.ordersModule || {}, {"orders":{"errors":{"noSelection":"कोई ऑर्डर चयनित नहीं"}}});
STRINGS.productCatalogue = deepMerge(STRINGS.productCatalogue || {}, {"notifications":{"bulkDeletedByName":"हटाया गया: {names}"}});
// `tax_percentage` is not a product_categories column and the importer reads only
// `name`; the modal passes its own templateHeaders anyway, so this override was
// both dead and wrong. Left out rather than corrected.
STRINGS.purchaseAdvice = deepMerge(STRINGS.purchaseAdvice || {}, {"notifications":{"updated":"परचेज़ एडवाइस सफलतापूर्वक अपडेट की गई"},"view":{"fields":{"receivedQty":"प्राप्त मात्रा"}}});
STRINGS.purchaseOrders = deepMerge(STRINGS.purchaseOrders || {}, {"forms":{"completeUnloadingForm":{"fields":{"loadedWeight":{"label":"लोडेड वज़न"}}},"startTruckLoadingForm":{"subtitle":"ट्रक लोडिंग विवरण दर्ज करें"}}});
STRINGS.taxes = deepMerge(STRINGS.taxes || {}, {"notifications":{"created":"टैक्स सफलतापूर्वक बनाया गया","updated":"टैक्स सफलतापूर्वक अपडेट किया गया","deleted":"टैक्स सफलतापूर्वक हटाया गया","bulkDeletedMultiple":"{count} टैक्स हटाए गए","bulkDeletedSingle":"टैक्स हटाया गया"}});
STRINGS.users = deepMerge(STRINGS.users || {}, {"forms":{"passwordReset":{"heading":"पासवर्ड बदलें","helper":"ऊपर दोनों पासवर्ड फ़ील्ड भरें, फिर रीसेट करने के लिए क्लिक करें।","resetting":"रीसेट हो रहा है...","submit":"अभी पासवर्ड अपडेट करें","success":"पासवर्ड सफलतापूर्वक रीसेट किया गया","errorLabel":"पासवर्ड रीसेट करने में विफल","unexpectedError":"एक अप्रत्याशित त्रुटि हुई"}}});

STRINGS.users = deepMerge(STRINGS.users || {}, {"forms":{"removeWarehouse":"हटाएं","submitFallbackError":"यूज़र सहेजने में विफल। कृपया विवरण जाँचें और पुनः प्रयास करें।","warehouseFallback":"वेयरहाउस {id}"}});

STRINGS.commonCols = deepMerge(STRINGS.commonCols || {}, {"remarks":"टिप्पणियाँ","orderRemarks":"ऑर्डर टिप्पणियाँ","vehicleNumber":"वाहन नंबर","truckNumber":"ट्रक नंबर","truckNo":"ट्रक नंबर","updateStatus":"स्थिति अपडेट करें","status":"स्थिति","location":"लोकेशन","amount":"राशि","qty":"मात्रा","totalItems":"कुल आइटम","tallyName":"टैली नाम","challanNo":"चालान नंबर","serialNumber":"सीरियल नंबर","supervisor":"सुपरवाइज़र","unloadedBy":"अनलोड करने वाला","orderedBy":"ऑर्डर करने वाला","labours":"मज़दूर","name":"नाम","print":"प्रिंट","export":"एक्सपोर्ट","from":"से","perKg":"प्रति किग्रा","pending":"लंबित","confirmedOrders":"पुष्ट ऑर्डर","vehicleTracking":"वाहन ट्रैकिंग","aiAdvisor":"एआई सलाहकार"});

STRINGS.navigation = deepMerge(STRINGS.navigation || {}, { pages: {"challan":"चालान","operations":"ऑपरेशंस","movement":"मूवमेंट","supervisorStaff":"सुपरवाइज़र स्टाफ","salesStaff":"सेल्स स्टाफ","deliveryChallanNav":"डिलीवरी चालान","deliveryChallanPrinted":"डिलीवरी चालान प्रिंटेड","purchaseChallanPrinted":"परचेज़ चालान प्रिंटेड","confirmedOrders":"पुष्ट ऑर्डर","vehicleTracking":"वाहन ट्रैकिंग"} });

STRINGS.navigation = deepMerge(STRINGS.navigation || {}, { pages: {"supervisorAnalytics":"सुपरवाइज़र एनालिटिक्स","warehouseInventory":"वेयरहाउस इन्वेंटरी","challanPrintVariance":"चालान प्रिंट अंतर","challanAnalytics":"चालान एनालिटिक्स","movementAnalysis":"मूवमेंट एनालिसिस","myProfile":"मेरी प्रोफ़ाइल"} });

STRINGS.commonCols = deepMerge(STRINGS.commonCols || {}, {"statusOptions":{"all":"सभी","pending":"लंबित","confirmed":"पुष्ट","deliveryAssigned":"डिलीवरी असाइन","truckLoading":"ट्रक लोडिंग","truckLoaded":"ट्रक लोडेड","inTransit":"ट्रांज़िट में","delivered":"डिलीवर हो गया","invoiced":"इनवॉइस हो गया","readyForChallan":"चालान के लिए तैयार","supervisorAssigned":"सुपरवाइज़र असाइन","unloading":"अनलोडिंग","unloaded":"अनलोडेड","active":"सक्रिय","inactive":"निष्क्रिय","allUsers":"सभी उपयोगकर्ता","fixed":"निश्चित","edited":"संपादित"},"warehouses":"वेयरहाउस","orderRemarks":"ऑर्डर टिप्पणियाँ","purchaseOrderRemarks":"परचेज़ ऑर्डर टिप्पणियाँ","purchaseOrdersRemarks":"परचेज़ ऑर्डर टिप्पणियाँ","noPrintedChallans":"कोई प्रिंटेड चालान नहीं","purchaseSummary":"परचेज़ सारांश","orderStatus":"ऑर्डर स्थिति"});

STRINGS.commonCols = deepMerge(STRINGS.commonCols || {}, {"gstNo":"जीएसटी नंबर","address":"पता"});

STRINGS.portal = {"login":{"title":"ग्राहक पोर्टल","subtitle":"अपने ऑर्डर और पूछताछ देखने के लिए साइन इन करें","backToStaff":"स्टाफ लॉगिन","identifierLabel":"ईमेल या मोबाइल","identifierPlaceholder":"you@example.com या 9876543210","passwordLabel":"पासवर्ड","passwordPlaceholder":"आपका पासवर्ड","show":"दिखाएँ","hide":"छिपाएँ","submit":"साइन इन करें","submitLoading":"साइन इन हो रहा है…","errors":{"identifierRequired":"अपना ईमेल या मोबाइल दर्ज करें।","invalid":"गलत ईमेल/मोबाइल या पासवर्ड।","locked":"बहुत अधिक असफल प्रयास। कृपया ~15 मिनट प्रतीक्षा करें या सहायता से संपर्क करें।","rateLimited":"बहुत अधिक प्रयास। कृपया एक मिनट प्रतीक्षा करके पुनः प्रयास करें।"}},"nav":{"portalName":"ग्राहक पोर्टल","purchases":"परचेज़ ऑर्डर","sales":"सेल्स ऑर्डर","inquiries":"पूछताछ","logout":"लॉगआउट"},"common":{"search":"खोजें","searchPlaceholder":"खोजें…","status":"स्थिति","allStatuses":"सभी स्थितियाँ","from":"से","to":"तक","view":"देखें","actions":"क्रियाएँ","items":"आइटम","noItems":"कोई आइटम नहीं","product":"उत्पाद","size":"साइज़","quantity":"मात्रा","ordered":"ऑर्डर किया","actual":"वास्तविक","pcs":"पीस","awaitingLoading":"लोडिंग के बाद","price":"मूल्य","amount":"राशि","gst":"जीएसटी शामिल","yes":"हाँ","no":"नहीं","remark":"टिप्पणी","optional":"वैकल्पिक","cancel":"रद्द करें","saving":"सहेज रहा है…","remove":"हटाएँ"},"purchases":{"title":"परचेज़ ऑर्डर","detailTitle":"ऑर्डर","totalLabel":"कुल ऑर्डर","columns":{"orderNumber":"ऑर्डर नं.","date":"तारीख","deliveryLocation":"डिलीवरी स्थान","total":"कुल","status":"स्थिति","expectedDelivery":"अपेक्षित डिलीवरी"},"statuses":{"pending":"लंबित","confirmed":"पुष्ट","delivery_assigned":"डिलीवरी असाइन","loading":"लोडिंग","loaded":"लोडेड","in_transit":"ट्रांज़िट में","delivered":"डिलीवर हो गया","invoiced":"इनवॉइस हो गया","cancelled":"रद्द"},"empty":{"title":"कोई परचेज़ नहीं","message":"आपके द्वारा दिए गए ऑर्डर यहाँ दिखेंगे।"}},"sales":{"title":"सेल्स ऑर्डर","detailTitle":"सेल्स ऑर्डर","totalLabel":"कुल सेल्स ऑर्डर","columns":{"poNumber":"ऑर्डर नं.","date":"तारीख","counterparty":"को","total":"कुल","status":"स्थिति","expectedDelivery":"अपेक्षित डिलीवरी"},"statuses":{"pending":"लंबित","confirmed":"पुष्ट","partial_advice_created":"आंशिक एडवाइस बनी","advice_created":"एडवाइस बनी","loading":"लोडिंग","loaded":"लोडेड","in_progress":"प्रगति में","received":"प्राप्त","completed":"पूर्ण","cancelled":"रद्द"},"empty":{"title":"कोई सेल्स ऑर्डर नहीं","message":"आपके द्वारा आपूर्ति किए ऑर्डर यहाँ दिखेंगे।"}},"inquiries":{"title":"मेरी पूछताछ","detailTitle":"पूछताछ","totalLabel":"कुल पूछताछ","newInquiry":"नई पूछताछ","pendingQuote":"कोटेशन लंबित","columns":{"id":"पूछताछ #","created":"बनाया गया","items":"आइटम","expectedDelivery":"अपेक्षित डिलीवरी","total":"कुल","status":"स्थिति","deliveryLocation":"डिलीवरी स्थान"},"statuses":{"pending":"लंबित","approved":"स्वीकृत","rejected":"अस्वीकृत","converted":"परिवर्तित"},"empty":{"title":"कोई पूछताछ नहीं","message":"कोटेशन पाने के लिए पूछताछ करें।"},"create":{"title":"नई पूछताछ","note":"आप आइटम का अनुरोध करते हैं; हमारी टीम उन्हें कोट करेगी। मूल्य की आवश्यकता नहीं।","selectProduct":"उत्पाद चुनें","productIdPlaceholder":"प्रोडक्ट साइज़ आईडी","addItem":"आइटम जोड़ें","expectedDelivery":"अपेक्षित डिलीवरी (वैकल्पिक)","locationId":"डिलीवरी स्थान आईडी (वैकल्पिक)","submit":"पूछताछ भेजें","errorItems":"कम से कम एक उत्पाद और मात्रा वाला आइटम जोड़ें।","productListUnavailable":"पोर्टल खातों के लिए उत्पाद सूची अभी उपलब्ध नहीं है — प्रोडक्ट साइज़ आईडी मैन्युअली दर्ज करें।"}}};

STRINGS.portal = deepMerge(STRINGS.portal, {"common":{"unit":"इकाई"},"inquiries":{"create":{"units":{"kg":"किग्रा","meters":"मीटर","pieces":"पीस"}}}});

STRINGS.portal = deepMerge(STRINGS.portal, {"nav":{"profile":"प्रोफ़ाइल"},"common":{"loading":"लोड हो रहा है…"},"inquiries":{"create":{"searchProduct":"उत्पाद खोजें…","noProducts":"कोई उत्पाद नहीं मिला","searchLocation":"स्थान खोजें…","noLocations":"कोई स्थान नहीं मिला"}},"profile":{"title":"मेरी प्रोफ़ाइल","details":"विवरण","savedLocations":"सहेजे गए डिलीवरी स्थान","default":"डिफ़ॉल्ट","fields":{"company":"कंपनी","owner":"मालिक","contact":"संपर्क व्यक्ति","type":"प्रकार","email":"ईमेल","phone1":"फ़ोन","phone2":"वैकल्पिक फ़ोन","gst":"जीएसटी नंबर","address":"पता","deliveryLocation":"डिलीवरी स्थान"},"password":{"title":"पासवर्ड बदलें","current":"वर्तमान पासवर्ड","new":"नया पासवर्ड","confirm":"नया पासवर्ड पुष्टि करें","min8":"न्यूनतम 8 अक्षर","submit":"पासवर्ड अपडेट करें","saving":"अपडेट हो रहा है…","success":"पासवर्ड अपडेट किया गया।","errors":{"required":"अपना वर्तमान और नया पासवर्ड दर्ज करें।","short":"नया पासवर्ड कम से कम 8 अक्षर का होना चाहिए।","mismatch":"नए पासवर्ड मेल नहीं खाते।","failed":"पासवर्ड अपडेट करने में विफल।"}}}});

STRINGS.portal = deepMerge(STRINGS.portal, { inquiries: { create: {"deliveryLocation":"डिलीवरी स्थान (वैकल्पिक)","selectLocation":"स्थान चुनें","locationIdPlaceholder":"डिलीवरी स्थान आईडी","locationListUnavailable":"स्थान सूची उपलब्ध नहीं — डिलीवरी स्थान आईडी मैन्युअली दर्ज करें।"} } });
STRINGS.login = deepMerge(STRINGS.login || {}, { tabs: {"staff":"स्टाफ","customer":"ग्राहक"} });
STRINGS.customers = deepMerge(STRINGS.customers || {}, { portalAccess: {"title":"पोर्टल एक्सेस","statusEnabled":"सक्षम","statusDisabled":"निष्क्रिय","passwordSetShort":"पासवर्ड सेट","noPassword":"कोई पासवर्ड नहीं","email":"लॉगिन ईमेल","mobile":"लॉगिन मोबाइल","enable":"पोर्टल लॉगिन सक्षम करें","updateIdentifiers":"ईमेल/मोबाइल अपडेट करें","disable":"लॉगिन निष्क्रिय करें","setPassword":"पासवर्ड सेट करें","resetPassword":"पासवर्ड रीसेट करें","min8":"न्यूनतम 8 अक्षर","save":"सहेजें","enabled":"पोर्टल लॉगिन सक्षम किया गया।","disabled":"पोर्टल लॉगिन निष्क्रिय किया गया।","passwordSet":"पासवर्ड सेट किया गया।","errors":{"identifierRequired":"ईमेल या मोबाइल दर्ज करें।","passwordShort":"पासवर्ड कम से कम 8 अक्षर का होना चाहिए।"}} });

// Portal unit labels, keyed by the CANONICAL value the form now posts.
STRINGS.portal = deepMerge(STRINGS.portal || {}, {
  inquiries: { create: { units: { kg: "kg", m: "मीटर", pcs: "पीस" } } },
});

// तीन इकाइयाँ — kg / m / pcs (en.js में कारण देखें).
STRINGS.ordersModule = deepMerge(STRINGS.ordersModule || {}, { common: { units: {"pcs":"पीस"}, lengthNote: "लंबाई / विवरण", lengthNotePlaceholder: "जैसे 5 feet" } });
STRINGS.purchaseOrdersModule = deepMerge(STRINGS.purchaseOrdersModule || {}, { common: { units: {"pcs":"पीस"} } });

STRINGS.inquiries = deepMerge(STRINGS.inquiries || {}, {"table":{"columns":{"createdBy":"द्वारा बनाया"},"copy":{"customer":"ग्राहक"}}});
STRINGS.inquiriesModule = deepMerge(STRINGS.inquiriesModule || {}, {"inquiries":{"view":{"fields":{"createdBy":"द्वारा"},"copy":{"customer":"ग्राहक"}}}});

STRINGS.portal = deepMerge(STRINGS.portal || {}, {"onboarding":{"forceChange":{"title":"अपना पासवर्ड सेट करें","subtitle":"सुरक्षा के लिए, आगे बढ़ने से पहले कृपया अपना अस्थायी पासवर्ड बदलें।","current":"अस्थायी पासवर्ड","new":"नया पासवर्ड","confirm":"नया पासवर्ड पुष्टि करें","min8":"न्यूनतम 8 अक्षर","submit":"पासवर्ड बदलें और जारी रखें","saving":"सहेज रहा है…","logout":"लॉग आउट","errors":{"required":"अपना अस्थायी और नया पासवर्ड दर्ज करें।","short":"नया पासवर्ड कम से कम 8 अक्षर का होना चाहिए।","mismatch":"नए पासवर्ड मेल नहीं खाते।","failed":"पासवर्ड नहीं बदल सका।"}},"profile":{"header":"अपनी प्रोफ़ाइल पूरी करें","logout":"लॉगआउट","intro":"अपने व्यवसाय के बारे में हमें बताएं। हमारी टीम समीक्षा करके आपका खाता स्वीकृत करेगी।","rejectedBanner":"आपके विवरण में बदलाव आवश्यक हैं। कृपया समीक्षा करके पुनः सबमिट करें।","submit":"स्वीकृति के लिए सबमिट करें","submitting":"सबमिट हो रहा है…","fields":{"gst":"जीएसटी नंबर (15 अक्षर)","contact":"संपर्क व्यक्ति","phone2":"वैकल्पिक फ़ोन","deliveryLocation":"डिलीवरी स्थान","address1":"पता पंक्ति 1","address2":"पता पंक्ति 2","city":"शहर","state":"राज्य","zip":"ज़िप कोड","country":"देश"},"errors":{"gstLength":"जीएसटी नंबर बिल्कुल 15 अक्षर का होना चाहिए।","failed":"आपके विवरण सबमिट नहीं हो सके।"}},"pending":{"title":"स्वीकृति प्रतीक्षित","message":"धन्यवाद! आपकी प्रोफ़ाइल सबमिट हो गई है। हमारी टीम समीक्षा कर रही है और स्वीकृति के बाद आपको पूर्ण एक्सेस मिलेगा।","refresh":"स्वीकृति स्थिति जांचें","checking":"जांच हो रही है…","logout":"लॉग आउट"}}});
STRINGS.customers = deepMerge(STRINGS.customers || {}, {"tabs":{"all":"सभी ग्राहक","approvals":"स्वीकृतियाँ"},"invite":{"sectionTitle":"पोर्टल ऑनबोर्डिंग","title":"ग्राहक को आमंत्रित करें","note":"ग्राहक को ईमेल/व्हाट्सएप द्वारा एक अस्थायी पासवर्ड मिलता है, वह उसे बदलता है, और स्वीकृति के लिए अपनी प्रोफ़ाइल पूरी करता है।","fields":{"company":"कंपनी नाम","owner":"मालिक का नाम","type":"प्रकार","email":"लॉगिन ईमेल","mobile":"लॉगिन मोबाइल"},"types":{"customer":"ग्राहक","supplier":"आपूर्तिकर्ता"},"identifierHint":"ईमेल या मोबाइल में से कम से कम एक आवश्यक है।","submit":"आमंत्रण भेजें","sending":"भेजा जा रहा है…","cancel":"रद्द करें","done":"पूर्ण","sentVia":"आमंत्रण भेजा गया","throttledNote":"छोड़ा गया (हाल ही में भेजा गया):","noChannel":"कोई ईमेल/व्हाट्सएप चैनल सक्षम नहीं है, इसलिए ग्राहक को कुछ नहीं मिला। एक चैनल सक्षम करें और ग्राहक के पोर्टल एक्सेस कार्ड से पुनः भेजें।","errors":{"identifierRequired":"लॉगिन ईमेल या मोबाइल दर्ज करें।","failed":"आमंत्रण नहीं भेजा जा सका।"}},"approvals":{"columns":{"company":"कंपनी","gst":"जीएसटी","location":"स्थान","submitted":"सबमिट किया","actions":"क्रियाएँ"},"view":"देखें","approve":"स्वीकृत करें","reject":"अस्वीकृत करें","approvedMsg":"ग्राहक स्वीकृत।","rejectedMsg":"ग्राहक अस्वीकृत।","rejectTitle":"प्रोफ़ाइल अस्वीकृत करें","rejectMessage":"इस ग्राहक की सबमिट की गई प्रोफ़ाइल अस्वीकृत करें? वे ठीक करके पुनः सबमिट कर सकते हैं।","reasonPlaceholder":"कारण (वैकल्पिक)","totalLabel":"लंबित ग्राहक","empty":{"title":"कोई लंबित स्वीकृति नहीं","message":"सबमिट की गई ग्राहक प्रोफ़ाइल समीक्षा के लिए यहाँ दिखेंगी।"}},"portalAccess":{"resendInvite":"आमंत्रण पुनः भेजें","resent":"आमंत्रण पुनः भेजा गया","resendThrottled":"हाल ही में आमंत्रित — कुछ देर में पुनः प्रयास करें। मौजूदा पासवर्ड अब भी काम करता है।","resendNoChannel":"कोई चैनल सक्षम नहीं — कुछ नहीं भेजा गया।"}});

STRINGS.portal = deepMerge(STRINGS.portal, { onboarding: { forceChange: {"changedRefreshFailed":"पासवर्ड बदल गया। जारी रखने के लिए कृपया पेज रीफ्रेश करें।"} } });

STRINGS.customers = deepMerge(STRINGS.customers || {}, {"approvals":{"columns":{"status":"स्थिति"},"badges":{"pendingReview":"समीक्षा लंबित","pendingCustomer":"ग्राहक कार्रवाई लंबित"},"resend":"पुनः भेजें","cancel":"आमंत्रण रद्द करें","cancelTitle":"आमंत्रण रद्द करें","cancelMessage":"यह आमंत्रण रद्द करें? ग्राहक को बाद में पुनः आमंत्रित किया जा सकता है।","resentMsg":"आमंत्रण पुनः भेजा गया","resendNoChannelMsg":"कोई चैनल सक्षम नहीं — कुछ नहीं भेजा गया।","resendThrottledMsg":"हाल ही में आमंत्रित — कुछ देर में पुनः प्रयास करें। मौजूदा पासवर्ड अब भी काम करता है।","empty":{"title":"समीक्षा के लिए कुछ नहीं","message":"आमंत्रित और सबमिट किए गए ग्राहक यहाँ कार्रवाई के लिए दिखेंगे।"}}});

STRINGS.printSettings = {
  title: "प्रिंट फ़ॉर्मेट",
  subtitle: "चालान प्रिंट करते समय उपयोग होने वाला पेपर साइज़। यह सभी उपयोगकर्ताओं पर लागू होता है।",
  saved: "{{document}} अब {{size}} पर प्रिंट होगा",
  saveFailed: "{{document}} के लिए पेपर साइज़ नहीं बदला जा सका",
  sizes: {
    a5: "A5",
    a4: "A4",
  },
  currentBadge: "वर्तमान",
  orientation: {
    portrait: "पोर्ट्रेट",
    landscape: "लैंडस्केप",
  },
  documents: {
    delivery_challan: {
      label: "डिलीवरी चालान",
      description: "डिलीवरी के साथ दिया जाने वाला बिक्री चालान।",
    },
    purchase_challan: {
      label: "खरीद चालान",
      description: "खरीद सलाह के विरुद्ध बनाया गया चालान।",
    },
  },
};

STRINGS.coilSettings = {
  title: "कॉइल ट्रैकिंग",
  subtitle: "जिन प्रोडक्ट साइज़ के लिए आवश्यक हो, उनका स्टॉक अलग-अलग कॉइल रोल के रूप में ट्रैक करें। यह पूरे सिस्टम पर लागू होता है।",
  tracking: {
    label: "प्रति-कॉइल इन्वेंटरी",
    description: "चालू होने पर किसी प्रोडक्ट साइज़ को कॉइल ट्रैक्ड चिह्नित किया जा सकता है। उसके ऑर्डर कॉइल-दर-कॉइल चुने जाते हैं, और आंशिक रूप से उपयोग हुई कॉइल अपनी बैच पहचान बनाए रखती है। जब तक यार्ड वास्तव में कॉइल दर्ज न करता हो, इसे बंद रखें।",
  },
  status: {
    enabled: "सक्षम",
    disabled: "निष्क्रिय",
  },
  notifications: {
    updated: "कॉइल ट्रैकिंग {{state}}",
    updateFailed: "कॉइल ट्रैकिंग सेटिंग नहीं बदली जा सकी",
  },
};

STRINGS.orderSettings = {
  title: "ऑर्डर कन्फ़र्मेशन",
  subtitle: "तय करता है कि नए ऑर्डर को आगे बढ़ने से पहले हाथ से कन्फ़र्म करना होगा या नहीं। यह सेल्स ऑर्डर और परचेज़ ऑर्डर दोनों पर लागू होता है।",
  skip: {
    label: "कन्फ़र्म चरण छोड़ें",
    description: "चालू होने पर नया ऑर्डर या परचेज़ ऑर्डर बनते ही कन्फ़र्म हो जाता है, किसी को Confirm दबाने की ज़रूरत नहीं। जो ऑर्डर अभी कन्फ़र्म नहीं हो सकता — जैसे कॉइल ट्रैक्ड आइटम जिसकी कॉइल अभी चुनी नहीं गई — वह फिर भी सुरक्षित रहता है और उसका Confirm बटन बना रहता है।",
  },
  status: {
    enabled: "सक्षम",
    disabled: "निष्क्रिय",
  },
  notifications: {
    enabled: "अब नए ऑर्डर स्वयं कन्फ़र्म हो जाएंगे",
    disabled: "अब नए ऑर्डर फिर से Confirm बटन का इंतज़ार करेंगे",
    updateFailed: "ऑर्डर कन्फ़र्मेशन सेटिंग नहीं बदली जा सकी",
  },
};

STRINGS.coils = {
  picker: {
    title: "कॉइल चुनें",
    context: {
      order: "ऑर्डर पिकिंग",
      loading: "लोडिंग — पिक बदलें",
    },
    panes: {
      available: "उपलब्ध कॉइल",
      picked: "चुनी गई कॉइल",
    },
    filters: {
      colour: "रंग",
      batch: "बैच",
      allColours: "सभी रंग",
      searchPlaceholder: "बैच या मिल बैच रेफ़",
    },
    sort: {
      label: "क्रमबद्ध करें",
      oldestFirst: "पहले प्राप्त पहले",
      newestFirst: "बाद में प्राप्त पहले",
      lightestFirst: "हल्की पहले",
      heaviestFirst: "भारी पहले",
      batch: "बैच नंबर",
    },
    columns: {
      batch: "बैच",
      supplierRef: "मिल बैच",
      colour: "रंग",
      received: "प्राप्त",
      remainingKg: "शेष (kg)",
      remainingM: "शेष (m)",
      availableKg: "उपलब्ध (kg)",
      kgPerMetre: "kg/m",
      take: "लें (kg)",
      status: "स्थिति",
    },
    take: "लें",
    takeAll: "पूरी लें",
    add: "जोड़ें",
    added: "जोड़ी गई",
    remove: "हटाएँ",
    clearAll: "सब हटाएँ",
    overrideRemainder: "शेष बदलें",
    remainderLabel: "शेष {weight} kg · {length} m",
    adjusted: "{delta} kg समायोजित",
    held: "{weight} kg अन्य ऑर्डर के लिए रोकी गई",
    truncated: "कुल {total} में से केवल पहली {count} कॉइल दिखाई गई हैं",
    age: {
      today: "आज",
      days: "{days} दिन",
      months: "{months} माह",
      years: "{years} वर्ष",
    },
    summary: {
      picked: "{required} kg में से {picked} kg चुना गया",
      bar: "{count} कॉइल चुनी · {required} kg में से {picked} kg",
      barNoTarget: "{count} कॉइल चुनी · {picked} kg",
      short: "{amount} kg कम",
      over: "{amount} kg अधिक",
      exact: "लाइन से बिल्कुल मेल खाता है",
    },
    empty: {
      title: "कोई कॉइल उपलब्ध नहीं",
      subtitle: "इस वेयरहाउस में इन फ़िल्टर से कोई कॉइल मेल नहीं खाती। कॉइल लेजर में कॉइल जोड़ें, या खरीद के विरुद्ध प्राप्त करें।",
      noStock: "इस वेयरहाउस में इस प्रोडक्ट के लिए कोई कॉइल स्टॉक में नहीं है",
    },
  },
  entry: {
    title: "कॉइल जोड़ें",
    subtitle: "हर भौतिक कॉइल के लिए एक पंक्ति। पंक्ति दर्ज करने और अगली शुरू करने के लिए Enter दबाएँ।",
    fields: {
      warehouse: "वेयरहाउस",
      productSize: "प्रोडक्ट",
      batchNumber: "बैच नंबर",
      supplierBatchRef: "सप्लायर / मिल बैच रेफ़",
      colour: "रंग",
      receivedDate: "प्राप्ति तारीख",
      weightKg: "वज़न (kg)",
      lengthM: "लंबाई (m)",
      remark: "टिप्पणी",
    },
    computedKgPerMetre: "{value} kg/m",
    addAnother: "एक और कॉइल जोड़ें",
    runningTotal: "{count} कॉइल · {weight} kg",
    coilNumber: "कॉइल {number}",
    removeRow: "यह कॉइल हटाएँ",
    expected: "अपेक्षित {quantity} {unit}",
    difference: {
      short: "{amount} kg कम",
      over: "{amount} kg अधिक",
      exact: "लाइन से बिल्कुल मेल खाता है",
    },
    awaitingKgPerMetre: "वज़न और लंबाई दर्ज करें",
    save: "कॉइल सहेजें",
  },
  ledger: {
    title: "कॉइल स्टॉक",
    subtitle: "इस वेयरहाउस में इस प्रोडक्ट की अलग-अलग कॉइल",
    addCoil: "कॉइल जोड़ें",
    editCoil: "कॉइल संपादित करें",
    adjustWeight: "वज़न सुधारें",
    adjustTitle: "शेष वज़न सुधारें",
    newRemaining: "नया शेष (kg)",
    adjustNote: "टिप्पणी",
    adjustNotePlaceholder: "धर्मकाँटा पुनः जाँच, स्टॉक टेक…",
    writeOff: "राइट ऑफ़ करें",
    writeOffTitle: "कॉइल राइट ऑफ़ करें",
    writeOffReason: "कारण",
    writeOffReasonPlaceholder: "हैंडलिंग में क्षतिग्रस्त, जाँच में अस्वीकृत…",
    writeOffConfirm: "कॉइल {batch} राइट ऑफ़ करें? इसका शेष {weight} kg स्टॉक से निकल जाएगा और यह कॉइल दोबारा कभी नहीं चुनी जा सकेगी।",
    filters: {
      includeInactive: "समाप्त और राइट ऑफ़ भी दिखाएँ",
    },
    columns: {
      heldKg: "रोका गया (kg)",
    },
    totals: "{count} कॉइल · {weight} kg",
    empty: {
      title: "अभी कोई कॉइल नहीं",
      subtitle: "कॉइल स्टॉक खाली से शुरू होता है। हर भौतिक कॉइल यहाँ जोड़ें, या खरीद के विरुद्ध प्राप्त करें।",
    },
    reconcile: {
      matched: "कॉइल वज़न इन्वेंटरी से मेल खाता है",
      mismatch: "{difference} kg का अंतर",
      coilTotal: "कॉइल कुल",
      inventoryTotal: "इन्वेंटरी कुल",
      coilCount: "{count} कॉइल",
      unavailable: "रिकंसिलिएशन उपलब्ध नहीं",
    },
    status: {
      whole: "पूरी",
      partlyConsumed: "आंशिक उपयोग",
      exhausted: "समाप्त",
      writtenOff: "राइट ऑफ़",
    },
    messages: {
      created: "{count} कॉइल जोड़ी गईं",
      updated: "कॉइल अपडेट हुई",
      adjusted: "कॉइल का वज़न सुधारा गया",
      writtenOff: "कॉइल राइट ऑफ़ हुई",
      loadFailed: "कॉइल लोड नहीं हो सकीं",
      stockManagedByCoils: "कॉयल-ट्रैक उत्पाद का स्टॉक उसके कॉयलों का योग होता है, इसलिए यह कॉयल प्राप्त करने या समायोजित करने से तय होता है — यहाँ टाइप करके नहीं। न्यूनतम स्टॉक अभी भी संपादन योग्य है।",
      notStockedHere: "कॉयल-ट्रैक — कॉयल लेजर से स्टॉक होता है",
      notStockedHereHint: "कॉयल-ट्रैक उत्पाद धूसर हैं: उनका स्टॉक अलग-अलग कॉयल प्राप्त करने या समायोजित करने से आता है, इस स्क्रीन से नहीं।"
    },
  },
  line: {
    piecesAreCoils: "कॉइल लाइन पर पीस का मतलब चुनी गई कॉइल की संख्या है",
    cutPieces: "कट पीस",
    cutPiecesHint: "कॉइल को कितने टुकड़ों में काटना है",
    cutPiecesRequired: "कॉइल को कितने टुकड़ों में काटना है, यह दर्ज करें",
    cutPiecesMinCoils: "कट पीस इस लाइन की {count} कॉइल से कम नहीं हो सकते",
    cutPiecesInteger: "कट पीस धनात्मक पूर्ण संख्या होनी चाहिए",
    summary: "{count} कॉइल · {weight} kg",
    summaryWithLength: "{count} कॉइल · {weight} kg · {length} m",
    selectCoils: "कॉइल चुनें",
    editCoils: "कॉइल बदलें",
    noneSelected: "कोई कॉइल चयनित नहीं",
    disabledNote: "कॉइल चुनने से पहले वेयरहाउस और प्रोडक्ट चुनें।",
  },
  receipt: {
    pill: "कॉइल",
    enterCoils: "कॉइल दर्ज करें",
    noneEntered: "कोई कॉइल दर्ज नहीं",
    derivedFromCoils: "इस लाइन पर दर्ज कॉइल से गणना किया गया",
  },
  errors: {
    takeExceedsAvailable: "इस कॉइल पर उपलब्ध {available} kg से अधिक नहीं ले सकते",
    takeMustBePositive: "शून्य से अधिक वज़न दर्ज करें",
    batchRequired: "बैच नंबर आवश्यक है",
    weightRequired: "वज़न आवश्यक है",
    lengthRequired: "लंबाई आवश्यक है",
    wholeKgRequired: "वज़न पूर्ण kg में होना चाहिए",
    receivedDateRequired: "प्राप्ति तारीख आवश्यक है",
    duplicateBatch: "बैच {batch} इस शीट में पहले से है",
    noCoilsEntered: "कम से कम एक पूरी कॉइल दर्ज करें",
    reasonRequired: "राइट ऑफ़ करने के लिए कारण आवश्यक है",
    coilsRequired: "इस लाइन पर प्राप्त कॉइल दर्ज करें",
    coilsExceedNet: "कॉइल का वज़न {coils} kg है जबकि ट्रक का नेट वज़न केवल {net} kg है — खाली (टेयर) वज़न और कॉइल के वज़न जाँचें।",
    coilRowInvalid: "कॉइल {number}: {message}",
  },
  challan: {
    coilsLabel: "कॉइल",
    moreCoils: "+{count} और",
  },
  productSize: {
    coilTracked: {
      label: "अलग-अलग कॉइल के रूप में ट्रैक करें",
      help: "इस साइज़ का स्टॉक अलग-अलग कॉइल रोल के रूप में रखा जाता है। ऑर्डर कॉइल-दर-कॉइल चुने जाते हैं, और आंशिक रूप से उपयोग हुई कॉइल अपना बैच बनाए रखती है। केवल कॉइल ट्रैकिंग चालू होने पर उपलब्ध।",
      badge: "कॉइल ट्रैक्ड",
      stockLocked: "नीचे दर्ज कॉइल्स से गणना — स्टॉक उनके वज़न का योग है, टाइप नहीं किया जाता।",
    },
    existingWarehouse: {
      current: "यहाँ पहले से स्टॉक: {current}. बढ़ाने के लिए नीचे कॉइल जोड़ें।",
      adding: "यहाँ पहले से स्टॉक: {current}. नीचे की कॉइल्स से {added} जोड़ा जा रहा है।",
    },
    openingCoils: {
      label: "प्रारंभिक कॉइल",
      none: "अभी कोई कॉइल नहीं — यह वेयरहाउस शून्य स्टॉक से शुरू होगा।",
      row: "{batch} · {weight} kg · {length} m",
      sheetTitle: "{product} · {warehouse}",
    },
  },
  looseCoil: {
    badge: "लूज़ कॉइल",
  },
};

STRINGS.pieces = {
  productSize: {
    trackByPieces: {
      errors: { coilConflict: "उत्पाद को कॉयल से या पीस से ट्रैक किया जा सकता है, दोनों से नहीं। कॉयल स्टॉक उसके कॉयलों का वज़न है; पीस ट्रैकिंग इकाइयाँ गिनती है।" },
      label: "स्टॉक पीस में ट्रैक करें",
      help: "इस साइज़ का वेयरहाउस स्टॉक किलोग्राम के बजाय पीस की संख्या में रखें। आरक्षण ऑर्डर किए गए पीस का उपयोग करता है; रसीद और डिलीवरी वास्तविक पीस को घटाती/जोड़ती है।",
      badge: "पीस में",
      flipWarning: "स्टॉक इकाई बदलने से मौजूदा बैलेंस स्वतः नहीं बदलता। सेव करने के बाद, वेयरहाउस इन्वेंटरी स्क्रीन पर इस साइज़ का स्टॉक नई इकाई में फिर से दर्ज करें।",
    },
  },
  inventory: {
    unitPieces: "पीस",
    unitKg: "kg",
  },
  loading: {
    pcsUnitNote: "पीस में बिकता है — राशि प्रति पीस के हिसाब से लगती है। “Actual Pieces” में डिलीवर किए गए पीस की संख्या दर्ज करें; प्रति-पीस वज़न केवल weighbridge और लोडिंग लागत के लिए है।",
    loadByPiecesNote: "पीस से लोड — डिलीवर किए गए पीस की संख्या डालें, वज़न नहीं।",
    pcsReceiveNote: "पीस में प्राप्त होता है — प्राप्त पीस की संख्या दर्ज करें। राशि प्रति पीस के हिसाब से लगती है।",
    pcsCountRequired: "पीस की संख्या ज़रूरी है — इस लाइन के लिए प्राप्त पीस दर्ज करें।",
  },
};

STRINGS.challanPrintVariance = {
  title: "चालान प्रिंट अंतर",
  subtitle: "जिस दिन चालान बनाए गए उसी दिन प्रिंट नहीं हुए, तथा जो कभी प्रिंट नहीं हुए। चालान बनने की तारीख से फ़िल्टर किया गया।",
  loading: "लोड हो रहा है...",
  apply: "लागू करें",
  empty: "इस अवधि में बना हर चालान उसी दिन प्रिंट हुआ",
  totalLabel: "प्रिंट अंतर वाले चालान",
  searchPlaceholder: "चालान नं, ऑर्डर नं, पार्टी या वाहन",
  tiles: {
    generated: "बनाए गए",
    variance: "अंतर",
    notPrinted: "प्रिंट नहीं हुए",
    printedLate: "बाद में प्रिंट",
  },
  filters: {
    generatedFrom: "बनने की तारीख से",
    generatedTo: "बनने की तारीख तक",
    type: "प्रकार",
    allTypes: "बिक्री और खरीद",
    status: "प्रिंट स्थिति",
    allStatuses: "सभी",
    printedOtherDay: "दूसरे दिन प्रिंट हुआ",
  },
  columns: {
    generatedDate: "बनने की तारीख",
    printedDate: "प्रिंट की तारीख",
    printStatus: "प्रिंट स्थिति",
    type: "प्रकार",
    party: "पार्टी",
    order: "ऑर्डर",
  },
  status: {
    notPrinted: "प्रिंट नहीं हुआ",
    daysLate: "{days} दिन देरी",
    differentDay: "अलग दिन",
  },
  source: {
    sales: "बिक्री",
    purchase: "खरीद",
  },
};

// The either/or offered when deleting something that has already moved warehouse stock.
STRINGS.stockRevert = {
  single: {
    heading: "इस ऑर्डर से स्टॉक पहले ही मूव हो चुका है। स्टॉक का क्या करें?",
    revertLabel: "स्टॉक वापस डालें",
    revertDescription:
      "मात्रा वेयरहाउस में वापस आ जाएगी, जैसे यह ऑर्डर बना ही न हो। अगर ऑर्डर गलती से बना था तो यह चुनें।",
    keepLabel: "स्टॉक जैसा है वैसा ही रहने दें",
    keepDescription:
      "स्टॉक जैसा है वैसा ही रहेगा — अगर माल सच में गया था तो यह चुनें। जो मात्रा सिर्फ़ रिज़र्व थी वह दोनों ही स्थिति में मुक्त हो जाएगी।",
  },
  bulk: {
    heading: "इनमें से कुछ ऑर्डर से स्टॉक पहले ही मूव हो चुका है। स्टॉक का क्या करें?",
    revertLabel: "स्टॉक वापस डालें",
    revertDescription:
      "सभी मात्राएँ वेयरहाउस में वापस आ जाएँगी, जैसे ये ऑर्डर बने ही न हों। अगर ये गलती से बने थे तो यह चुनें।",
    keepLabel: "स्टॉक जैसा है वैसा ही रहने दें",
    keepDescription:
      "स्टॉक जैसा है वैसा ही रहेगा — अगर माल सच में गया था तो यह चुनें। जो मात्रा सिर्फ़ रिज़र्व थी वह दोनों ही स्थिति में मुक्त हो जाएगी।",
  },
};

STRINGS.purchaseOrdersModule.purchaseOrders.table.tooltips.deleteReceivedSuperAdminOnly =
  "माल रिसीव हो जाने के बाद परचेज़ ऑर्डर सिर्फ सुपर एडमिन ही हटा सकते हैं";


// Keys the audit-fix batch used with inline English defaults — defined here so the
// Hindi UI does not fall back to English for them.
STRINGS.ordersModule = STRINGS.ordersModule || {};
STRINGS.ordersModule.common = STRINGS.ordersModule.common || {};
STRINGS.ordersModule.common.loadFailed = "यह ऑर्डर लोड नहीं हो सका। हो सकता है इसे हटा दिया गया हो, या सर्वर उपलब्ध न हो।";
STRINGS.purchaseOrdersModule = STRINGS.purchaseOrdersModule || {};
STRINGS.purchaseOrdersModule.common = STRINGS.purchaseOrdersModule.common || {};
STRINGS.purchaseOrdersModule.common.loadFailed = "यह परचेज़ ऑर्डर लोड नहीं हो सका। हो सकता है इसे हटा दिया गया हो, या सर्वर उपलब्ध न हो।";
STRINGS.common = STRINGS.common || {};
STRINGS.common.errors = STRINGS.common.errors || {};
STRINGS.common.errors.generic = "कार्रवाई पूरी नहीं हो सकी। कृपया फिर से प्रयास करें।";
STRINGS.deliveryOrdersModule = STRINGS.deliveryOrdersModule || {};
STRINGS.deliveryOrdersModule.forms = STRINGS.deliveryOrdersModule.forms || {};
STRINGS.deliveryOrdersModule.forms.challan = STRINGS.deliveryOrdersModule.forms.challan || {};
STRINGS.deliveryOrdersModule.forms.challan.sendFailed = "भेजा नहीं जा सका। कृपया फिर से प्रयास करें।";
STRINGS.deliveryOrdersModule = STRINGS.deliveryOrdersModule || {};
STRINGS.deliveryOrdersModule.forms = STRINGS.deliveryOrdersModule.forms || {};
STRINGS.deliveryOrdersModule.forms.challan = STRINGS.deliveryOrdersModule.forms.challan || {};
STRINGS.deliveryOrdersModule.forms.challan.sendSuccess = "सफलतापूर्वक भेजा गया।";
STRINGS.purchaseOrders = STRINGS.purchaseOrders || {};
STRINGS.purchaseOrders.forms = STRINGS.purchaseOrders.forms || {};
STRINGS.purchaseOrders.forms.unloading = STRINGS.purchaseOrders.forms.unloading || {};
STRINGS.purchaseOrders.forms.unloading.draftSaveFailed = "ड्राफ़्ट सहेजा नहीं जा सका। कृपया फिर से प्रयास करें।";
STRINGS.purchaseOrders = STRINGS.purchaseOrders || {};
STRINGS.purchaseOrders.forms = STRINGS.purchaseOrders.forms || {};
STRINGS.purchaseOrders.forms.unloading = STRINGS.purchaseOrders.forms.unloading || {};
STRINGS.purchaseOrders.forms.unloading.draftSaved = "ड्राफ़्ट सहेज लिया गया।";
STRINGS.hsnCodes = STRINGS.hsnCodes || {};
STRINGS.hsnCodes.notifications = STRINGS.hsnCodes.notifications || {};
STRINGS.hsnCodes.notifications.bulkPartial = "{{deleted}} हटाए गए, {{skipped}} छोड़े गए (अन्य रिकॉर्ड में उपयोग में)।";
STRINGS.taxes = STRINGS.taxes || {};
STRINGS.taxes.notifications = STRINGS.taxes.notifications || {};
STRINGS.taxes.notifications.bulkPartial = "{{deleted}} हटाए गए, {{skipped}} छोड़े गए (अभी भी उपयोग में)।";
STRINGS.purchaseOrdersModule = STRINGS.purchaseOrdersModule || {};
STRINGS.purchaseOrdersModule.purchaseOrders = STRINGS.purchaseOrdersModule.purchaseOrders || {};
STRINGS.purchaseOrdersModule.purchaseOrders.forms = STRINGS.purchaseOrdersModule.purchaseOrders.forms || {};
STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm = STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm || {};
STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm.errors = STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm.errors || {};
STRINGS.purchaseOrdersModule.purchaseOrders.forms.adviceForm.errors.noItems = "सलाह बनाने के लिए कोई आइटम उपलब्ध नहीं है।";

// Daybook / proforma print header for the parent sales order column.
STRINGS.commonCols = deepMerge(STRINGS.commonCols || {}, {"orderNo": "ऑर्डर नंबर"});

export default STRINGS;
